import React, {
  useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef,
} from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { PRINT_ROLES, hasRole, allRolesIn } from '../constants/roles';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Button, Popover, Badge, Banner, Text, Modal, BlockStack, Tag, TextField, Checkbox,
} from '@shopify/polaris';
import SourceBadge from '../SourceBadge';
import { useAuth, useAuthFetch, authenticatedFetch } from '../AuthComponent';
import { useStore, STORE_DISPLAY_NAMES } from '../StoreContext';
import {
  PRINT_TAGS, NEW_PRINT_TAG, isPrinted,
  getPerLocationPrintTags, hasLegacyPrintTag,
  OrderPrintCard, MergedNjPrint, buildPopupHTML,
} from '../OrderPrintCard';
import { njMergeAllowed, isNjWarehouseName, njWarehouseLocsOnOrder, readMergeNjPref, writeMergeNjPref } from '../constants/njWarehouses';
import { tzMarker } from '../utils/datetime';
import { compareBins, binAisle, isWarehouseLocation } from '../binSort';
import { COLUMN_DEFS, COLUMN_MAP, DEFAULT_COLUMN_ORDER, ORIGINAL_ORDER } from './columns';
import { printedIdsForLocation, tagsSayPrintedHere } from './printedScope';
import useOrdersQuery from './useOrdersQuery';
import useVirtualOrdersQuery from './useVirtualOrdersQuery';
import VirtualTbody from './VirtualTbody';
import HeaderCell from './HeaderCell';
import AdvancedFilters from './filters/AdvancedFilters';
import BulkLabelsModal from './BulkLabelsModal';
import SaveViewModal from './filters/SaveViewModal';
import SavedViewsDropdown from './filters/SavedViewsDropdown';
import './orders-tanstack.css';
import { isFbmRouted, isFbmOpenRow, isAmazonFbm } from './fbmRow';
// 2026-08-31: Bulk Print Labels is TEST-ONLY for now (pulled back from prod
// per Eric after the initial rollout) — flip this to true everywhere when
// it's cleared for production.
const BULK_LABELS_ENABLED = window.location.hostname.startsWith('operations-test');


// ── Constants ──────────────────────────────────────────────────────────────────
const DELIVERY_LABELS = {
  LOCAL_PICKUP: 'Pickup in store',
  LOCAL_DELIVERY: 'Local Delivery',
  NONE: 'Shipping not required',
  SHIPPING: 'Shipping',
  PICK_UP: 'Pickup in store',
  RETAIL: 'Pickup in store',
};
const PICKUP_TYPES = new Set(['LOCAL_PICKUP', 'PICK_UP', 'RETAIL']);
// Keyed on BOTH the row data's numeric store_status (0/1/2/3 — drives the
// per-row chip via formatStoreStatus below) AND the URL-slug filter values
// ('in_transit', etc. — drives the filter-bar chip in activeFilterSummary).
const STORE_STATUS_LABELS = {
  0: 'None',          1: 'In Transit',  2: 'Ready For Pickup',  3: 'Picked Up',
  none: 'None', in_transit: 'In Transit', ready_for_pickup: 'Ready For Pickup', picked_up: 'Picked Up',
  partial_picked_up: 'Partially Picked Up',
};
// Back-compat: ?store_statuses=1 from a bookmark gets normalized to
// 'in_transit' so it round-trips with the new slug-based URLs.
const _STORE_STATUS_NUMERIC_TO_SLUG = { 0: 'none', 1: 'in_transit', 2: 'ready_for_pickup', 3: 'picked_up' };
const _normalizeStoreStatusParam = v => _STORE_STATUS_NUMERIC_TO_SLUG[v] || v;
// Renders "Picked Up" at 100%, "40% Picked Up" for partials. The pct is
// driven by store_status_pct on the cache row (% of items at the highest
// pickup stage reached). For status=0 nothing renders.
// 2026-08-04: when status=3 (Picked Up) but pct<100, show
// "Partially Picked Up" instead of "20% Picked Up" — clearer signal that
// there's still work to do (SP-300486 canonical: 1 of 5 items picked up,
// 4 still awaiting pickup at the store).
const formatStoreStatus = (status, pct) => {
  if (!status) return '';
  const p = Math.min(100, Math.max(0, parseInt(pct, 10) || 0));
  if (status === 3 && p > 0 && p < 100) return STORE_STATUS_LABELS.partial_picked_up;
  const base = STORE_STATUS_LABELS[status] || '';
  if (!base) return '';
  if (p > 0 && p < 100) return `${p}% ${base}`;
  return base;
};

// ── Tracking / Delivery Date columns (2026-09-09) ───────────────────────────
// fo.tracking (from the API, Task 2) merges Shopify shipping-label tracking
// (fo.label_tracking) with Bulk Shipping Portal scans in fulfillment order,
// then bulk numbers not already present — one entry per line in both the
// Tracking and Delivery Date columns. `fo.tracking` may be absent on a
// failure path (spec §3/§4); fall back to the legacy fo.bulk_tracking shape
// ({tracking, fo_id, scanned_at}) with no status/eta/delivered_on, same as
// before this column read tracking.
export function trackingEntries(fo) {
  if (Array.isArray(fo?.tracking)) return fo.tracking;
  const bulk = Array.isArray(fo?.bulk_tracking) ? fo.bulk_tracking : [];
  return bulk.map(x => ({ number: x.tracking, kind: 'bulk' }));
}

// Dedupe by number, first occurrence wins — a label number that's also a
// bulk-scanned number should only render/export once. Factored out
// (2026-09-09 review) so renderCellContent and cellTextForExport agree for
// BOTH the Tracking and Delivery Date columns; before this, the Delivery
// Date CSV export case didn't dedupe while the other three call sites did,
// so a duplicated number could make the CSV's Tracking and Delivery Date
// columns disagree on line count.
export function dedupeTrackingEntries(entries) {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : []).filter(e => {
    const num = e?.number;
    if (seen.has(num)) return false;
    seen.add(num);
    return true;
  });
}

// Pure text for one tracking entry's Delivery Date line — shared by the
// rendered cell (which adds `ots-track-bad` styling for exception/returned)
// and the CSV export (plain text). Rule order per spec §4:
//   delivered_on → the date; out_for_delivery/exception/returned → status
//   text (+ issue for exception); else eta → "Est. <eta>"; else a UPS
//   number UPS reports as label-only → "Awaiting UPS scan"; else "—" (carriers
//   we don't track, e.g. USPS/FedEx).
export function trackingLineText(entry) {
  if (!entry) return '—';
  if (entry.delivered_on) return entry.delivered_on;
  // Hand-carried box (2026-09-10): no UPS eta/status ever exists for these —
  // "Driving" until the box's delivered_on is stamped (first order scanned
  // in at the store), caught by the delivered_on check above.
  if (entry.kind === 'hand') return 'Driving';
  if (entry.status === 'out_for_delivery') return 'Out for delivery';
  if (entry.status === 'exception') return `Exception${entry.issue ? `: ${entry.issue}` : ''}`;
  if (entry.status === 'returned') return 'Returned to shipper';
  if (entry.eta) return `Est. ${entry.eta}`;
  const isUps = entry.kind === 'bulk'
    || (entry.company || '').toUpperCase().includes('UPS')
    || (entry.number || '').startsWith('1Z');
  // Only a package UPS has actually told us about (label created, no scan
  // yet) reads "Awaiting UPS scan". A number the poller has never checked —
  // orders older than the 30-day tracking window, or a label bought in the
  // last few minutes — shows "—" rather than implying UPS is late.
  if (isUps && entry.status === 'label_created') return 'Awaiting UPS scan';
  return '—';
}

// Group already-deduped tracking `entries` (dedupeTrackingEntries) by which
// per_loc_status line shipped them (2026-09-10, SP-355456: on All-Locations
// the UPS number must render on the location line that actually shipped it,
// not on whichever line happens to be first). `perLoc` is `fo.per_loc_status`
// (one entry per location, in Status-cell order).
//
// - lines[i] = entries whose (string) location_id matches
//   String(perLoc[i].shopify_location_id) — exact id match, index-aligned
//   with the Status / Print / Pick / Press / Pack columns so rows line up.
// - Entries with no usable location_id (missing, or not matching ANY
//   per-loc line — e.g. an older cached row, or a location no longer in
//   per_loc_status) fall back: when exactly ONE per-loc line is shipped
//   (status FULFILLED or PARTIALLY_FULFILLED), attach them there — SP-355456
//   itself (one shipped location, one unfulfilled location, tracking has no
//   location_id yet) resolves via this branch. When 0 or 2+ lines are
//   shipped there's no unambiguous home, so they go to `unassigned`.
export function trackingByLocation(entries, perLoc) {
  const locs = Array.isArray(perLoc) ? perLoc : [];
  const list = Array.isArray(entries) ? entries : [];
  const lines = locs.map(() => []);
  const unassigned = [];

  const lineIndexByLocId = new Map();
  locs.forEach((l, i) => {
    const id = l?.shopify_location_id;
    if (id == null) return;
    const key = String(id);
    if (!lineIndexByLocId.has(key)) lineIndexByLocId.set(key, i);
  });

  const fallback = [];
  for (const e of list) {
    const locId = e?.location_id;
    const idx = locId != null ? lineIndexByLocId.get(String(locId)) : undefined;
    if (idx !== undefined) lines[idx].push(e);
    else fallback.push(e);
  }

  if (fallback.length) {
    const shippedIdx = [];
    locs.forEach((l, i) => {
      const st = (l?.status || '').toUpperCase();
      if (st === 'FULFILLED' || st === 'PARTIALLY_FULFILLED') shippedIdx.push(i);
    });
    if (shippedIdx.length === 1) lines[shippedIdx[0]].push(...fallback);
    else unassigned.push(...fallback);
  }

  return { lines, unassigned };
}

const STORE_COLORS_CACHE_KEY = 'ops_store_colors_v1';

function fmtDateTime(iso, activeLocTz) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday - 864e5);
  const startOfWeek = new Date(startOfToday - 6 * 864e5);
  // Phase 6: append browser-TZ abbrev when single-loc TZ ≠ browser TZ, or when
  // multi-loc (activeLocTz nullish). When the user is viewing a location whose
  // TZ matches their browser, no suffix — the times are unambiguous.
  const tz = tzMarker(activeLocTz);
  if (d >= startOfToday)     return `Today at ${time}${tz}`;
  if (d >= startOfYesterday) return `Yesterday at ${time}${tz}`;
  if (d >= startOfWeek)      return `${d.toLocaleDateString('en-US', { weekday: 'long' })} at ${time}${tz}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${time}${tz}`;
}

function customerName(fo) {
  const c = fo.order?.customer;
  if (!c) return '—';
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
}

function orderNumericId(gid) {
  return gid?.split('/').pop() || gid;
}

const STATUS_LABELS = {
  'UNFULFILLED':        'Unfulfilled',
  'PARTIALLY_FULFILLED':'Partially Fulfilled',
  'FULFILLED':          'Fulfilled',
  'IN_PROGRESS':        'In Progress',
  'ON_HOLD':            'On Hold',
  'CANCELLED':          'Cancelled',
};

function StatusBadge({ status }) {
  const raw = STATUS_LABELS[status?.toUpperCase()] || (status || 'Unfulfilled');
  const key = raw.toLowerCase().replace(/ /g, '-');
  return (
    <span className={`ots-status-badge ots-status-badge--${key}`}>{raw}</span>
  );
}

function PPBadge({ pct }) {
  if (pct == null || pct === 0)
    return <span className="ots-muted">—</span>;
  const cls = pct >= 100 ? 'ots-pp-badge' : 'ots-pp-badge ots-pp-badge--partial';
  return <span className={cls}>{pct}%</span>;
}

// ── Read/write localStorage hidden cols with migration from ordersHiddenCols ──
function loadHiddenCols() {
  // Cols hidden by default for everyone — match the original /orders table
  // so the TanStack view doesn't show duplicate or unused data.
  // pickup_store is a duplicate of Delivery's pickup-store-name rendering.
  // notes + tags are usually noise. Toggle on via the Columns button.
  const FORCE_HIDE = ['notes', 'tags', 'pickup_store'];
  try {
    const v2 = localStorage.getItem('ordersV2HiddenCols');
    if (v2) {
      const set = new Set(JSON.parse(v2));
      // Ensure the force-hidden defaults are applied to existing v2 users
      // who saved their state before this defaults change.
      let changed = false;
      for (const id of FORCE_HIDE) {
        if (!set.has(id)) { set.add(id); changed = true; }
      }
      if (changed) localStorage.setItem('ordersV2HiddenCols', JSON.stringify([...set]));
      return set;
    }
    // Migrate from old key
    const v1 = localStorage.getItem('ordersHiddenCols');
    if (v1) {
      const set = new Set(JSON.parse(v1));
      for (const id of FORCE_HIDE) set.add(id);
      localStorage.setItem('ordersV2HiddenCols', JSON.stringify([...set]));
      return set;
    }
  } catch {}
  return new Set(FORCE_HIDE);
}

function saveHiddenCols(set) {
  try { localStorage.setItem('ordersV2HiddenCols', JSON.stringify([...set])); } catch {}
  // Mirror to server so the prefs follow the user across browsers/devices.
  // localStorage is the fast-path; server is the cross-device source of truth.
  // Fire-and-forget — column changes are user-driven, not blocking.
  _syncOrdersColumnsToServer({ hidden: [...set] });
}

// Bumped to V5 (2026-05-24) — Notifications column inserted between Custom
// and Status. Discards any earlier saved order so existing users see the new
// column in its intended slot until they drag-reorder explicitly.
const COL_ORDER_KEY = 'ordersV2ColOrderV5';

// One-time layout migrations applied to a SAVED order (local or server).
// Each entry moves a column to a new canonical slot exactly once per browser,
// flagged in localStorage, so a user who later drags it elsewhere is left
// alone. Existing users otherwise never see a default-slot change because
// reconcileColOrder only places columns that are MISSING from their layout —
// Bulk Tracking was appended to the tail when the column first shipped and
// stayed there after it got its slot next to Store Status (2026-09-04).
const COL_ORDER_MIGRATIONS = [
  { flag: 'ordersV2ColMig_bulk_tracking_after_store_status', col: 'bulk_tracking', after: 'store_status' },
  // Delivery Date shipped alongside Tracking (2026-09-09) and belongs right
  // after it, but reconcileColOrder only splices a MISSING column in at its
  // ORIGINAL_ORDER index — on a saved layout that had already reordered
  // columns, that index can land far to the right of Tracking instead of
  // next to it. One-shot migration puts it in the intended slot.
  { flag: 'ordersV2ColMig_delivery_date_after_bulk_tracking', col: 'delivery_date', after: 'bulk_tracking' },
];
function migrateColOrder(order) {
  let out = order;
  for (const m of COL_ORDER_MIGRATIONS) {
    let done = false;
    try { done = localStorage.getItem(m.flag) === '1'; } catch {}
    if (done) continue;
    if (out.includes(m.col) && out.includes(m.after)) {
      const arr = out.filter(id => id !== m.col);
      arr.splice(arr.indexOf(m.after) + 1, 0, m.col);
      out = arr;
    }
    try { localStorage.setItem(m.flag, '1'); } catch {}
  }
  if (out !== order) saveColOrder(out);
  return out;
}

function loadColOrder() {
  try {
    const saved = localStorage.getItem(COL_ORDER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length) return migrateColOrder(reconcileColOrder(parsed));
    }
  } catch {}
  // Cleanup older keys so they don't sit forever in localStorage
  try {
    localStorage.removeItem('ordersV2ColOrder');
    localStorage.removeItem('ordersV2ColOrderV2');
    localStorage.removeItem('ordersV2ColOrderV3');
    localStorage.removeItem('ordersV2ColOrderV4');
  } catch {}
  return DEFAULT_COLUMN_ORDER;
}

// Splice any column present in COLUMN_DEFS but missing from `saved` into its
// canonical ORIGINAL_ORDER slot. Without this a new column added to the code
// after a user first saved their layout appends to the tail of the picker
// AND can't be drag-reordered (drag handler bails when the column isn't in
// colOrder — indexOf returns -1). Insert-at-canonical-position keeps the
// picker aligned with the table's own visibleColDefs merge logic (~line 1064)
// so a fresh browser and a saved-layout browser see the same visual order.
export function reconcileColOrder(saved) {
  const savedSet = new Set(saved);
  const missing = COLUMN_DEFS
    .map(c => c.id)
    .filter(id => !savedSet.has(id));
  if (!missing.length) return saved;
  const out = [...saved];
  const origIdx = (id) => ORIGINAL_ORDER.indexOf(id);
  for (const id of missing) {
    const myPos = origIdx(id);
    let insertAt = out.length;   // default append (cols not in ORIGINAL_ORDER)
    if (myPos >= 0) {
      // Prefer the column's canonical NEIGHBOUR: walk back through
      // ORIGINAL_ORDER from the slot before it and insert right after the
      // nearest predecessor the user actually has. On a reordered layout the
      // old "after the last column with a smaller index" rule dropped a new
      // column far from where it belongs (Delivery Date landed after Order
      // Risk instead of next to Tracking, 2026-09-09).
      insertAt = -1;
      for (let k = myPos - 1; k >= 0 && insertAt < 0; k--) {
        const at = out.indexOf(ORIGINAL_ORDER[k]);
        if (at >= 0) insertAt = at + 1;
      }
      if (insertAt < 0) {
        insertAt = 0;
        for (let i = out.length - 1; i >= 0; i--) {
          const idx = origIdx(out[i]);
          if (idx >= 0 && idx < myPos) { insertAt = i + 1; break; }
        }
      }
    }
    out.splice(insertAt, 0, id);
  }
  return out;
}

function saveColOrder(arr) {
  try { localStorage.setItem(COL_ORDER_KEY, JSON.stringify(arr)); } catch {}
  _syncOrdersColumnsToServer({ order: arr });
}

// Fire-and-forget POST to /api/users/me/preferences. Coalesces multiple
// per-tick calls into a single request via a microtask. We don't await
// the result — column changes are immediate-feeling local-storage writes;
// the server sync is just so the next browser/device sees the new state.
let _pendingPrefs = null;
let _prefsFlushScheduled = false;
function _syncOrdersColumnsToServer(patch) {
  if (typeof window === 'undefined') return;
  _pendingPrefs = { ...(_pendingPrefs || {}), ...patch };
  if (_prefsFlushScheduled) return;
  _prefsFlushScheduled = true;
  Promise.resolve().then(async () => {
    const payload = _pendingPrefs;
    _pendingPrefs = null;
    _prefsFlushScheduled = false;
    try {
      const tok = localStorage.getItem('operations_token') || '';
      if (!tok) return;  // not logged in — nothing to save
      await fetch('/api/users/me/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ orders_columns: payload }),
      });
    } catch { /* offline / network blip — localStorage still wins */ }
  });
}

// Header chips: Unfulfilled / Partially Fulfilled counts scoped to the
// currently-emulated location. Silent when no loc emulation is active
// ("All Locations" or an admin viewing everything) — the counts only make
// sense against a single loc's per-loc status.
function LocStatusChips({ storeFetch, activeStore, shopifyLocationId, retailActive, onRetailToggle }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    if (!shopifyLocationId || !activeStore) {
      setData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await storeFetch(
          `/api/orders/loc-status-summary?shopify_location_id=${encodeURIComponent(shopifyLocationId)}&store=${encodeURIComponent(activeStore)}`
        );
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [shopifyLocationId, activeStore, storeFetch]);
  // hidden: server-gated — customer_service, corporate retail store
  // locations, and users assigned only corporate stores get no chips.
  if (!shopifyLocationId || !data || data.hidden) return null;
  // 2026-10-07 (Eric): corporate retail stores get ONE chip — "Retail
  // Unfulfilled" (100%-retail orders still unfulfilled there), pulsing the
  // same green as the row highlight. No fulfillment chips at those stores.
  if (data.retail_only) {
    // 2026-10-08 (Eric): the chip is also a filter button — click to show
    // only those orders (retail_store=1), click again to clear.
    return (
      <button
        type="button"
        className={'ots-retail-chip' + (retailActive ? ' ots-retail-chip--active' : '')}
        aria-pressed={!!retailActive}
        onClick={onRetailToggle}
        title={retailActive
          ? 'Showing only 100% retail orders still unfulfilled at this store — click to clear'
          : '100% retail orders still unfulfilled at this store — click to show only those'}
      >
        Retail Unfulfilled <strong style={{ marginLeft: 2 }}>{data.retail_unfulfilled ?? 0}</strong>
      </button>
    );
  }
  return (
    <>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#fef7d1', color: '#3d3320',
        border: '1px solid #e6d180', borderRadius: 12,
        padding: '2px 8px', fontSize: 12, fontWeight: 600,
        whiteSpace: 'nowrap',
      }}>
        Unfulfilled <strong style={{ marginLeft: 2 }}>{data.unfulfilled ?? 0}</strong>
      </span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#e0f2fe', color: '#0c4a6e',
        border: '1px solid #7dd3fc', borderRadius: 12,
        padding: '2px 8px', fontSize: 12, fontWeight: 600,
        whiteSpace: 'nowrap',
      }}>
        Partially Fulfilled <strong style={{ marginLeft: 2 }}>{data.partial ?? 0}</strong>
      </span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#e5f5ec', color: '#0b5132',
        border: '1px solid #8fd6b1', borderRadius: 12,
        padding: '2px 8px', fontSize: 12, fontWeight: 600,
        whiteSpace: 'nowrap',
      }}>
        Fulfilled <strong style={{ marginLeft: 2 }}>{data.fulfilled ?? 0}</strong>
      </span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#fdeaea', color: '#8a1f1f',
        border: '1px solid #f1b8b8', borderRadius: 12,
        padding: '2px 8px', fontSize: 12, fontWeight: 600,
        whiteSpace: 'nowrap',
      }}>
        Cancelled <strong style={{ marginLeft: 2 }}>{data.cancelled ?? 0}</strong>
      </span>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function OrdersTanStack() {
  const { user, emulating, startEmulation, stopEmulation } = useAuth();
  const { activeStore } = useStore();
  const storeFetch = useAuthFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // The query string EXACTLY as this page was opened, frozen before any effect
  // rewrites it. The default-saved-view guard below must judge the user's
  // intent from this, not from the live params: the emulation effect injects
  // `loc` on every mount, so by the time that guard runs the live URL always
  // carries a param and a bare /orders would never get its default view.
  const entryParamsRef = useRef(null);
  if (entryParamsRef.current === null) {
    entryParamsRef.current = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : '');
  }

  // isAdmin gates UI affordances that need all-locations scope (location
  // filter pill, "All Locations" picker). customer_service shares the same
  // all-locations scope, so it gets these affordances too.
  const isAdmin = hasRole(user, ['admin']) || hasRole(user, ['customer_service']);
  // 2026-09-11 (Eric): only these roles print labels / pick lists / selected orders.
  const canPrint = hasRole(user, PRINT_ROLES);

  // ── Virtual mode flag ──────────────────────────────────────────────────
  // Virtual mode disabled — too many trade-offs vs cursor pagination
  // (cold load 6.9s vs 2.5s, 1.22 MB vs 255 KB initial payload). Cursor mode
  // is the default + only mode now. The `?virtual=1` URL param is ignored.
  // Files (useVirtualOrdersQuery.js, VirtualTbody.jsx) kept but inert via the
  // `enabled` flag below — easy to re-enable by flipping this constant.
  const isVirtual = false;

  // ── Seed filter state from URL ─────────────────────────────────────────────
  const urlStatuses       = searchParams.getAll('statuses');
  const urlClubs          = searchParams.getAll('clubs');
  const urlTeams          = searchParams.getAll('teams');
  const urlDateFrom       = searchParams.get('date_from') ?? searchParams.get('dateFrom') ?? '';
  const urlDateTo         = searchParams.get('date_to') ?? searchParams.get('dateTo') ?? '';
  const urlFulfillByFrom  = searchParams.get('fulfill_by_from') ?? searchParams.get('fulfillByFrom') ?? '';
  const urlFulfillByTo    = searchParams.get('fulfill_by_to')   ?? searchParams.get('fulfillByTo')   ?? '';
  const urlPrinteds       = searchParams.getAll('printeds');
  const urlPickStatuses   = searchParams.getAll('pick_statuses').length
    ? searchParams.getAll('pick_statuses') : searchParams.getAll('pickStatuses');
  const urlPressStatuses  = searchParams.getAll('press_statuses').length
    ? searchParams.getAll('press_statuses') : searchParams.getAll('pressStatuses');
  const urlPackStatuses   = searchParams.getAll('pack_statuses').length
    ? searchParams.getAll('pack_statuses') : searchParams.getAll('packStatuses');
  const urlDeliveryMethods = searchParams.getAll('delivery_methods').length
    ? searchParams.getAll('delivery_methods') : searchParams.getAll('deliveryMethods');
  const urlDeliveryNames   = searchParams.getAll('delivery_names').length
    ? searchParams.getAll('delivery_names') : searchParams.getAll('deliveryNames');
  const urlPickupStores   = searchParams.getAll('pickup_stores').length
    ? searchParams.getAll('pickup_stores') : searchParams.getAll('pickupStores');
  const urlStoreStatuses  = (searchParams.getAll('store_statuses').length
    ? searchParams.getAll('store_statuses') : searchParams.getAll('storeStatuses'))
    .map(_normalizeStoreStatusParam);
  const urlIsCustoms      = searchParams.getAll('is_customs').length
    ? searchParams.getAll('is_customs') : searchParams.getAll('isCustoms');
  const urlCrms           = searchParams.getAll('crms');
  const urlSources        = searchParams.getAll('sources');
  const urlRetailStore    = searchParams.get('retail_store') === '1';   // Retail Unfulfilled chip filter (Eric, 2026-10-08)
  const urlProtectStatuses = searchParams.getAll('protect_statuses');
  const urlRiskLevels      = searchParams.getAll('risk_levels');
  const urlDeliveryStatuses = searchParams.getAll('delivery_statuses');
  const urlLocations      = searchParams.getAll('locations');
  const urlSku            = searchParams.get('sku') || '';
  // 2026-08-03: companion boolean to `sku` — when on, backend narrows the
  // SKU match to orders where at least one matching item_state row has
  // qty_picked < qty_total (partial or zero picks). Rendered as a checkbox
  // inside the SKU text filter editor. Requires `sku` to be set to do
  // anything on the server.
  const urlSkuUnpicked    = searchParams.get('sku_unpicked') === '1'
                         || searchParams.get('sku_unpicked') === 'true';
  const urlSkuExclude     = searchParams.get('sku_exclude') === '1'
                         || searchParams.get('sku_exclude') === 'true';
  const urlSplitTeamFulfillment = searchParams.get('split_team_fulfillment') === '1'
                                 || searchParams.get('split_team_fulfillment') === 'true';
  const urlIncludeArchived = searchParams.get('include_archived') === '1'
                          || searchParams.get('includeArchived') === '1'
                          || searchParams.get('include_archived') === 'true'
                          || searchParams.get('includeArchived') === 'true';
  const urlSortKey        = searchParams.get('sort_key') ?? searchParams.get('sortKey') ?? 'created_at';
  const urlSortDir        = searchParams.get('sort_dir') ?? searchParams.get('sortDir') ?? 'desc';

  // ── Filter state (mirrors /api/orders query params) ────────────────────────
  const [statuses, setStatuses]                 = useState(urlStatuses);
  const [clubs, setClubs]                       = useState(urlClubs);
  const [teams, setTeams]                       = useState(urlTeams);
  const [dateFrom, setDateFrom]                 = useState(urlDateFrom);
  const [dateTo, setDateTo]                     = useState(urlDateTo);
  const [fulfillByFrom, setFulfillByFrom]       = useState(urlFulfillByFrom);
  const [fulfillByTo, setFulfillByTo]           = useState(urlFulfillByTo);
  const [printeds, setPrinteds]                 = useState(urlPrinteds);
  const [pickStatuses, setPickStatuses]         = useState(urlPickStatuses);
  const [pressStatuses, setPressStatuses]       = useState(urlPressStatuses);
  const [packStatuses, setPackStatuses]         = useState(urlPackStatuses);
  const [deliveryMethods, setDeliveryMethods]   = useState(urlDeliveryMethods);
  const [deliveryNames, setDeliveryNames]       = useState(urlDeliveryNames);
  const [pickupStores, setPickupStores]         = useState(urlPickupStores);
  const [storeStatuses, setStoreStatuses]       = useState(urlStoreStatuses);
  const [isCustoms, setIsCustoms]               = useState(urlIsCustoms);
  const [crms, setCrms]                         = useState(urlCrms);
  const [sources, setSources]                   = useState(urlSources);
  const [retailStore, setRetailStore]           = useState(urlRetailStore);
  const [protectStatuses, setProtectStatuses]   = useState(urlProtectStatuses);
  const [riskLevels, setRiskLevels]             = useState(urlRiskLevels);
  const [deliveryStatuses, setDeliveryStatuses] = useState(urlDeliveryStatuses);
  const [locations, setLocations]               = useState(urlLocations);
  const [sku, setSku]                           = useState(urlSku);
  const [skuUnpicked, setSkuUnpicked]           = useState(urlSkuUnpicked);
  const [skuExclude, setSkuExclude]             = useState(urlSkuExclude);
  const [splitTeamFulfillment, setSplitTeamFulfillment] = useState(urlSplitTeamFulfillment);
  const [includeArchived, setIncludeArchived]   = useState(urlIncludeArchived);

  // Per-store printout footer HTML — backend-sanitized. Fetched once per
  // store; passed to OrderPrintCard for the bulk-print clone. Null until
  // fetched, in which case OrderPrintCard uses the bundled default.
  const [printoutFooterHtml, setPrintoutFooterHtml] = useState(null);
  useEffect(() => {
      const sk = activeStore || 'soccerpost';
      let cancelled = false;
      authenticatedFetch(`/api/store/printout-footer?store=${sk}`)
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (!cancelled && d?.html != null) setPrintoutFooterHtml(d.html); })
          .catch(() => {});
      return () => { cancelled = true; };
  }, [activeStore]);

  // ── Sort state ─────────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = useState(urlSortKey);
  const [sortDir, setSortDir] = useState(urlSortDir);

  // TanStack sorting state — kept in sync with sortKey/sortDir
  const [sorting, setSorting] = useState(() => {
    if (urlSortKey) return [{ id: urlSortKey, desc: urlSortDir === 'desc' }];
    return [{ id: 'created_at', desc: true }];
  });

  // Sync TanStack sorting -> API sort params
  useEffect(() => {
    if (sorting.length === 0) {
      setSortKey('created_at');
      setSortDir('desc');
    } else {
      const s = sorting[0];
      // Map TanStack column id -> apiSortKey
      const colDef = COLUMN_MAP[s.id];
      const apiKey = colDef?.apiSortKey || s.id;
      setSortKey(apiKey);
      setSortDir(s.desc ? 'desc' : 'asc');
    }
  }, [sorting]);

  // Body class so the global stylesheet can stretch the table to the
  // viewport bottom (sticky pagination footer flush with the screen edge
  // instead of floating above page padding).
  useEffect(() => {
    document.body.classList.add('on-orders-page');
    return () => document.body.classList.remove('on-orders-page');
  }, []);

  // When the sidebar location dropdown selects the same location that's
  // currently in the URL `locations` filter, the filter is redundant — and
  // the chip can't resolve a name (the available-locations list is empty
  // under single-loc emulation), so it shows a raw sloc. Drop it.
  useEffect(() => {
    const sloc = emulating?.shopify_location_id;
    if (!sloc) return;
    if (locations.some(l => String(l) === String(sloc))) {
      setLocations(prev => prev.filter(l => String(l) !== String(sloc)));
    }
  }, [emulating, locations]);

  // ── URL-driven emulation (2026-07-06) ─────────────────────────────────────
  // `?loc=<shopify_location_id>` on the URL forces emulation to that loc.
  // `?loc=all` forces stopEmulation. Absent → fall back to existing client-
  // state emulation (backward compat with old bookmarks). Once we're on
  // /orders, dropdown changes also sync back to the URL so shared links
  // preserve scope. Fetches a full location list separately from the
  // admin-only availableLocations state so non-admins can also URL-drive
  // their scope (e.g. site_manager with 3 locations).
  const [urlEmuLocs, setUrlEmuLocs] = useState([]);
  useEffect(() => {
    let cancelled = false;
    storeFetch('/api/locations').then(r => r.ok ? r.json() : []).then(rows => {
      if (!cancelled) setUrlEmuLocs(Array.isArray(rows) ? rows : []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [storeFetch, activeStore]);

  // URL → emulation. Runs on mount + whenever ?loc= changes.
  // Ref-guarded: skip when we JUST set the URL from emulation change so the
  // two effects don't ping-pong. `lastSyncedLoc.current` records the last
  // value we've already reconciled; if neither the URL nor emulation moved
  // past it, we bail before touching state.
  const urlLocParam = searchParams.get('loc');
  const lastSyncedLoc = useRef(null);
  useEffect(() => {
    if (urlLocParam == null) return;                  // no ?loc= → don't touch
    if (String(lastSyncedLoc.current) === String(urlLocParam)) return;
    if (urlLocParam === 'all' || urlLocParam === '') {
      lastSyncedLoc.current = 'all';
      if (emulating) stopEmulation();
      return;
    }
    if (!urlEmuLocs.length) return;
    const target = urlEmuLocs.find(l =>
      String(l.shopify_location_id) === String(urlLocParam)
    );
    if (!target) return;
    lastSyncedLoc.current = String(target.shopify_location_id);
    // Re-emulate when the stored object is stale (same Shopify location but
    // an old internal id): the backend resolves emulation by id, and a stale
    // id silently widened the list to every location while the sidebar
    // still said "Teams Lancaster" (2026-09-04).
    if (emulating?.shopify_location_id !== target.shopify_location_id
        || String(emulating?.id) !== String(target.id)) {
      startEmulation({
        id: target.id, name: target.name,
        shopify_location_id: target.shopify_location_id,
        is_franchise: target.is_franchise || false,
        store_key: activeStore,
      });
    }
  }, [urlLocParam, urlEmuLocs, emulating, startEmulation, stopEmulation, activeStore]);

  // Emulation → URL. Same ref guard: only push a URL update when the
  // effective loc actually changed since our last write. Otherwise
  // successive renders (which recreate searchParams each time) would
  // trigger a fresh setSearchParams and keep the loop alive.
  // GATE: wait until urlEmuLocs has loaded before we start writing the
  // URL. Otherwise a fresh mount with `?loc=<sid>` sees emulating=null
  // (client state hasn't rehydrated yet) and this effect strips the
  // incoming param down to ?loc=all before Effect 1 gets a chance to
  // startEmulation on the target loc.
  useEffect(() => {
    if (!urlEmuLocs.length) return;
    const desired = emulating?.shopify_location_id
      ? String(emulating.shopify_location_id) : 'all';
    if (String(lastSyncedLoc.current) === desired) return;
    if (String(urlLocParam || '') === desired) {
      lastSyncedLoc.current = desired;
      return;
    }
    lastSyncedLoc.current = desired;
    const next = new URLSearchParams(searchParams);
    next.set('loc', desired);
    setSearchParams(next, { replace: true });
  }, [emulating, urlLocParam, urlEmuLocs, searchParams, setSearchParams]);

  // ── Location filter (admin only) ───────────────────────────────────────────
  const [availableLocations, setAvailableLocations] = useState([]);
  useEffect(() => {
    if (!isAdmin || emulating || activeStore === 'all') { setAvailableLocations([]); return; }
    let cancelled = false;
    storeFetch('/api/locations').then(r => r.ok ? r.json() : []).then(rows => {
      if (cancelled) return;
      // Hide store-only locations (is_store && !is_franchise) — their orders
      // aren't ingested into Operations Hub, so showing them in the filter
      // would always return zero results.
      const active = (rows || []).filter(l => l.active && l.shopify_location_id !== '0'
                                              && !(l.is_store && !l.is_franchise));
      setAvailableLocations(active.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    }).catch(() => { if (!cancelled) setAvailableLocations([]); });
    return () => { cancelled = true; };
  }, [user?.role, emulating, activeStore, storeFetch]); // eslint-disable-line

  // ── Active location TZ (Phase 6 — TZ ambiguity suffix) ─────────────────────
  // Resolves to the IANA TZ of the currently-viewed location when there's
  // exactly one (sidebar emulation OR a single sloc in the location filter).
  // Multi-loc / All-Locations / no available list → null = "ambiguous, append
  // browser-TZ abbrev to timestamps".
  const activeLocTz = useMemo(() => {
    const sloc = emulating?.shopify_location_id
      || (locations && locations.length === 1 ? locations[0] : null);
    if (!sloc) return null;
    const match = (availableLocations || []).find(l => String(l.shopify_location_id) === String(sloc));
    return match?.timezone || null;
  }, [emulating, locations, availableLocations]);

  // ── Store colors ───────────────────────────────────────────────────────────
  const [storeColors, setStoreColors] = useState({});
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORE_COLORS_CACHE_KEY);
      if (raw) { setStoreColors(JSON.parse(raw)); return; }
    } catch {}
    storeFetch('/api/store-colors').then(r => r.ok ? r.json() : {}).then(m => {
      setStoreColors(m || {});
      try { sessionStorage.setItem(STORE_COLORS_CACHE_KEY, JSON.stringify(m || {})); } catch {}
    }).catch(() => {});
  }, [storeFetch]);

  // ── Data query (cursor-paginated — Phase 1) ────────────────────────────────
  // Include emulating's sloc so the data hook refetches when the sidebar
  // location changes (the storeFetch sends X-Emulate-Location automatically,
  // but the hook only refetches when its filterKey changes). Same for the
  // active store so cross-store switches reset.
  const queryArgs = {
    statuses, clubs, teams, dateFrom, dateTo, fulfillByFrom, fulfillByTo, printeds,
    pickStatuses, pressStatuses, packStatuses,
    deliveryMethods, deliveryNames, pickupStores, storeStatuses, isCustoms,
    crms, sources, retailStore, protectStatuses, riskLevels, deliveryStatuses, locations, sku, skuUnpicked, skuExclude, splitTeamFulfillment, includeArchived, sortKey, sortDir,
    emulatingSloc: emulating?.shopify_location_id || null,
    // 2026-09-10: the URL's ?loc= rides on every request so the server scopes
    // by it even before the emulation state has caught up.
    urlLoc: urlLocParam || null,
  };

  // 2026-07-28: gate the initial fetch on emulation state matching the URL's
  // `loc=` intent. Without this, a fresh URL like `?loc=all&statuses=FULFILLED`
  // loaded with a stale emulation in localStorage would fire its first fetch
  // scoped to the OLD emulated sloc — returning 0 rows — before the
  // stopEmulation() effect (line 386) cleared `emulating` on the next render.
  // The re-render then re-fetches with the correct scope, but the user only
  // sees the empty first result until they reload. Definition of "reconciled":
  //   • urlLocParam is null (no scope intent) → always ready
  //   • urlLocParam === 'all' → ready when NOT emulating
  //   • urlLocParam === '<sid>' → ready when emulating matches that sid
  //     (avoids firing against the old sid before startEmulation lands)
  // urlEmuLocs must have loaded before we can trust "matches that sid" — the
  // emu→URL sync effect (line 415) also waits on it for the same reason.
  const locReady = (() => {
    if (urlLocParam == null) return true;
    if (urlLocParam === '' || urlLocParam === 'all') return !emulating;
    if (!urlEmuLocs.length) return false;
    return String(emulating?.shopify_location_id || '') === String(urlLocParam);
  })();

  // Both hooks always run (hooks can't be conditional), but only the active
  // one fetches. The `enabled` flag gates the inner useEffects so the inactive
  // hook stays inert — no API calls, no buffer-walks. Without this, BOTH
  // hooks fire their initial fetches AND the virtual one auto-loads its 500-row
  // buffer (= 10 cursor pages) regardless of mode. Was causing /orders-v2 to
  // fire ~12 API calls on initial load instead of 4.
  const cursorQuery = useOrdersQuery({ ...queryArgs, enabled: !isVirtual && locReady });

  // ── Data query (virtualized — Phase 2) ────────────────────────────────────
  // Same locReady gate as the cursor path — the virtualized hook also fires
  // its initial buffer walk immediately on enable, so a stale emulation would
  // taint the first 10 pages here too.
  const virtualQuery = useVirtualOrdersQuery({ ...queryArgs, enabled: isVirtual && locReady });

  // Pick which query result to use based on mode.
  // Both hooks always run (hooks can't be conditional), but only one drives
  // the rendered output.
  const activeQuery = isVirtual ? virtualQuery : cursorQuery;
  const {
    orders: rawOrders,
    fetching,
    error,
    totalCount,
    filterOptions,
  } = activeQuery;

  // Cursor-paginated extras (Phase 1 only). NOTE: hasPrev/hasNext get
  // overridden further down after `isSearching` + `searchResults` are
  // defined — search mode paginates client-side against searchResults.
  const pageNum  = isVirtual ? 1           : (cursorQuery.pageNum  ?? 1);
  let   hasPrev  = isVirtual ? false        : (cursorQuery.hasPrev  ?? false);
  let   hasNext  = isVirtual ? false        : (cursorQuery.hasNext  ?? false);
  const goNext   = isVirtual ? () => {}     : (cursorQuery.goNext   ?? (() => {}));
  const goPrev   = isVirtual ? () => {}     : (cursorQuery.goPrev   ?? (() => {}));
  const pageSize = isVirtual ? 500          : (cursorQuery.pageSize ?? 50);
  const refresh  = isVirtual ? virtualQuery.refresh : cursorQuery.refresh;

  // Virtual extras (Phase 2 only)
  const hasMore          = isVirtual ? (virtualQuery.hasMore          ?? false)  : false;
  const loadMore         = isVirtual ? (virtualQuery.loadMore         ?? (() => {})) : () => {};
  const PREFETCH_THRESHOLD = isVirtual ? (virtualQuery.PREFETCH_THRESHOLD ?? 0.8) : 0.8;

  // Local orders state for optimistic updates (tags)
  const [orders, setOrders] = useState([]);
  useEffect(() => { setOrders(rawOrders); }, [rawOrders]);

  // ── Search ──────────────────────────────────────────────────────────────
  // Debounced query against /api/orders/search; while a non-empty query is
  // active it overrides the filtered list.
  // Initial value from URL ?search=… so a shared link reproduces the search.
  const [searchQ, setSearchQ]               = useState(() => searchParams.get('search') ?? '');
  const [searchResults, setSearchResults]   = useState(null); // null = not searching
  const [searchLoading, setSearchLoading]   = useState(false);
  // Matches hidden by active filter chips (search respects chips since
  // 2026-08-04; this surfaces what they filtered out — 2026-08-27).
  const [searchHidden, setSearchHidden]     = useState(0);
  const searchDebounceRef                   = useRef(null);
  // Stale-response guard: every runSearch bumps the token and aborts the
  // prior fetch. Late responses whose token != current one are discarded so
  // an in-flight reply for an older query can't clobber the newest result.
  const searchTokenRef                      = useRef(0);
  const searchAbortRef                      = useRef(null);
  const runSearch = useCallback((q) => {
    if (q.trim().length < 2) { setSearchResults(null); setSearchLoading(false); return; }
    // 2026-08-27 stale-emulation guard: portal flows can leave a location
    // emulation in localStorage while this page's URL says loc=all (the
    // URL-sync stopEmulation converges asynchronously — observed 24s). A
    // typed search then fires SCOPED to the stale location and silently
    // returns nothing: SP-318438 searched under a leftover Teams Brookfield
    // emulation came back empty despite the chip reading "All". storeFetch
    // reads emulation from localStorage per-call, so clearing here takes
    // effect for this very request.
    try {
      const _urlLoc = new URLSearchParams(window.location.search).get('loc');
      if (_urlLoc === 'all' && emulating) stopEmulation();
    } catch {}
    if (searchAbortRef.current) { try { searchAbortRef.current.abort(); } catch {} }
    const token = ++searchTokenRef.current;
    const ctrl  = new AbortController();
    searchAbortRef.current = ctrl;
    setSearchLoading(true);
    // 2026-08-04: pass active filter chips so search respects them.
    // Previously search returned every match regardless of filter state,
    // which was confusing when a matched order visually didn't fit the
    // active chips (e.g. searching under an UNFULFILLED filter for an
    // order that reads FULFILLED at the user's loc).
    const p = new URLSearchParams();
    p.set('q', q.trim());
    statuses.forEach(v => p.append('statuses', v));
    packStatuses.forEach(v => p.append('pack_statuses', v));
    pickStatuses.forEach(v => p.append('pick_statuses', v));
    pressStatuses.forEach(v => p.append('press_statuses', v));
    // 2026-08-27: search honors ALL chips (previously only the four above,
    // so e.g. a HIGH-risk chip silently did nothing while searching).
    clubs.forEach(v => p.append('clubs', v));
    teams.forEach(v => p.append('teams', v));
    printeds.forEach(v => p.append('printeds', v));
    deliveryMethods.forEach(v => p.append('delivery_methods', v));
    deliveryNames.forEach(v => p.append('delivery_names', v));
    pickupStores.forEach(v => p.append('pickup_stores', v));
    storeStatuses.forEach(v => p.append('store_statuses', v));
    isCustoms.forEach(v => p.append('is_customs', v));
    crms.forEach(v => p.append('crms', v));
    sources.forEach(v => p.append('sources', v));
    if (retailStore) p.append('retail_store', '1');
    protectStatuses.forEach(v => p.append('protect_statuses', v));
    riskLevels.forEach(v => p.append('risk_levels', v));
    deliveryStatuses.forEach(v => p.append('delivery_statuses', v));
    if (urlLocParam) p.set('loc', urlLocParam);
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo)   p.set('date_to', dateTo);
    if (fulfillByFrom) p.set('fulfill_by_from', fulfillByFrom);
    if (fulfillByTo)   p.set('fulfill_by_to',   fulfillByTo);
    // Declare All-Locations intent explicitly so the SERVER can ignore a
    // stale X-Emulate-Location header (SP-355662 2026-08-28: a pre-guard
    // bundle kept emulating Tinton Falls while the URL said loc=all —
    // search came back empty until a hard refresh).
    try {
      if (new URLSearchParams(window.location.search).get('loc') === 'all') p.set('loc', 'all');
    } catch {}
    // Hidden-match hint: when filter chips are active, also run the search
    // UNFILTERED so we can tell the user how many matches their chips hid
    // (e.g. searching a 94%-packed order under Pack:Complete → 0 rows with
    // zero explanation — SP-314955, 2026-08-27).
    const hasChips = (statuses.length + packStatuses.length + pickStatuses.length + pressStatuses.length
      + clubs.length + teams.length + printeds.length + deliveryMethods.length + deliveryNames.length
      + pickupStores.length + storeStatuses.length + isCustoms.length + crms.length + sources.length
      + protectStatuses.length + riskLevels.length + deliveryStatuses.length + (retailStore ? 1 : 0)) > 0
      || !!(dateFrom || dateTo || fulfillByFrom || fulfillByTo);
    const unfilteredFetch = hasChips
      ? storeFetch(`/api/orders/search?q=${encodeURIComponent(q.trim())}`, { signal: ctrl.signal })
          .then(r => r.json()).catch(() => null)
      : Promise.resolve(null);
    storeFetch(`/api/orders/search?${p.toString()}`, { signal: ctrl.signal })
      .then(r => r.json())
      .then(async data => {
        if (token !== searchTokenRef.current) return;
        const rows = data.orders || [];
        setSearchResults(rows);
        const unf = await unfilteredFetch;
        if (token !== searchTokenRef.current) return;
        setSearchHidden(unf ? Math.max(0, (unf.orders || []).length - rows.length) : 0);
      })
      .catch(e => { if (e?.name !== 'AbortError' && token === searchTokenRef.current) { setSearchResults([]); setSearchHidden(0); } })
      .finally(() => { if (token === searchTokenRef.current) setSearchLoading(false); });
  }, [storeFetch, statuses, packStatuses, pickStatuses, pressStatuses,
      clubs, teams, printeds, deliveryMethods, deliveryNames, pickupStores,
      storeStatuses, isCustoms, crms, sources, retailStore, protectStatuses, riskLevels, deliveryStatuses,
      dateFrom, dateTo, fulfillByFrom, fulfillByTo, emulating, stopEmulation]);
  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchQ(v);
    clearTimeout(searchDebounceRef.current);
    if (v.trim().length < 2) { setSearchResults(null); setSearchHidden(0); return; }
    searchDebounceRef.current = setTimeout(() => runSearch(v), 350);
  };
  const clearSearch = () => {
    setSearchQ('');
    setSearchResults(null);
    setSearchHidden(0);
    setSearchLoading(false);
    clearTimeout(searchDebounceRef.current);
    if (searchAbortRef.current) { try { searchAbortRef.current.abort(); } catch {} }
    searchTokenRef.current += 1;
  };
  // Mount-time: if ?search=… is in the URL, fire the same debounced query
  // the user would have triggered by typing. Lets shared links land on
  // populated results without the user having to click into the box.
  //
  // 2026-07-27: gate on URL/emulation consistency. `storeFetch` reads
  // `emulating` from localStorage per-call and attaches X-Emulate-Location.
  // On a fresh page load with `?loc=all&search=X`, this effect used to
  // fire BEFORE the URL-sync effect (line 381+) called `stopEmulation()`,
  // so the search request went out scoped to whatever loc was in
  // localStorage from the prior session — producing a false "no orders
  // found" render until a subsequent search fired with the emulation
  // cleared. Wait until React state matches the URL intent before firing
  // the initial search. Only fires once via the ref guard so subsequent
  // typed searches use the debounced path.
  // 2026-08-06: dropped the one-shot ref, then hardened again the same day.
  // A shared URL like ?loc=<sid>&search=X kept failing intermittently because
  // the URL→emulation cascade completes asynchronously (fetch /api/locations,
  // then startEmulation, then emulating updates). Two failure shapes:
  //   (a) The effect fires BEFORE emulation resolves → gate returns → never
  //       fires again once emulation converged. Fixed by removing the
  //       one-shot ref (below).
  //   (b) The effect fires with STALE emulation (prior session still in
  //       localStorage) → search runs scoped to wrong loc → returns [] →
  //       later stopEmulation fires → searchResults is [] (not null) →
  //       "already have results" guard returns → never re-fetches under
  //       correct scope. Fixed by tracking the emulation-at-time-of-search
  //       in a ref and forcing another fetch when it drifts.
  // Once a fetch is in-flight, the token guard inside runSearch handles
  // concurrent aborts, so re-firing is safe.
  const lastSearchEmuRef = useRef({ q: '', emu: undefined });
  useEffect(() => {
    if (searchQ.trim().length < 2) return;
    if (searchLoading) return;              // fetch in flight — let it finish
    const urlLoc = searchParams.get('loc');
    if (urlLoc === 'all' && emulating) return;                            // wait for stopEmulation
    if (urlLoc && urlLoc !== 'all' &&
        String(emulating?.shopify_location_id ?? '') !== urlLoc) return;  // wait for startEmulation
    const emuKey = String(emulating?.shopify_location_id ?? '');
    const sameScope = lastSearchEmuRef.current.q === searchQ
                   && lastSearchEmuRef.current.emu === emuKey;
    if (sameScope && searchResults !== null) return;  // already searched under this scope
    lastSearchEmuRef.current = { q: searchQ, emu: emuKey };
    runSearch(searchQ);
  }, [emulating, searchParams, searchQ, runSearch, searchResults, searchLoading]);
  // Store-switch: /api/orders/search is per-store, so an active query when
  // the user changes stores must re-fire against the new store. Without
  // this, searchResults stays stuck at the prior store's (often empty)
  // response and the user sees no rows until they clear + retype the
  // query — even when the new store has the order.
  const lastStoreForSearchRef = useRef(activeStore);
  useEffect(() => {
    if (lastStoreForSearchRef.current === activeStore) return;
    lastStoreForSearchRef.current = activeStore;
    if (searchQ.trim().length >= 2) {
      setSearchResults(null);
      runSearch(searchQ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStore]);
  const isSearching = searchQ.trim().length >= 2;
  // Search-mode pagination override. Search returns up to 500 rows in
  // one shot; we paginate client-side by slicing to pageSize per page.
  // hasPrev/hasNext were declared with `let` above so we can reassign
  // here without a TDZ error on isSearching / searchResults.
  const _searchTotal = isSearching ? (searchResults || []).length : 0;
  const _searchPagesTotal = isSearching ? Math.max(1, Math.ceil(_searchTotal / pageSize)) : 1;
  if (isSearching) {
    hasPrev = pageNum > 1;
    hasNext = pageNum < _searchPagesTotal;
  }
  // Overshoot guard: if the URL points at a page beyond the search's
  // last page (e.g. ?page=2&search=SP-323744 when only 1 row matches),
  // snap pageNum back to 1 so we render results instead of a blank
  // slice. Only fires once search results have actually arrived
  // (searchLoading=false) so we don't preempt in-flight fetches.
  const _setPageNumRef = useRef(cursorQuery.setPageNum);
  _setPageNumRef.current = cursorQuery.setPageNum;
  useEffect(() => {
    if (!isSearching || searchLoading || isVirtual) return;
    if (pageNum > _searchPagesTotal && typeof _setPageNumRef.current === 'function') {
      _setPageNumRef.current(1);
    }
  }, [isSearching, searchLoading, isVirtual, pageNum, _searchPagesTotal]);
  // Search results come from /api/orders/search which returns rows in relevance
  // order and has no sort_key/sort_dir params. Without this client-side sort,
  // clicking any column header while a search is active does nothing (rows
  // stayed in relevance order — user reported ":A" search + Created sort was
  // broken). Sort here so the header click still reorders the visible rows.
  const displayedOrders = useMemo(() => {
    const rows = isSearching ? (searchResults || []) : orders;
    if (!isSearching) return rows;
    // Sort search results client-side. Backend returns up to 500 rows in
    // relevance/id order; header clicks reorder without re-fetching.
    const dir = sortDir === 'asc' ? 1 : -1;
    const keyOf = (fo) => {
      const o = fo.order || {};
      switch (sortKey) {
        case 'created_at':   return o.created_at || '';
        case 'name':         return o.name || '';
        case 'customer':     return (customerName(fo) || '').toLowerCase();
        case 'customer_email': return (fo.order?.customer?.email || '').toLowerCase();
        case 'total':        return Number(o.total_price || 0);
        case 'items':        return (o.line_items || []).reduce((s, li) => s + (li.quantity || 0), 0);
        case 'pick_pct':     return fo.pick_pct  ?? -1;
        case 'press_pct':    return fo.press_pct ?? -1;
        case 'pack_pct':     return fo.pack_pct  ?? -1;
        case 'club':         return (o.club || '').toLowerCase();
        case 'fulfill_by':   return o.fulfill_by || '';
        case 'status':       return o.fulfillment_status || '';
        case 'source':       return o.source || '';
        default:             return '';
      }
    };
    const sorted = rows.length < 2 ? rows : [...rows].sort((a, b) => {
      const va = keyOf(a), vb = keyOf(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
    // Client-side page slice — search endpoint returns the full result set
    // (up to 500) in one shot; the pagination footer walks it here.
    // Fixed 2026-07-26 — before this, page=2 on a search rendered the same
    // rows as page=1 because displayedOrders wasn't sliced.
    const _start = Math.max(0, (pageNum - 1) * pageSize);
    return sorted.slice(_start, _start + pageSize);
  }, [isSearching, searchResults, orders, sortKey, sortDir, pageNum, pageSize]);

  // ── Column width tracking for virtual row alignment ────────────────────────
  // Measured from the real thead after each render so virtual cells match headers.
  const theadRef = useRef(null);
  const [colWidths, setColWidths] = useState({});
  useLayoutEffect(() => {
    if (!isVirtual) return;
    const thead = theadRef.current;
    if (!thead) return;
    const ths = thead.querySelectorAll('th[data-colid]');
    const map = {};
    ths.forEach(th => {
      map[th.dataset.colid] = th.getBoundingClientRect().width;
    });
    // Only update if values changed to avoid render loop
    setColWidths(prev => {
      const same = Object.keys(map).every(k => prev[k] === map[k]);
      return same ? prev : map;
    });
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Column visibility & order ──────────────────────────────────────────────
  const [hiddenCols, setHiddenCols] = useState(() => loadHiddenCols());
  const [colOrder, setColOrder]     = useState(() => loadColOrder());

  // On mount, hydrate column visibility & order from the server's per-user
  // preferences (operations_users.preferences JSONB). The localStorage values
  // are used as the synchronous initial state (fast path); the server values
  // override once they land, so the user's columns follow them across
  // browsers and devices instead of being trapped per-browser.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tok = localStorage.getItem('operations_token');
        if (!tok) return;
        const res = await fetch('/api/me', {
          headers: { Authorization: `Bearer ${tok}` },
        });
        if (!res.ok || cancelled) return;
        const me = await res.json();
        const oc = me?.preferences?.orders_columns;
        if (!oc) return;
        if (Array.isArray(oc.hidden)) {
          const nextHidden = new Set(oc.hidden);
          setHiddenCols(nextHidden);
          try { localStorage.setItem('ordersV2HiddenCols', JSON.stringify(oc.hidden)); } catch {}
        }
        if (Array.isArray(oc.order) && oc.order.length) {
          const reconciled = migrateColOrder(reconcileColOrder(oc.order));
          setColOrder(reconciled);
          try { localStorage.setItem(COL_ORDER_KEY, JSON.stringify(reconciled)); } catch {}
        }
      } catch { /* offline / no token — stick with localStorage */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── CSV export state ───────────────────────────────────────────────────────
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ done: 0, total: 0 });

  // ── Column pinning — {colId: 'left'|'right'} — persisted per-browser ──────
  const [pinnedCols, setPinnedCols] = useState(() => {
    try {
      const saved = localStorage.getItem('ordersV2PinnedCols');
      if (saved) return JSON.parse(saved);
    } catch {}
    return {};
  });

  const setPin = useCallback((colId, side) => {
    setPinnedCols(prev => {
      const next = {};
      // Re-build map so newly-pinned col goes at END (latest = innermost from edge)
      for (const [k, v] of Object.entries(prev)) {
        if (k !== colId) next[k] = v;
      }
      if (side === 'left' || side === 'right') next[colId] = side;
      try { localStorage.setItem('ordersV2PinnedCols', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // Only one pin menu open at a time across all headers
  const [openPinMenuId, setOpenPinMenuId] = useState(null);

  // ── Saved Views ──────────────────────────────────────────────────────────
  const [savedViews, setSavedViews] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const defaultViewAppliedRef = useRef(false);

  const reloadSavedViews = useCallback(() => {
    storeFetch('/api/saved-views')
      .then(r => r.ok ? r.json() : [])
      .then(rows => setSavedViews(Array.isArray(rows) ? rows : []))
      .catch(() => setSavedViews([]));
  }, [storeFetch]);

  useEffect(() => { reloadSavedViews(); }, [reloadSavedViews]);

  // Snapshot the current Orders view state into a JSON-serializable blob.
  const captureViewState = useCallback(() => ({
    v: 1,
    filters: {
      statuses, clubs, teams, dateFrom, dateTo, fulfillByFrom, fulfillByTo, printeds,
      pickStatuses, pressStatuses, packStatuses,
      deliveryMethods, deliveryNames, pickupStores, storeStatuses,
      isCustoms, crms, sources, retailStore, protectStatuses, riskLevels, deliveryStatuses, locations,
      includeArchived,
    },
    search: searchQ,
    sort: { key: sortKey, dir: sortDir },
    columns: {
      hidden: [...hiddenCols],
      order: colOrder,
    },
  }), [statuses, clubs, teams, dateFrom, dateTo, fulfillByFrom, fulfillByTo, printeds,
       pickStatuses, pressStatuses, packStatuses,
       deliveryMethods, deliveryNames, pickupStores, storeStatuses,
       isCustoms, crms, sources, retailStore, protectStatuses, riskLevels, deliveryStatuses, locations, includeArchived,
       searchQ, sortKey, sortDir, hiddenCols, colOrder]);

  // Restore a captured state. Each section is optional so older blobs still load.
  const applyViewState = useCallback((state) => {
    if (!state || typeof state !== 'object') return;
    const f = state.filters || {};
    setStatuses(Array.isArray(f.statuses) ? f.statuses : []);
    setClubs(Array.isArray(f.clubs) ? f.clubs : []);
    setTeams(Array.isArray(f.teams) ? f.teams : []);
    setDateFrom(f.dateFrom || '');
    setDateTo(f.dateTo || '');
    setFulfillByFrom(f.fulfillByFrom || '');
    setFulfillByTo(f.fulfillByTo || '');
    setPrinteds(Array.isArray(f.printeds) ? f.printeds : []);
    setPickStatuses(Array.isArray(f.pickStatuses) ? f.pickStatuses : []);
    setPressStatuses(Array.isArray(f.pressStatuses) ? f.pressStatuses : []);
    setPackStatuses(Array.isArray(f.packStatuses) ? f.packStatuses : []);
    setDeliveryMethods(Array.isArray(f.deliveryMethods) ? f.deliveryMethods : []);
    setPickupStores(Array.isArray(f.pickupStores) ? f.pickupStores : []);
    setStoreStatuses(Array.isArray(f.storeStatuses) ? f.storeStatuses.map(_normalizeStoreStatusParam) : []);
    setIsCustoms(Array.isArray(f.isCustoms) ? f.isCustoms : []);
    setCrms(Array.isArray(f.crms) ? f.crms : []);
    setSources(Array.isArray(f.sources) ? f.sources : []);
    setRetailStore(!!f.retailStore);
    setProtectStatuses(Array.isArray(f.protect_statuses) ? f.protect_statuses : []);
    setRiskLevels(Array.isArray(f.risk_levels) ? f.risk_levels : []);
    setDeliveryStatuses(Array.isArray(f.delivery_statuses) ? f.delivery_statuses : []);
    setLocations(Array.isArray(f.locations) ? f.locations : []);
    setIncludeArchived(Boolean(f.includeArchived));

    if (typeof state.search === 'string') setSearchQ(state.search);

    if (state.sort && typeof state.sort === 'object') {
      const k = state.sort.key || 'created_at';
      const d = state.sort.dir === 'asc' ? 'asc' : 'desc';
      setSortKey(k);
      setSortDir(d);
      setSorting([{ id: k, desc: d === 'desc' }]);
    }

    const cols = state.columns || {};
    if (Array.isArray(cols.hidden)) {
      const next = new Set(cols.hidden);
      setHiddenCols(next);
      saveHiddenCols(next);
    }
    if (Array.isArray(cols.order) && cols.order.length) {
      const reconciled = reconcileColOrder(cols.order);
      setColOrder(reconciled);
      saveColOrder(reconciled);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCurrentView = useCallback(async (name) => {
    const r = await storeFetch('/api/saved-views', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, state: captureViewState() }),
    });
    if (r.status === 409) {
      throw new Error('A saved view with that name already exists');
    }
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      throw new Error(j.detail || 'Could not save view');
    }
    reloadSavedViews();
  }, [storeFetch, captureViewState, reloadSavedViews]);

  const handleLoadView = useCallback((view) => {
    if (view && view.state) applyViewState(view.state);
  }, [applyViewState]);

  const handleSetDefaultView = useCallback(async (view) => {
    await storeFetch(`/api/saved-views/${view.id}/default`, { method: 'POST' });
    reloadSavedViews();
  }, [storeFetch, reloadSavedViews]);

  const handleDeleteView = useCallback(async (id) => {
    await storeFetch(`/api/saved-views/${id}`, { method: 'DELETE' });
    reloadSavedViews();
  }, [storeFetch, reloadSavedViews]);

  // Auto-apply the user's default view on first mount, ONLY when the URL has
  // no filter/sort params yet. If the user navigated here with explicit
  // ?status=… etc, we respect that and don't override.
  useEffect(() => {
    if (defaultViewAppliedRef.current) return;
    if (!savedViews || savedViews.length === 0) return;
    const def = savedViews.find(v => v.is_default);
    if (!def) { defaultViewAppliedRef.current = true; return; }
    // Detect "URL-driven entry": the caller asked for something specific, so
    // the default saved view must not be layered on top of it.
    //
    // 2026-09-11: this used to be a hand-maintained allow-list of filter
    // params, and it had drifted — `loc`, `search`, `sku`, `sku_unpicked`,
    // `protect_statuses`, `risk_levels` and `delivery_statuses` were all read
    // by this component but missing from the list. Opening
    // /orders?loc=78353924274 therefore looked like "no URL state", the default
    // view was applied, and its Status filter appeared out of nowhere — the
    // URL visibly rewrote itself to ...&statuses=UNFULFILLED a moment after
    // load. `search` had the same hole, despite the writer below explicitly
    // supporting a pasted /orders?search=… link.
    //
    // Inverted to a deny-list so it cannot drift again: ANY query param counts
    // as intent except the few that carry none. A new filter param is covered
    // the day it is added, with nothing to remember.
    const NON_FILTER_PARAMS = new Set(['store']);
    const entryParams = entryParamsRef.current || searchParams;
    const hasUrlState = Array.from(entryParams.keys())
      .some(k => !NON_FILTER_PARAMS.has(k));
    if (!hasUrlState) {
      applyViewState(def.state);
    }
    defaultViewAppliedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedViews, applyViewState]);

  // ── Drag-to-reorder columns ────────────────────────────────────────────────
  const dragColRef = useRef(null);

  const handleColDragStart = (e, colId) => {
    dragColRef.current = colId;
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleColDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleColDrop = (e, targetId) => {
    e.preventDefault();
    const fromId = dragColRef.current;
    if (!fromId || fromId === targetId) return;
    setColOrder(prev => {
      const next = [...prev];
      const fi = next.indexOf(fromId);
      const ti = next.indexOf(targetId);
      if (fi < 0 || ti < 0) return prev;
      next.splice(fi, 1);
      next.splice(ti, 0, fromId);
      saveColOrder(next);
      return next;
    });
    dragColRef.current = null;
  };
  const handleColDragEnd = () => { dragColRef.current = null; };

  // ── Visible columns (ordered, filtered, split by pin state) ───────────────
  const visibleColDefs = useMemo(() => {
    // 1. Merge colOrder with COLUMN_DEFS (handle newly added cols not in saved
    // order). Each missing column is inserted at its ORIGINAL_ORDER slot —
    // walk the current order from the end and drop the new column right after
    // the last existing column that comes before it in ORIGINAL_ORDER.
    // Naive push-to-end stranded new columns at the far right of users'
    // saved layouts, so they couldn't see additions like 'total_reworks'
    // in the canonical position.
    const byId = Object.fromEntries(COLUMN_DEFS.map(c => [c.id, c]));
    // 2026-07-03: also strip filterOnly columns (SKU) from the ordered
    // rendered list — they only exist for AdvancedFilters, never as table
    // cells. Without this, a stale colOrder that includes 'sku' (or the
    // insert-new-columns branch below adding it) renders a blank SKU header.
    let ordered = colOrder
      .filter(id => byId[id] && !byId[id].filterOnly)
      .map(id => byId[id]);
    const origIdx = (id) => ORIGINAL_ORDER.indexOf(id);
    for (const def of COLUMN_DEFS) {
      if (def.filterOnly) continue;
      if (colOrder.includes(def.id)) continue;
      const myPos = origIdx(def.id);
      let insertAt = ordered.length;  // default: append (works for cols not in ORIGINAL_ORDER)
      if (myPos >= 0) {
        insertAt = 0;
        for (let i = ordered.length - 1; i >= 0; i--) {
          const idx = origIdx(ordered[i].id);
          if (idx >= 0 && idx < myPos) {
            insertAt = i + 1;
            break;
          }
        }
      }
      ordered.splice(insertAt, 0, def);
    }
    ordered = ordered.filter(def => !hiddenCols.has(def.id));

    // 2. Split into [checkbox, name, pinnedLeft(in pin order), unpinned, pinnedRight(in pin order)]
    const checkbox = ordered.find(d => d.id === 'checkbox');
    const name     = ordered.find(d => d.id === 'name');
    const rest     = ordered.filter(d => d.id !== 'checkbox' && d.id !== 'name');

    const leftIds  = Object.keys(pinnedCols).filter(k => pinnedCols[k] === 'left');
    const rightIds = Object.keys(pinnedCols).filter(k => pinnedCols[k] === 'right');
    const leftSet  = new Set(leftIds);
    const rightSet = new Set(rightIds);

    const unpinned    = rest.filter(d => !leftSet.has(d.id) && !rightSet.has(d.id));
    const pinnedLeft  = leftIds.map(id => byId[id]).filter(d => d && !hiddenCols.has(d.id));
    const pinnedRight = rightIds.map(id => byId[id]).filter(d => d && !hiddenCols.has(d.id));

    return [
      ...(checkbox ? [checkbox] : []),
      ...(name ? [name] : []),
      ...pinnedLeft,
      ...unpinned,
      ...pinnedRight,
    ];
  }, [colOrder, hiddenCols, pinnedCols]);

  // ── Compute sticky offsets for pinned columns ──────────────────────────────
  // All newly-pinned columns get a fixed 140px width so offsets are predictable.
  const PIN_COL_WIDTH = 140;
  const CB_WIDTH = 40;
  const ORDER_WIDTH = 110;

  const pinLayout = useMemo(() => {
    const leftIds  = Object.keys(pinnedCols).filter(k => pinnedCols[k] === 'left');
    const rightIds = Object.keys(pinnedCols).filter(k => pinnedCols[k] === 'right');

    // Filter out any that are hidden
    const visIds = new Set(visibleColDefs.map(d => d.id));
    const visLeftIds  = leftIds.filter(id => visIds.has(id));
    const visRightIds = rightIds.filter(id => visIds.has(id));

    // Map colId -> { side, stickyOffset, isLastLeft, isFirstRight }
    const map = {};
    visLeftIds.forEach((id, i) => {
      map[id] = {
        side: 'left',
        stickyOffset: CB_WIDTH + ORDER_WIDTH + i * PIN_COL_WIDTH,
        isLastLeft: i === visLeftIds.length - 1,
        isFirstRight: false,
        zIndex: 28 - i,
      };
    });
    visRightIds.forEach((id, i) => {
      map[id] = {
        side: 'right',
        stickyOffset: i * PIN_COL_WIDTH,
        isLastLeft: false,
        isFirstRight: i === 0,
        zIndex: 28 - i,
      };
    });
    return map;
  }, [pinnedCols, visibleColDefs]);

  // ── Build dynamic choices for columns populated from filterOptions ─────────
  const dynamicChoices = useMemo(() => {
    const clubChoices = (filterOptions.clubs || []).map(c => ({ label: c, value: c }));
    const teamChoices = (() => {
      const allTeams = new Set();
      const selectedClubs = Array.isArray(clubs) ? clubs : [];
      if (selectedClubs.length === 0) {
        Object.values(filterOptions.teams_by_club || {}).forEach(ts => ts.forEach(t => allTeams.add(t)));
      } else {
        selectedClubs.forEach(c => (filterOptions.teams_by_club?.[c] || []).forEach(t => allTeams.add(t)));
      }
      return [...allTeams].sort().map(t => ({ label: t, value: t }));
    })();
    const pickupStoreChoices = (filterOptions.pickup_stores || Object.keys(storeColors)).map(s => ({ label: s, value: s }));
    const crmChoices = (filterOptions.crms || []).map(c => ({ label: c, value: c }));
    // Delivery filter: "Shipping" + each known pickup store with its
    // matching color chip, so the filter view mirrors the cell display.
    // Values are namespaced ("ship:SHIPPING" vs "store:<name>") so the
    // setter can route SHIPPING → delivery_methods and store-name →
    // pickup_stores. Stripping the prefix recovers the API value.
    // 2026-07-06: distinct shipping presentedNames from filter-options
    // (e.g. "Team Portal Flat Rate Shipping", "Ground Advantage") appear
    // as their own choices below the generic "Shipping" entry. Values are
    // prefixed `ship_name:` so the URL-sync layer routes them to the
    // `delivery_names` API param instead of `delivery_methods` (which
    // takes SHIPPING/PICK_UP enums).
    const shipNameChoices = (filterOptions.delivery_names || []).map(n => ({
      label: (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span aria-hidden="true">🚚</span>
        <span style={{ fontSize: 12 }}>{n}</span>
      </span>),
      value: `ship_name:${n}`,
      searchValue: n,
    }));
    const deliveryChoices = [
      {
        label: (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span aria-hidden="true">🚚</span>
          <span style={{ fontSize: 12 }}>Shipping (any)</span>
        </span>),
        value: 'ship:SHIPPING',
        searchValue: 'shipping any',
      },
      ...shipNameChoices,
      ...(filterOptions.pickup_stores || Object.keys(storeColors)).map(s => {
        const c = storeColors[s] || { background: '#dbeafe', text: '#1e40af' };
        return {
          label: (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden="true">🏪</span>
            <span style={{
              display: 'inline-block', padding: '1px 7px', borderRadius: 10,
              fontSize: 11, fontWeight: 600, background: c.background, color: c.text,
            }}>{s}</span>
          </span>),
          value: `store:${s}`,
          searchValue: s,
        };
      }),
    ];
    return { club: clubChoices, teams: teamChoices, pickup_store: pickupStoreChoices, crm: crmChoices, delivery: deliveryChoices };
  }, [filterOptions, clubs, storeColors]);

  // ── URL sync ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const p = new URLSearchParams();
    // Preserve the loc emulation param — this filter-sync effect rebuilds
    // the query string from scratch and would otherwise strip ?loc= off
    // every keystroke, launching the emu↔URL ping-pong (2026-07-06 bug).
    // Read from window.location.search rather than the closure-captured
    // searchParams so we see writes from the emu→URL effect that ran in
    // the same batch (react-router uses history.replaceState synchronously
    // in effects, so the window URL is authoritative).
    const preservedLoc = new URLSearchParams(window.location.search).get('loc');
    if (preservedLoc != null) p.set('loc', preservedLoc);
    // Preserve virtual=1 flag
    if (isVirtual) p.set('virtual', '1');
    statuses.forEach(s => p.append('statuses', s));
    clubs.forEach(c => p.append('clubs', c));
    teams.forEach(t => p.append('teams', t));
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo)   p.set('date_to', dateTo);
    if (fulfillByFrom) p.set('fulfill_by_from', fulfillByFrom);
    if (fulfillByTo)   p.set('fulfill_by_to',   fulfillByTo);
    printeds.forEach(v => p.append('printeds', v));
    pickStatuses.forEach(v => p.append('pick_statuses', v));
    pressStatuses.forEach(v => p.append('press_statuses', v));
    packStatuses.forEach(v => p.append('pack_statuses', v));
    deliveryMethods.forEach(v => p.append('delivery_methods', v));
    deliveryNames.forEach(v => p.append('delivery_names', v));
    pickupStores.forEach(s => p.append('pickup_stores', s));
    storeStatuses.forEach(v => p.append('store_statuses', v));
    isCustoms.forEach(v => p.append('is_customs', v));
    crms.forEach(v => p.append('crms', v));
    sources.forEach(v => p.append('sources', v));
    if (retailStore) p.append('retail_store', '1');
    protectStatuses.forEach(v => p.append('protect_statuses', v));
    riskLevels.forEach(v => p.append('risk_levels', v));
    deliveryStatuses.forEach(v => p.append('delivery_statuses', v));
    locations.forEach(v => p.append('locations', v));
    if (sku.trim()) {
      p.set('sku', sku.trim());
      if (skuUnpicked) p.set('sku_unpicked', '1');
      if (skuExclude) p.set('sku_exclude', '1');
    }
    if (splitTeamFulfillment) p.set('split_team_fulfillment', '1');
    if (includeArchived) p.set('include_archived', '1');
    if (sortKey !== 'created_at') p.set('sort_key', sortKey);
    if (sortDir !== 'desc')       p.set('sort_dir', sortDir);
    if (!isVirtual && pageNum > 1) p.set('page', String(pageNum));
    // Search query: include only when ≥2 chars (matches the search-trigger
    // threshold). Lets a teammate paste /orders?search=245841 and land on
    // the same result set.
    if (searchQ.trim().length >= 2) p.set('search', searchQ.trim());
    setSearchParams(p, { replace: true });
  }, [statuses, clubs, teams, dateFrom, dateTo, fulfillByFrom, fulfillByTo, printeds, pickStatuses, pressStatuses,
      packStatuses, deliveryMethods, deliveryNames, pickupStores, storeStatuses, isCustoms, crms,
      sources, retailStore, protectStatuses, riskLevels, deliveryStatuses, locations, sku, skuUnpicked, skuExclude, splitTeamFulfillment, includeArchived, sortKey, sortDir, pageNum, isVirtual, searchQ]); // eslint-disable-line

  // ── TanStack table instance ────────────────────────────────────────────────
  // We build a minimal column list for TanStack; actual rendering is manual
  // so we only use TanStack for sort state management.
  const tsColumns = useMemo(() =>
    visibleColDefs.map(def => ({
      id: def.id,
      accessorKey: def.id,
      enableSorting: def.sortable,
    })),
  [visibleColDefs]);

  const table = useReactTable({
    data: orders,
    columns: tsColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    // Two-state sort cycle: desc ↔ asc. Disables the third "no sort" state
    // so a third click toggles back to desc instead of resetting.
    enableSortingRemoval: false,
  });

  // ── Bulk select ────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds]     = useState(new Set());
  const [selectingAll, setSelectingAll]   = useState(false);
  const [selectMenuOpen, setSelectMenuOpen] = useState(false);
  const lastSelectedIdxRef = useRef(null);

  // Bulk notification modal — one message applied to N selected orders.
  // Same role gate as the Settings → Notifications tab (admin / site_manager /
  // customer_service / franchise / user). Backend rejects cross-store
  // selections, so disable the button when activeStore === 'all'.
  const NOTIFICATION_ROLES = ['admin', 'site_manager', 'customer_service', 'franchise', 'user'];
  const canBulkNotify = hasRole(user, NOTIFICATION_ROLES);
  const [bulkNotifOpen, setBulkNotifOpen] = useState(false);
  const [bulkNotifMessage, setBulkNotifMessage] = useState('');
  const [bulkNotifSaving, setBulkNotifSaving] = useState(false);
  const [bulkNotifError, setBulkNotifError] = useState(null);
  const [bulkNotifToast, setBulkNotifToast] = useState(null);  // {msg, key}

  const allPageIds = orders.map(fo => fo.order?.id).filter(Boolean);
  const allPageSelected = allPageIds.length > 0 && allPageIds.every(id => selectedIds.has(id));
  const somePageSelected = allPageIds.some(id => selectedIds.has(id));

  const handleSelectAll = (e) => {
    e.stopPropagation();
    if (allPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allPageIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allPageIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const handleRowCheckbox = (e, orderId, rowIndex) => {
    e.stopPropagation();
    const isShift = e.nativeEvent?.shiftKey;
    if (isShift && lastSelectedIdxRef.current != null && rowIndex != null) {
      const start = Math.min(lastSelectedIdxRef.current, rowIndex);
      const end   = Math.max(lastSelectedIdxRef.current, rowIndex);
      const target = !selectedIds.has(orderId);
      setSelectedIds(prev => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) {
          const oid = orders[i]?.order?.id;
          if (!oid) continue;
          if (target) next.add(oid); else next.delete(oid);
        }
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
        return next;
      });
    }
    if (rowIndex != null) lastSelectedIdxRef.current = rowIndex;
  };

  const handleSelectAllFiltered = async () => {
    setSelectMenuOpen(false);
    setSelectingAll(true);
    try {
      const params = new URLSearchParams();
      statuses.forEach(s => params.append('statuses', s));
      clubs.forEach(c => params.append('clubs', c));
      teams.forEach(t => params.append('teams', t));
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo)   params.set('date_to', dateTo);
      if (fulfillByFrom) params.set('fulfill_by_from', fulfillByFrom);
      if (fulfillByTo)   params.set('fulfill_by_to',   fulfillByTo);
      printeds.forEach(v => params.append('printeds', v));
      pickStatuses.forEach(v => params.append('pick_statuses', v));
      pressStatuses.forEach(v => params.append('press_statuses', v));
      packStatuses.forEach(v => params.append('pack_statuses', v));
      deliveryMethods.forEach(v => params.append('delivery_methods', v));
      deliveryNames.forEach(v => params.append('delivery_names', v));
      pickupStores.forEach(s => params.append('pickup_stores', s));
      storeStatuses.forEach(v => params.append('store_statuses', v));
      isCustoms.forEach(v => params.append('is_customs', v));
      crms.forEach(v => params.append('crms', v));
      sources.forEach(v => params.append('sources', v));
      if (retailStore) params.append('retail_store', '1');
      protectStatuses.forEach(v => params.append('protect_statuses', v));
    riskLevels.forEach(v => params.append('risk_levels', v));
      riskLevels.forEach(v => params.append('risk_levels', v));
      deliveryStatuses.forEach(v => params.append('delivery_statuses', v));
      if (urlLocParam) params.set('loc', urlLocParam);
      locations.forEach(v => params.append('locations', v));
      if (sku.trim()) {
        params.set('sku', sku.trim());
        if (skuUnpicked) params.set('sku_unpicked', '1');
        if (skuExclude) params.set('sku_exclude', '1');
      }
      if (splitTeamFulfillment) params.set('split_team_fulfillment', '1');
      if (includeArchived) params.set('include_archived', '1');
      const res = await storeFetch(`/api/orders/ids?${params}`);
      const data = await res.json().catch(() => ({}));
      // 2026-09-16 (Eric): never fail silently — say why nothing was selected.
      if (!res.ok) {
        const d = data?.detail;
        const msg = (d && typeof d === 'object' && d.message) || (typeof d === 'string' && d) || `Could not select all (HTTP ${res.status}).`;
        setPrintToast({ key: Date.now(), msg });
      } else if (data.error === 'too_many') {
        setPrintToast({ key: Date.now(), msg: `Cannot select more than 500 orders (${data.count} match) — narrow the filter.` });
      } else if (data.error) {
        setPrintToast({ key: Date.now(), msg: `Could not select all: ${data.error}` });
      } else if (data.ids?.length > 0) {
        setSelectedIds(new Set(data.ids));
      } else {
        setPrintToast({ key: Date.now(), msg: 'No orders match the current filter.' });
      }
    } catch (e) {
      setPrintToast({ key: Date.now(), msg: `Could not select all: ${e.message}` });
    }
    setSelectingAll(false);
  };

  // ── Print state ────────────────────────────────────────────────────────────
  const printPopupRef    = useRef(null);
  const printSourceRef   = useRef(null);
  const [printReadyOrders, setPrintReadyOrders]         = useState(null);
  const [printConfirmOrders, setPrintConfirmOrders]     = useState(null);
  // 2026-09-26 (Eric): "Merge NJ Warehouses" — one ticket per order, Tinton Falls
  // items then Eatontown items. Offered when printing from either warehouse on a
  // store that has them; the choice sticks per browser.
  const [printMergeNj, setPrintMergeNj] = useState(() => readMergeNjPref());
  const setMergeNj = (on) => { setPrintMergeNj(on); writeMergeNjPref(on); };
  // Emulation guardrail: prevents bulk print from firing when the user has
  // access to multiple locations but hasn't emulated a specific one. Without
  // this the addPrintTags path silently skips orders whose fo lacks a
  // resolvable shopify_location_id (multi-page selections carry only
  // {order:{id:gid}} shells) — the paper slip prints but the OpsHubPrinted
  // tag + timeline event never land. Root-cause of the 2026-08-13 Matt-
  // Coleman 79-of-177-missed-tag incident. When set to a number > 0, we
  // render a Modal telling the user to pick a specific location first.
  const [printNeedsLocation, setPrintNeedsLocation]     = useState(null);
  const [printAlreadyPrinted, setPrintAlreadyPrinted]   = useState([]);
  const [printOnHoldOrders, setPrintOnHoldOrders]       = useState(null);  // { all, onHold }
  // Fatal-throttle modal — surfaces the "Shopify is throttling. Please retry
  // again." error when a bulk print aborts because one or more per-order
  // fetches failed after retries. Shape: {count, total, failures:[...]}.
  const [printFailureModal, setPrintFailureModal]       = useState(null);
  // {gid → order_name} map populated by /api/orders/check-printed so the
  // already-printed modal can show real names for orders that were selected
  // from OTHER pages (their local fo.order.name is undefined — page 2's
  // orders aren't in the current `orders` slice).
  const [printedNamesByGid, setPrintedNamesByGid]       = useState({});
  // Sort-mode picker shown after "Print selected" and before triggerPrint.
  // { orders: [...] } while open; null when closed.
  const [printSortChoice, setPrintSortChoice]           = useState(null);
  const [printFetching, setPrintFetching]               = useState(false);
  const [printToast, setPrintToast]                     = useState(null);
  // Bulk pick-list state (separate from Print Selected — different popup,
  // different render path, aggregates by SKU instead of rendering per-order).
  const pickListPopupRef = useRef(null);
  const [pickListFetching, setPickListFetching] = useState(false);
  // Bulk Print Labels modal — buys UPS/USPS shipping labels for the selected
  // orders' shippable FOs at the operator's location. Same location-scope
  // gate as Print Selected.
  const [bulkLabelsOpen, setBulkLabelsOpen] = useState(false);

  useEffect(() => {
    if (!printToast) return;
    const t = setTimeout(() => setPrintToast(null), 4000);
    return () => clearTimeout(t);
  }, [printToast]);

  useEffect(() => {
    if (!bulkNotifToast) return;
    const t = setTimeout(() => setBulkNotifToast(null), 4000);
    return () => clearTimeout(t);
  }, [bulkNotifToast]);

  const printScopeLocName = emulating?.name
    || (locations?.length === 1 ? ((urlEmuLocs || []).find(r => String(r.shopify_location_id) === String(locations[0]))?.name || '') : '');
  const njPrintOffer = njMergeAllowed(activeStore) && isNjWarehouseName(printScopeLocName);
  const printLocationInScope = !!(
    emulating?.shopify_location_id
    || (locations?.length === 1)
    || (!isAdmin)
  );

  const addPrintTags = useCallback((printedOrders) => {
    const scopeSloc = emulating?.shopify_location_id
      || (locations?.length === 1 ? locations[0] : null);
    const scopeLocName = emulating?.name || null;
    printedOrders.forEach(fo => {
      const numId = fo.order?.id?.split('/').pop();
      if (!numId) return;
      // 2026-09-26: a merged NJ ticket was printed for both warehouses — tag
      // each one that had items on it (never a warehouse with nothing printed).
      const mergedLocs = Array.isArray(fo.merged_nj_locs) && fo.merged_nj_locs.length > 1 ? fo.merged_nj_locs : null;
      const sloc = mergedLocs ? mergedLocs[0].id : (scopeSloc || fo.shopify_location_id);
      if (!sloc) return;
      // Resolve the location name we expect to appear on the new tag so we
      // can optimistically insert `OpsHubPrinted <Name>` immediately.
      // Server response replaces with canonical tags ~1s later.
      const locName = mergedLocs ? mergedLocs[0].name : (scopeLocName
        || fo.location_name
        || (fo.print_locations || []).find(pl => String(pl.shopify_location_id) === String(sloc))?.name
        || '');
      const optimisticTag = locName ? `OpsHubPrinted ${locName}` : null;
      const applyTagAdd = (newTags) => {
        const updater = (fo2) =>
          fo2.order?.id === fo.order?.id
            ? { ...fo2, order: { ...fo2.order, tags: newTags(fo2.order?.tags || []) }, printed: true }
            : fo2;
        setOrders(prev => prev.map(updater));
        setSearchResults(prev => prev ? prev.map(updater) : prev);
      };
      const applyTagRollback = (tag) => {
        const updater = (fo2) =>
          fo2.order?.id === fo.order?.id
            ? { ...fo2, order: { ...fo2.order, tags: (fo2.order?.tags || []).filter(t => t !== tag) } }
            : fo2;
        setOrders(prev => prev.map(updater));
        setSearchResults(prev => prev ? prev.map(updater) : prev);
      };
      if (optimisticTag) {
        // Dedupe — if the row already carries this tag (reprint of an order
        // previously printed at this loc) blindly appending shows the chip
        // twice until the canonical server response lands ~1s later.
        applyTagAdd(prevTags => prevTags.includes(optimisticTag) ? prevTags : [...prevTags, optimisticTag]);
      }
      // Log to timeline so the order page shows who printed it. Reprint
      // distinction uses the order's tags BEFORE this print — count both
      // legacy bare tags and per-location print tags.
      const _priorTags = fo.order?.tags || [];
      const wasAlreadyPrinted = isPrinted(_priorTags) || getPerLocationPrintTags(_priorTags).length > 0;
      storeFetch(`/api/orders/${numId}/log-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: wasAlreadyPrinted ? 'order_reprint' : 'order_print',
          order_name: fo.order?.name,
          portal_username: user?.user || user?.username || null,
          payload: mergedLocs ? { merged_nj_warehouses: true, locations: mergedLocs.map(l => l.name) } : null,
        }),
      }).catch(() => {});
      const slocsToTag = mergedLocs ? mergedLocs.map(l => l.id) : [sloc];
      if (mergedLocs) {
        mergedLocs.slice(1).forEach(l => applyTagAdd(prevTags => prevTags.includes(`OpsHubPrinted ${l.name}`) ? prevTags : [...prevTags, `OpsHubPrinted ${l.name}`]));
      }
      // One request for every warehouse on the ticket (2026-10-07): the old
      // one-after-another chain could stop after the first tag.
      storeFetch(`/api/orders/${numId}/printed_at_location`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ shopify_location_id: String(slocsToTag[0]), shopify_location_ids: slocsToTag.map(String), action: 'add' }),
        }).then(r => r.json()).then(data => {
          if (data.tags) {
            applyTagAdd(() => data.tags);
          }
        }).catch(() => {
        if (optimisticTag) {
          applyTagRollback(optimisticTag);
        }
      });
    });
  }, [emulating, locations, storeFetch, user]); // eslint-disable-line

  useEffect(() => {
    if (!printReadyOrders || !printSourceRef.current || !printPopupRef.current) return;
    const popup = printPopupRef.current;
    if (popup.closed) { setPrintReadyOrders(null); return; }
    let cancelled = false;
    const captureAndWrite = () => {
      if (cancelled || popup.closed) return;
      const content = printSourceRef.current.innerHTML;
      const doc = popup.document;
      doc.open();
      doc.write(buildPopupHTML(content));
      doc.close();
      addPrintTags(printReadyOrders.map(x => x.fo));
      setPrintReadyOrders(null);
      printPopupRef.current = null;
    };
    const imgs = Array.from(printSourceRef.current.querySelectorAll('img'));
    const pending = imgs.filter(i => !i.complete || i.naturalWidth === 0);
    if (pending.length === 0) {
      requestAnimationFrame(() => requestAnimationFrame(captureAndWrite));
    } else {
      let left = pending.length;
      const tick = () => { if (--left <= 0) requestAnimationFrame(() => requestAnimationFrame(captureAndWrite)); };
      pending.forEach(img => {
        img.addEventListener('load',  tick, { once: true });
        img.addEventListener('error', tick, { once: true });
      });
      setTimeout(() => { if (!cancelled) captureAndWrite(); }, 3000);
    }
    return () => { cancelled = true; };
  }, [printReadyOrders, addPrintTags]);

  const executePrint = async (printOrders) => {
    setPrintConfirmOrders(null);
    const popup = window.open('', '', 'width=920,height=700,scrollbars=yes,resizable=yes');
    if (!popup) { alert('Please allow popups for this site to enable printing.'); return; }
    popup.document.title = 'Loading orders…';
    const body = popup.document.body;
    body.style.cssText = 'font-family:sans-serif;padding:40px;color:#374151';
    const progressEl = popup.document.createElement('p');
    progressEl.textContent = `Loading order details… (0 / ${printOrders.length})`;
    progressEl.style.fontSize = '15px';
    body.appendChild(progressEl);
    printPopupRef.current = popup;
    setPrintFetching(true);

    // Track any order that couldn't be loaded (any HTTP non-200 or missing
    // fullOrder body). 2026-08-17 rule: if ANY order fails to load, the
    // entire print MUST hard-abort — nothing prints, no tags are applied,
    // no event-log rows are written. Prior behavior silently pushed a
    // {fullOrder:null} entry into printReadyOrders, which rendered as a
    // blank page AND flowed through addPrintTags → marking blank pages as
    // successfully printed. See Matt's incident.
    const printFailures = [];

    try {
      const BATCH = 8;
      const enriched = [];
      // Allowed-location set for deriving shopify_location_id when fo doesn't
      // have one. Happens when an order was selected from a different page —
      // handlePrintSelected falls back to {order:{id:gid}} for IDs not in the
      // current `orders` slice or in `searchResults`. Without this, the print-
      // mark step in addPrintTags would bail (no sloc) and the tag wouldn't be
      // applied for those orders — exactly the bug Gina hit when she printed
      // 67 orders and only the first ~50 (the visible page) got tagged.
      const userLocSet = new Set((locations || []).map(String));
      // Scope the bin lookup to the location the user is viewing/printing
      // from. Without this, the BE auto-detects from the first FO it sees,
      // which on multi-location orders (e.g. Lancaster + Tinton Falls
      // Warehouse) can pick the wrong location's metafield → empty bins.
      const printScopeSloc = emulating?.shopify_location_id
        || (locations?.length === 1 ? String(locations[0]) : null);
      for (let i = 0; i < printOrders.length; i += BATCH) {
        if (popup.closed) break;
        const batch = printOrders.slice(i, i + BATCH);
        const results = await Promise.all(batch.map(async fo => {
          const numId = fo.order?.id?.split('/').pop();
          if (!numId) {
            printFailures.push({ id: fo.order?.id, name: fo.order?.name, status: 'no-id' });
            return { fo, fullOrder: null, bins: {}, overstockBins: {}, numberPreviews: {}, skuNotifications: {} };
          }
          try {
            const binSloc = printScopeSloc || fo.shopify_location_id || '';
            const binsUrl = binSloc
              ? `/api/orders/${numId}/bin-locations?shopify_location_id=${encodeURIComponent(binSloc)}`
              : `/api/orders/${numId}/bin-locations`;
            // 2026-08-17: client-side retry on top of the backend's server-
            // side retry — belt & suspenders for the throttle case. Any
            // still-failed order after 3 total attempts (this loop) surfaces
            // as a printFailures entry and hard-aborts the print below.
            // bins/previews/skuNotif failures are tolerated (they render as
            // empty), only the order-detail fetch is treated as fatal.
            let orderRes = null;
            for (let attempt = 0; attempt < 3; attempt++) {
              orderRes = await storeFetch(`/api/orders/gid://shopify/Order/${numId}`);
              if (orderRes.ok) break;
              // Backoff before retry: 500ms, 1.5s. Backend also retries THROTTLED
              // internally with 2s + 5s, so worst-case per order is ~8-10s.
              if (attempt < 2) await new Promise(r => setTimeout(r, 500 + attempt * 1000));
            }
            const [binsRes, previewsRes, skuNotifRes] = await Promise.all([
              storeFetch(binsUrl).catch(() => null),
              storeFetch(`/api/orders/${numId}/number-previews`).catch(() => null),
              storeFetch(`/api/sku-notifications/for-order/${numId}`).catch(() => null),
            ]);
            const fullOrder = (orderRes && orderRes.ok) ? await orderRes.json() : null;
            if (!fullOrder) {
              printFailures.push({
                id: fo.order?.id,
                name: fo.order?.name,
                status: orderRes ? orderRes.status : 'network',
              });
            }
            let bins = {};
            let overstockBins = {};
            if (binsRes?.ok) {
              try {
                const binsJson = await binsRes.json();
                bins = binsJson.bins || {};
                overstockBins = binsJson.overstock || {};
              } catch {}
            }
            let numberPreviews = {};
            if (previewsRes?.ok) { try { numberPreviews = (await previewsRes.json()).previews || {}; } catch {} }
            let skuNotifications = {};
            if (skuNotifRes?.ok) { try { skuNotifications = (await skuNotifRes.json()).sku_notifications || {}; } catch {} }
            // Backfill shopify_location_id from fullOrder.fulfillmentOrders
            // when the fo lacks it. Prefer an FO assigned to one of the
            // current user's locations; otherwise fall back to the first FO
            // we see so addPrintTags has something workable.
            // 2026-09-26 (Eric): merged NJ ticket — only when the order has open
            // items at BOTH warehouses; otherwise it prints as a normal single-
            // location ticket and only that warehouse is tagged.
            let merged = null;
            if (printMergeNj && njPrintOffer && fullOrder) {
              const njLocs = njWarehouseLocsOnOrder(fullOrder);
              if (njLocs.length === 2) {
                const withBins = await Promise.all(njLocs.map(async l => {
                  try {
                    const r = await storeFetch(`/api/orders/${numId}/bin-locations?shopify_location_id=${encodeURIComponent(l.id)}`);
                    const j = r && r.ok ? await r.json() : {};
                    return { ...l, bins: j.bins || {}, overstockBins: j.overstock || {} };
                  } catch { return { ...l, bins: {}, overstockBins: {} }; }
                }));
                merged = { locs: withBins };
              }
            }
            let foOut = merged ? { ...fo, merged_nj_locs: merged.locs.map(l => ({ id: l.id, name: l.name })) } : fo;
            if (!fo.shopify_location_id && fullOrder) {
              const foEdges = fullOrder.fulfillmentOrders?.edges || [];
              let pickedSloc = null;
              let firstSloc = null;
              for (const e of foEdges) {
                const locId = (e.node?.assignedLocation?.location?.id || '').split('/').pop();
                if (!locId) continue;
                if (!firstSloc) firstSloc = locId;
                if (userLocSet.size === 0 || userLocSet.has(locId)) {
                  pickedSloc = locId;
                  break;
                }
              }
              const sloc = pickedSloc || firstSloc;
              // 2026-10-07 (Ingrid, B2B-93748): build on foOut, not fo — this used to
              // throw away merged_nj_locs, so only the scope warehouse got tagged.
              if (sloc) foOut = { ...foOut, shopify_location_id: sloc };
            }
            return { fo: foOut, fullOrder, bins, overstockBins, numberPreviews, skuNotifications, merged };
          } catch (e) {
            printFailures.push({
              id: fo.order?.id,
              name: fo.order?.name,
              status: 'exception',
            });
            return { fo, fullOrder: null, bins: {}, overstockBins: {}, numberPreviews: {}, skuNotifications: {} };
          }
        }));
        enriched.push(...results);
        if (!popup.closed) progressEl.textContent = `Loading order details… (${enriched.length} / ${printOrders.length})`;
      }
      if (popup.closed) { setPrintFetching(false); printPopupRef.current = null; return; }
      setPrintFetching(false);
      // Hard-abort if any order failed to load. Do NOT set printReadyOrders
      // (that's what triggers window.print() + addPrintTags in the effect
      // at line ~1534). No tags applied; no event-log rows; nothing prints.
      // Operator gets a modal + the popup shows a matching message so a
      // "printer already opened" tab isn't left dangling silent.
      if (printFailures.length > 0) {
        if (!popup.closed) {
          try {
            popup.document.title = 'Print aborted';
            const b = popup.document.body;
            b.style.cssText = 'font-family:sans-serif;padding:40px;color:#7b0f0f;margin:0';
            // Rebuild via safe DOM methods (no innerHTML) — content is
            // static but keep textContent-only assignment as habit.
            while (b.firstChild) b.removeChild(b.firstChild);
            const h = popup.document.createElement('h2');
            h.style.marginTop = '0';
            h.textContent = 'Shopify is throttling. Please retry again.';
            const p1 = popup.document.createElement('p');
            p1.style.cssText = 'font-size:15px;line-height:1.5;color:#374151';
            p1.textContent = 'No orders were printed. No orders were marked as printed.';
            const p2 = popup.document.createElement('p');
            p2.style.cssText = 'font-size:13px;color:#6d7175;margin-top:24px';
            p2.textContent = `${printFailures.length} of ${printOrders.length} orders failed to load.`;
            b.appendChild(h); b.appendChild(p1); b.appendChild(p2);
          } catch { /* popup may have been closed */ }
        }
        printPopupRef.current = null;
        setPrintFailureModal({
          count: printFailures.length,
          total: printOrders.length,
          failures: printFailures.slice(0, 25),
        });
        return;
      }
      setPrintReadyOrders(enriched);
    } catch {
      setPrintFetching(false);
      if (!popup.closed) popup.close();
      printPopupRef.current = null;
    }
  };

  const triggerPrint = async (printOrders) => {
    // Server-side truth for on-hold + printed state — was: local
    // fo.order.tags. That local read was correct only for orders whose
    // row lived in the current visible page; selections spanning saved
    // views / paged results have fallback `{order:{id:gid}}` shells with
    // no tags → the "already printed" check silently missed them (Matt's
    // report 2026-08-17: selected 150 printed orders, modal only counted
    // the ~50 currently on-screen). Ask the backend for the ground truth
    // via /api/orders/check-printed, then apply on-hold + already-printed
    // gates against that response.
    const ids = printOrders.map(fo => fo.order?.id).filter(Boolean);
    let printedIdSet = new Set();
    let onHoldIdSet  = new Set();
    try {
      const r = await storeFetch('/api/orders/check-printed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (r.ok) {
        const d = await r.json();
        // 2026-09-02: scope to the location we are printing FROM (Karen @ Lancaster was
        // warned about Tinton-only prints). All Locations keeps the any-loc behaviour.
        printedIdSet = printedIdsForLocation(d, emulating?.shopify_location_id);
        onHoldIdSet  = new Set(d.on_hold_ids || []);
        // Capture backend-side names so the modal can render real order
        // names even for selections whose local `fo.order.name` isn't
        // hydrated (e.g. rows from other pages).
        if (d.names && typeof d.names === 'object') {
          setPrintedNamesByGid(d.names);
        }
      } else {
        // Backend hiccup — fall back to the old local-tags heuristic so a
        // check-printed 500 doesn't stall the print entirely. This path
        // will under-count for cross-page selections but the operator is
        // strictly better off than getting no print at all.
        printOrders.forEach(fo => {
          const tags = fo.order?.tags;
          if (emulating?.name ? tagsSayPrintedHere(tags, emulating.name) : (isPrinted(tags) || getPerLocationPrintTags(tags).length > 0)) {
            printedIdSet.add(fo.order?.id);
          }
          const s = (fo.order?.fulfillment_status || fo.fulfillment_status || fo.order?.displayFulfillmentStatus || '').toUpperCase();
          if (s === 'ON_HOLD') onHoldIdSet.add(fo.order?.id);
        });
      }
    } catch {
      // Same graceful fallback if the fetch itself throws.
      printOrders.forEach(fo => {
        const tags = fo.order?.tags;
        if (emulating?.name ? tagsSayPrintedHere(tags, emulating.name) : (isPrinted(tags) || getPerLocationPrintTags(tags).length > 0)) {
          printedIdSet.add(fo.order?.id);
        }
        const s = (fo.order?.fulfillment_status || fo.fulfillment_status || fo.order?.displayFulfillmentStatus || '').toUpperCase();
        if (s === 'ON_HOLD') onHoldIdSet.add(fo.order?.id);
      });
    }

    // On-hold gate first — hard policy block.
    const onHold = printOrders.filter(fo => onHoldIdSet.has(fo.order?.id));
    if (onHold.length > 0) {
      setPrintOnHoldOrders({ all: printOrders, onHold });
      return;
    }
    // Match V1 + V2 individual print: an order is "already printed" if the
    // backend flagged its GID (any cache row for that order has a printed
    // flag or the equivalent tag; see /api/orders/check-printed for the
    // OpsHubPrinted-per-loc + legacy-tag logic).
    const knownPrinted = printOrders.filter(fo => printedIdSet.has(fo.order?.id));
    if (knownPrinted.length > 0) {
      setPrintConfirmOrders(printOrders);
      setPrintAlreadyPrinted(knownPrinted);
    } else {
      executePrint(printOrders);
    }
  };

  const handlePrintSelected = () => {
    const map = new Map();
    (orders || []).forEach(fo => { if (fo.order?.id) map.set(fo.order.id, fo); });
    (searchResults || []).forEach(fo => { if (fo.order?.id) map.set(fo.order.id, fo); });
    const selected = [...selectedIds]
      .map(gid => map.get(gid) || { order: { id: gid } })
      .filter(fo => fo.order?.id);
    if (selected.length === 0) return;
    // Location-emulation guardrail. If the user has access to multiple
    // locations and hasn't emulated a specific one (scopeSloc = null), the
    // addPrintTags path silently skips any order whose fo lacks a resolvable
    // shopify_location_id — that's the 2026-08-13 Matt-Coleman incident
    // where 79 of 177 orders printed on paper without the OpsHubPrinted
    // tag or the timeline event. Block here so the tag path is guaranteed
    // to work. Single-loc users and users already emulating go straight
    // through.
    const _scopeSloc = emulating?.shopify_location_id
      || (locations?.length === 1 ? String(locations[0]) : null);
    if (!_scopeSloc && (locations?.length || 0) > 1) {
      setPrintNeedsLocation(selected.length);
      return;
    }
    // Ask sort mode BEFORE the on-hold / already-printed gates so the
    // choice sticks even if the user goes through those confirmations.
    setPrintSortChoice({ orders: selected });
  };

  // Sort a list of {order:{name,club}} entries in place-safe fashion.
  // 'order' = alphabetical by order name (SP-####, SUR-####, …).
  // 'club'  = alphabetical by club then order name; orders with no club
  //           fall to the end.
  const _sortPrintOrders = (list, mode) => {
    const arr = list.slice();
    const nameOf = fo => (fo.order?.name || '');
    const clubOf = fo => (fo.order?.club || '').trim();
    if (mode === 'club') {
      arr.sort((a, b) => {
        const ca = clubOf(a), cb = clubOf(b);
        // Empty-club rows sink to the bottom so the collated blocks stay clean.
        if (!ca && cb) return 1;
        if (ca && !cb) return -1;
        const cmp = ca.localeCompare(cb);
        if (cmp !== 0) return cmp;
        return nameOf(a).localeCompare(nameOf(b));
      });
    } else {
      arr.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
    }
    return arr;
  };

  // ── Bulk notification — broadcast one message to the selected orders ─────
  // Strips Shopify GIDs to numeric IDs (backend looks up store/loc/order_name
  // per id, rejects cross-store selections). Submits via POST /bulk; on
  // success closes the modal, clears selection, and toasts the result.
  const handleBulkNotificationSubmit = async () => {
    const msg = (bulkNotifMessage || '').trim();
    if (!msg || msg.length > 300) {
      setBulkNotifError('Message must be between 1 and 300 characters.');
      return;
    }
    const numericIds = [...selectedIds]
      .map(gid => (gid || '').split('/').pop())
      .filter(Boolean);
    if (numericIds.length === 0) {
      setBulkNotifError('No orders selected.');
      return;
    }
    setBulkNotifSaving(true);
    setBulkNotifError(null);
    try {
      const r = await storeFetch('/api/settings/notifications/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, order_ids: numericIds }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      // Optimistically bump notifications_count on every affected row in the
      // local orders + searchResults state so the chip appears immediately,
      // without waiting for a full refetch. Backend already persisted and
      // recomputed cache; this just keeps the UI in sync until the next page
      // change.
      const affectedSelGids = new Set([...selectedIds]);
      const bumpCount = (fo) => {
        if (!fo?.order?.id || !affectedSelGids.has(fo.order.id)) return fo;
        return { ...fo, notifications_count: (fo.notifications_count || 0) + 1 };
      };
      setOrders(prev => prev.map(bumpCount));
      setSearchResults(prev => prev ? prev.map(bumpCount) : prev);

      setBulkNotifOpen(false);
      setBulkNotifMessage('');
      setSelectedIds(new Set());
      setBulkNotifToast({
        msg: `Notification sent to ${d.count || numericIds.length} orders.`,
        key: Date.now(),
      });
    } catch (e) {
      setBulkNotifError(String(e.message || e));
    } finally {
      setBulkNotifSaving(false);
    }
  };

  // ── Bulk Pick List ─────────────────────────────────────────────────────
  // Aggregates every line item across the selected orders by SKU, summing
  // quantities. Each SKU row shows: thumbnail, SKU code, product title +
  // variant, bin location (when picking-location metafields are configured
  // for the user's location), and total quantity. Sorted alphabetically by
  // SKU. Each line item is counted individually — bundle parent/child
  // relationships are ignored on purpose.
  const handlePickListSelected = async () => {
    const map = new Map();
    (orders || []).forEach(fo => { if (fo.order?.id) map.set(fo.order.id, fo); });
    (searchResults || []).forEach(fo => { if (fo.order?.id) map.set(fo.order.id, fo); });
    const selected = [...selectedIds]
      .map(gid => map.get(gid) || { order: { id: gid } })
      .filter(fo => fo.order?.id);
    if (selected.length === 0) return;

    const popup = window.open('', '', 'width=920,height=700,scrollbars=yes,resizable=yes');
    if (!popup) { alert('Please allow popups for this site to enable printing.'); return; }
    popup.document.title = 'Building pick list…';
    const popupBody = popup.document.body;
    popupBody.style.cssText = 'font-family:sans-serif;padding:40px;color:#374151;margin:0';
    const progressEl = popup.document.createElement('p');
    progressEl.textContent = `Loading orders… (0 / ${selected.length})`;
    progressEl.style.fontSize = '15px';
    popupBody.appendChild(progressEl);
    pickListPopupRef.current = popup;
    setPickListFetching(true);

    try {
      const BATCH = 8;
      const fetched = [];
      // Scope bin lookup to the viewing/print location — same reasoning as
      // handlePrintSelected: multi-location orders otherwise resolve to
      // whichever FO comes back first from Shopify.
      const pickScopeSloc = emulating?.shopify_location_id
        || (locations?.length === 1 ? String(locations[0]) : null);
      for (let i = 0; i < selected.length; i += BATCH) {
        if (popup.closed) break;
        const batch = selected.slice(i, i + BATCH);
        const results = await Promise.all(batch.map(async fo => {
          const numId = fo.order?.id?.split('/').pop();
          if (!numId) return null;
          try {
            const binSloc = pickScopeSloc || fo.shopify_location_id || '';
            const binsUrl = binSloc
              ? `/api/orders/${numId}/bin-locations?shopify_location_id=${encodeURIComponent(binSloc)}`
              : `/api/orders/${numId}/bin-locations`;
            const [orderRes, binsRes, notifRes] = await Promise.all([
              storeFetch(`/api/orders/gid://shopify/Order/${numId}`),
              storeFetch(binsUrl).catch(() => null),
              storeFetch(`/api/sku-notifications/for-order/${numId}`).catch(() => null),
            ]);
            const fullOrder = orderRes.ok ? await orderRes.json() : null;
            let bins = {};
            if (binsRes?.ok) { try { bins = (await binsRes.json()).bins || {}; } catch {} }
            let skuNotifs = {};
            if (notifRes?.ok) { try { skuNotifs = (await notifRes.json()).sku_notifications || {}; } catch {} }
            return { fullOrder, bins, skuNotifs };
          } catch { return null; }
        }));
        fetched.push(...results.filter(Boolean));
        if (!popup.closed) progressEl.textContent = `Loading orders… (${fetched.length} / ${selected.length})`;
      }
      if (popup.closed) { setPickListFetching(false); pickListPopupRef.current = null; return; }

      // Mirrors backend VIRTUAL_LINE_ITEM_TITLES — these are non-physical line
      // items (insurance, package protection, processing fee) that should never
      // appear on a pick list.
      const VIRTUAL_LINE_ITEM_TITLES = new Set([
        'free returns + package protection',
        'package protection',
        'free returns',
        'processing fee',
      ]);
      // Team Portal personalization can show up in two places:
      //  1. customAttributes on the line item (Player Number / Player Name / Number / Name / #)
      //  2. SKU suffix (`BASE#21`, `BASE!Alice`) — some stores do this instead
      // We extract both, plus inherit attrs from a parent line when a NUMBER
      // child has none of its own (parent has `_Required SKU <child_sku>` keys).
      const NUMBER_PRODUCT_TYPES = new Set(['Number', 'Embroidered Number']);
      const NAME_PRODUCT_TYPES   = new Set(['Name', 'Embroidered Name']);
      const REQUIRED_SKU_PREFIX = '_Required SKU ';
      const attrLookup = (attrs, ...keys) => {
        for (const k of keys) {
          const found = (attrs || []).find(a => a.key === k);
          if (found && found.value && String(found.value).trim()) {
            return String(found.value).trim();
          }
        }
        return '';
      };
      const parseSkuSuffix = (raw) => {
        const s = String(raw || '');
        if (!s) return { base: '', player: '', number: '' };
        const playerIdx = s.indexOf('!');
        const numberIdx = s.indexOf('#');
        let base = s, player = '', number = '';
        if (playerIdx >= 0 && (numberIdx < 0 || playerIdx < numberIdx)) {
          base = s.slice(0, playerIdx);
          const rest = s.slice(playerIdx + 1);
          const hashAfter = rest.indexOf('#');
          if (hashAfter >= 0) {
            player = rest.slice(0, hashAfter);
            number = rest.slice(hashAfter + 1);
          } else {
            player = rest;
          }
        } else if (numberIdx >= 0) {
          base = s.slice(0, numberIdx);
          const rest = s.slice(numberIdx + 1);
          const bangAfter = rest.indexOf('!');
          if (bangAfter >= 0) {
            number = rest.slice(0, bangAfter);
            player = rest.slice(bangAfter + 1);
          } else {
            number = rest;
          }
        }
        const pipeIdx = base.indexOf('|');
        if (pipeIdx >= 0) base = base.slice(0, pipeIdx);
        return { base, player, number };
      };

      // Aggregate by BASE sku; keep parallel lists of numbers + players so the
      // print row can show "21, 5, 8, …" / "Alice, Bob, …" instead of one row
      // per personalized copy.
      const skuMap = new Map();
      const orderNames = [];
      // Merge every order's SKU-notification map into one lookup keyed by
      // the pick-list's row SKU (which is the base — same key we use to
      // slot line items into skuMap). Any active notification on ANY of the
      // selected orders for a given base SKU surfaces here.
      const skuToNotif = new Map();
      for (const { skuNotifs } of fetched) {
        for (const [k, v] of Object.entries(skuNotifs || {})) {
          if (k && v && !skuToNotif.has(k)) skuToNotif.set(k, v);
        }
      }
      for (const { fullOrder, bins } of fetched) {
        if (!fullOrder) continue;
        const orderName = fullOrder.order?.name || fullOrder.name || '';
        if (orderName) orderNames.push(orderName);
        const lineItems = (fullOrder.lineItems?.edges || []).map(e => e.node);

        // Build parent-inheritance map for NUMBER lines: parents have
        // `_Required SKU <child_sku>` KEYS whose attrs (Number / Player Name)
        // should be inherited by their NUMBER children if the child has none.
        const childInheritance = new Map(); // child li.id → [{attrs, qty}, ...]
        for (const parent of lineItems) {
          const pAttrs = parent.customAttributes || [];
          const required = pAttrs.filter(a => a.key && a.key.startsWith(REQUIRED_SKU_PREFIX));
          if (!required.length) continue;
          const pAttrMap = pAttrs;
          for (const req of required) {
            const childSku = req.key.slice(REQUIRED_SKU_PREFIX.length).trim();
            if (!childSku) continue;
            for (const child of lineItems) {
              if ((child.sku || '').trim() !== childSku) continue;
              const childPt = ((child.variant || {}).product || {}).productType || '';
              if (!NUMBER_PRODUCT_TYPES.has(childPt)) continue;
              const cAttrs = child.customAttributes || [];
              const hasOwn = ['Number', '#', 'Name', 'Player Name', 'Player Number']
                .some(k => attrLookup(cAttrs, k));
              if (hasOwn) continue;
              if (!childInheritance.has(child.id)) childInheritance.set(child.id, []);
              childInheritance.get(child.id).push({ attrs: pAttrMap, qty: parent.quantity || 1 });
            }
          }
        }

        for (const li of lineItems) {
          if (VIRTUAL_LINE_ITEM_TITLES.has((li.title || '').trim().toLowerCase())) continue;
          const rawSku = (li.sku || '').trim();
          if (!rawSku) continue;
          const { base: skuBase, player: skuPlayer, number: skuNumber } = parseSkuSuffix(rawSku);
          const keySku = skuBase || rawSku;
          const qty = li.quantity || 0;
          if (qty <= 0) continue;
          const bin = bins[li.id] || '';
          if (!skuMap.has(keySku)) {
            skuMap.set(keySku, {
              sku: keySku,
              title: li.title || li.product?.title || '',
              variant: li.variant?.title || li.variantTitle || '',
              image: li.image?.url || li.variant?.image?.url || li.product?.featuredImage?.url || '',
              productType: (li.productType || li.product?.productType || li.variant?.product?.productType || '').trim(),
              bins: new Set(),
              numbers: [],
              players: [],
              qty: 0,
            });
          }
          const entry = skuMap.get(keySku);
          entry.qty += qty;
          if (bin) entry.bins.add(bin);

          // Collect personalization for each unit of this line. Priority:
          // own customAttributes → inherited from parent (NUMBER children
          // only) → SKU suffix. Each unit of qty contributes one entry.
          //
          // For Name product types (Stahls Initials, etc.), the line item's
          // own `Name` attribute carries the letters to cut — NOT the jersey's
          // `Player Name` (full name). Prefer `Name` on those LIs so the pick
          // list shows "ZEA" / "OB" / "BAIRD" instead of full player names.
          const ownAttrs = li.customAttributes || [];
          const liProductType = (li.productType || li.product?.productType || li.variant?.product?.productType || '').trim();
          const ownNumber = attrLookup(ownAttrs, 'Player Number', 'player_number', 'Number', '#');
          const ownPlayer = NAME_PRODUCT_TYPES.has(liProductType)
            ? attrLookup(ownAttrs, 'Name', 'name')
            : attrLookup(ownAttrs, 'Player Name', 'player_name');
          const inh = childInheritance.get(li.id) || [];

          // Expand the qty into individual unit-personalizations. For inherited
          // lines, each parent (with its share_qty) contributes that many units.
          const units = [];
          if (ownNumber || ownPlayer) {
            for (let i = 0; i < qty; i++) {
              units.push({ number: ownNumber, player: ownPlayer });
            }
          } else if (inh.length) {
            for (const { attrs, qty: shareQty } of inh) {
              const n = attrLookup(attrs, 'Player Number', 'player_number', 'Number', '#');
              const p = attrLookup(attrs, 'Player Name', 'player_name');
              for (let i = 0; i < shareQty; i++) {
                units.push({ number: n, player: p });
              }
            }
            // Pad with empty if inherited count < qty (defensive).
            while (units.length < qty) units.push({ number: '', player: '' });
          } else if (skuNumber || skuPlayer) {
            for (let i = 0; i < qty; i++) {
              units.push({ number: skuNumber, player: skuPlayer });
            }
          }

          for (const u of units) {
            if (u.number) entry.numbers.push(u.number);
            if (u.player) entry.players.push(u.player);
          }
        }
      }

      // Aggregate clubs across selected orders (de-duped, in order seen).
      const clubSet = new Set();
      const clubs = [];
      for (const { fullOrder } of fetched) {
        const c = (fullOrder?.order?.club || '').trim();
        if (c && !clubSet.has(c)) { clubSet.add(c); clubs.push(c); }
      }

      // Resolve the picker's effective location for the top header. emulating
      // wins, else single-location user, else unscoped. Use the full location
      // name verbatim — "Teams Lancaster" is the actual warehouse name and
      // dropping the "Teams " prefix lost meaningful info (the warehouse vs.
      // the retail-store-at-the-same-city distinction).
      let locLabel = '';
      if (emulating?.name) {
        locLabel = emulating.name;
      } else if (locations && locations.length === 1) {
        const match = (availableLocations || []).find(l => String(l.shopify_location_id) === String(locations[0]));
        if (match?.name) locLabel = match.name;
      }
      // "Sun May 24 · 4:12pm ET" — always render the printed time in ET; ops
      // is on the east coast and the slip is for warehouse use.
      const fmtNowET = () => {
        const d = new Date();
        const datePart = d.toLocaleDateString('en-US', {
          weekday: 'short', month: 'short', day: 'numeric',
          timeZone: 'America/New_York',
        }).replace(',', '');
        const timePart = d.toLocaleTimeString('en-US', {
          hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
        }).toLowerCase().replace(/\s+/g, '');
        return `${datePart} · ${timePart} ET`;
      };
      const topHeaderText = locLabel ? `${fmtNowET()}, ${locLabel}` : fmtNowET();

      // Two aisle schemes:
      //   Warehouse locations ("Tinton Falls Warehouse (Virtual)" etc.):
      //     bins are letter-coded ("AD-05"). Aisle = the alphabetic prefix
      //     ("AD"). Walk in bijective base-26 order (A,B,...,Z,AA,AB,...,
      //     AAA,...) so the picker traces the physical aisle path.
      //   Teams locations ("Teams Lancaster" etc.):
      //     bins are numeric-segment codes ("2-19-7-2-14"). Aisle = first
      //     two segments ("2-19"), sorted by the segments numerically.
      // Within each aisle, rows are clustered under product-type subheaders.
      // Empty productType sorts last under the "Uncategorized" label.
      const warehouseMode = isWarehouseLocation(locLabel);
      const TYPE_BLANK_LABEL = 'Uncategorized';
      const typeKey = (r) => r.productType || TYPE_BLANK_LABEL;
      const rowBin = (r) => [...r.bins][0] || '';
      const typeAndTitleCmp = (a, b) => {
        const ta = typeKey(a), tb = typeKey(b);
        if (ta === TYPE_BLANK_LABEL && tb !== TYPE_BLANK_LABEL) return 1;
        if (tb === TYPE_BLANK_LABEL && ta !== TYPE_BLANK_LABEL) return -1;
        return ta.localeCompare(tb) || (a.title || a.sku).localeCompare(b.title || b.sku);
      };
      const rowSorter = warehouseMode
        // Warehouse: physical aisle walk first, type/title only on tie.
        ? (a, b) => compareBins(rowBin(a), rowBin(b)) || typeAndTitleCmp(a, b)
        // Teams (legacy): cluster by product type within the aisle, alpha title.
        : typeAndTitleCmp;
      const aisleMap = new Map();
      const unbinned = [];
      for (const r of skuMap.values()) {
        const firstBin = rowBin(r);
        if (!firstBin) { unbinned.push(r); continue; }
        const aisle = warehouseMode
          ? binAisle(firstBin)
          : firstBin.split('-').slice(0, 2).join('-');
        if (!aisle) { unbinned.push(r); continue; }
        if (!aisleMap.has(aisle)) aisleMap.set(aisle, []);
        aisleMap.get(aisle).push(r);
      }
      for (const items of aisleMap.values()) items.sort(rowSorter);
      unbinned.sort(rowSorter);
      const sortedRows = [...skuMap.values()];  // retained for totalSkus/totalQty
      const aisleEntries = [...aisleMap.entries()].sort((a, b) => {
        if (warehouseMode) {
          // Bijective base-26 by comparing a synthetic "<aisle>-0" bin.
          return compareBins(`${a[0]}-0`, `${b[0]}-0`);
        }
        // Legacy: parse the two numeric segments.
        const [aw, an] = a[0].split('-').map(Number);
        const [bw, bn] = b[0].split('-').map(Number);
        return (aw || 0) - (bw || 0) || (an || 0) - (bn || 0);
      });

      const totalSkus = sortedRows.length;
      const totalQty = sortedRows.reduce((s, r) => s + r.qty, 0);

      // ── Build the document via DOM API. Every variable value goes through
      //    textContent / setAttribute — no HTML-injection surface.
      const doc = popup.document;
      doc.title = `Pick List — ${totalSkus} items / ${totalQty} units`;

      const style = doc.createElement('style');
      style.textContent = `
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, sans-serif;
               color: #111418; padding: 24px 28px; margin: 0; background: #fff;
               font-variant-numeric: tabular-nums; }
        * { box-sizing: border-box; }
        /* Top bar */
        .pls-toolbar { display: flex; align-items: center; justify-content: space-between;
                        gap: 16px; padding-bottom: 12px; margin-bottom: 14px;
                        border-bottom: 1px solid #e5e7eb; }
        .pls-toolbar-info { font-size: 12px; color: #6d7175; letter-spacing: 0;
                             font-family: ui-monospace, Menlo, monospace; }
        .pls-print-btn { background: #111418; color: #fff; font-size: 13px; font-weight: 600;
                          padding: 7px 14px; border-radius: 6px; border: 0; cursor: pointer;
                          display: inline-flex; align-items: center; gap: 6px; }
        .pls-print-btn svg { width: 14px; height: 14px; }
        /* Title block */
        .pls-header { margin-bottom: 6px; }
        .pls-title { margin: 0 0 4px 0; font-size: 32px; font-weight: 700; color: #111418;
                      letter-spacing: -0.01em; line-height: 1.05; }
        .pls-meta { display: flex; flex-wrap: wrap; align-items: baseline; font-size: 14px;
                     color: #6d7175; margin: 0; line-height: 1.4; }
        .pls-meta b { color: #111418; font-weight: 700; margin-right: 4px; }
        .pls-meta-sep { margin: 0 10px; color: #d0d5dd; }
        .pls-club { color: #111418; font-weight: 500; }
        .pls-orderlist { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px;
                          align-items: center; }
        .pls-orderlist-label { font-size: 13px; color: #6d7175; margin-right: 4px; }
        .pls-orderlist-label b { color: #111418; font-weight: 700; }
        .pls-orderpill { display: inline-flex; font-family: ui-monospace, Menlo, monospace;
                          font-size: 12px; color: #111418; background: #fff;
                          border: 1px solid #d0d5dd; border-radius: 3px; padding: 3px 10px;
                          white-space: nowrap; font-weight: 600; }
        /* Aisle header */
        .pls-aisle { display: flex; align-items: center; gap: 10px; padding: 0 0 4px 0;
                      margin: 14px 0 0 0; border-bottom: 1px solid #111418; }
        .pls-aisle:first-of-type { margin-top: 6px; }
        .pls-aisle-tag { font-family: ui-monospace, Menlo, monospace; font-size: 11px;
                          font-weight: 700; color: #fff; background: #111418;
                          padding: 3px 8px 2px 8px; border-radius: 3px 3px 0 0;
                          margin-bottom: -1px; letter-spacing: 0.04em;
                          text-transform: uppercase; }
        .pls-aisle-tag--warn { background: #b86e00; }
        .pls-aisle-meta { font-size: 11px; color: #6d7175; letter-spacing: 0.02em;
                           text-transform: uppercase; font-weight: 600; }
        /* Product-type subheader (within an aisle / Not-in-bin section) */
        .pls-type { display: flex; align-items: baseline; gap: 8px;
                    margin: 10px 0 0 0; padding: 4px 0 2px 0;
                    border-bottom: 1px solid #d0d5dd; }
        .pls-type-name { font-size: 13px; font-weight: 700; color: #111418;
                         letter-spacing: 0; }
        .pls-type-meta { font-size: 10px; color: #9ca3af; letter-spacing: 0.02em;
                         text-transform: uppercase; font-weight: 600; }
        /* Row */
        .pls-row { display: grid; grid-template-columns: 22px 56px 1fr 110px 72px;
                    align-items: center; gap: 16px; padding: 8px 14px;
                    border-bottom: 1px solid #e5e7eb; min-height: 64px; cursor: pointer; }
        .pls-row:last-child { border-bottom: 0; }
        .pls-row--unbinned { background: #fffaf0; }
        .pls-check { width: 18px; height: 18px; border: 1.5px solid #9ca3af;
                      border-radius: 3px; background: #fff; flex: 0 0 18px; }
        .pls-row--done .pls-check { background: #111418; border-color: #111418;
                                     position: relative; }
        .pls-row--done .pls-check::after { content: ''; position: absolute; left: 4px;
                      top: 1px; width: 5px; height: 10px;
                      border-right: 2px solid #fff; border-bottom: 2px solid #fff;
                      transform: rotate(45deg); }
        .pls-row--done .pls-prod, .pls-row--done .pls-bin,
        .pls-row--done .pls-qty, .pls-row--done .pl-thumb { opacity: 0.45; }
        .pl-thumb { width: 56px; height: 56px; border: 1px solid #e5e7eb;
                     border-radius: 4px; background: #fafafa; overflow: hidden;
                     display: flex; align-items: center; justify-content: center; }
        /* Darker fill behind white-on-white product photos so the product is
           visible. Pixel-probe in buildRow toggles this class. */
        .pl-thumb--lightbg { background: #9ca3af; border-color: #9ca3af; }
        .pl-thumb img { width: 100%; height: 100%; object-fit: contain; display: block; }
        .pl-thumb-empty { font-size: 10px; color: #c4c8cc; text-align: center; line-height: 1.1; }
        .pls-prod { min-width: 0; }
        .pls-prod-name { font-size: 14px; font-weight: 500; color: #111418; line-height: 1.3;
                          display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
        .pls-variant { display: inline-block; font-family: ui-monospace, Menlo, monospace;
                        font-size: 18px; font-weight: 700; color: #1a2744;
                        background: #dce8f7; border-radius: 4px; padding: 3px 10px;
                        letter-spacing: 0.02em; white-space: nowrap; }
        .pls-prod-sku { font-family: ui-monospace, Menlo, monospace; font-size: 11px;
                         color: #9ca3af; margin-top: 3px; }
        .pls-personalization { font-size: 13px; color: #111418; margin-top: 4px;
                                line-height: 1.4; }
        .pls-personalization-label { font-size: 11px; color: #6d7175; font-weight: 600;
                                      letter-spacing: 0.02em; text-transform: uppercase;
                                      margin-right: 4px; }
        .pls-bin { font-family: ui-monospace, Menlo, monospace; font-size: 14px;
                    font-weight: 700; color: #111418; white-space: nowrap; }
        .pls-bin-none { font-size: 11px; font-weight: 600; color: #b86e00;
                         background: #fff3d6; border: 1px solid #f0d28e; border-radius: 3px;
                         padding: 3px 8px; letter-spacing: 0.02em; text-transform: uppercase;
                         display: inline-block; }
        .pls-qty { text-align: right; font-size: 28px; font-weight: 700; color: #111418;
                    letter-spacing: -0.02em; line-height: 1; padding-left: 12px; }
        .pls-qty-x { font-size: 14px; color: #9ca3af; font-weight: 500; margin-right: 8px; }
        .pls-footer { margin-top: 22px; padding-top: 10px; border-top: 1px solid #e5e7eb;
                       display: flex; justify-content: space-between; font-size: 10px;
                       color: #9ca3af; }
        @media print {
          body { padding: 12px; }
          .pls-print-btn { display: none; }
          .pls-row { cursor: default; break-inside: avoid; }
          .pls-aisle { break-inside: avoid; }
          .pls-type { break-inside: avoid; }
          /* Always print the checkbox empty regardless of on-screen done state. */
          .pls-row--done .pls-check { background: #fff !important; border-color: #9ca3af !important; }
          .pls-row--done .pls-check::after { display: none !important; }
          .pls-row--done .pls-prod, .pls-row--done .pls-bin,
          .pls-row--done .pls-qty, .pls-row--done .pl-thumb { opacity: 1 !important; }
        }
      `;

      // Helper: build a single row element.
      const buildRow = (r) => {
        const row = doc.createElement('div');
        row.className = 'pls-row' + (r.bins.size === 0 ? ' pls-row--unbinned' : '');
        row.setAttribute('role', 'button');
        row.addEventListener('click', () => { row.classList.toggle('pls-row--done'); });

        const check = doc.createElement('div');
        check.className = 'pls-check';
        row.appendChild(check);

        const thumb = doc.createElement('div');
        thumb.className = 'pl-thumb';
        if (r.image) {
          const img = doc.createElement('img');
          img.setAttribute('src', r.image);
          img.setAttribute('alt', '');
          img.setAttribute('referrerpolicy', 'no-referrer');
          thumb.appendChild(img);
          // Match SmartProductImage: probe the pixel data; if the product is
          // white-on-white the thumb gets a darker background so the product
          // stays visible. Same logic as the order view's thumbs.
          try {
            const probe = new Image();
            probe.crossOrigin = 'anonymous';
            probe.onload = () => {
              try {
                const canvas = doc.createElement('canvas');
                canvas.width = 20; canvas.height = 20;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(probe, 0, 0, 20, 20);
                const { data } = ctx.getImageData(0, 0, 20, 20);
                let product = 0, lightProduct = 0;
                for (let i = 0; i < data.length; i += 4) {
                  const a = data[i + 3];
                  if (a < 30) continue;
                  product++;
                  if (data[i] > 210 && data[i+1] > 210 && data[i+2] > 210) lightProduct++;
                }
                if (product > 0 && lightProduct / product > 0.90) {
                  thumb.classList.add('pl-thumb--lightbg');
                }
              } catch (_) { /* CORS taint */ }
            };
            probe.onerror = () => {};
            probe.src = r.image + (r.image.includes('?') ? '&' : '?') + '__corsprobe=1';
          } catch (_) {}
        } else {
          const empty = doc.createElement('div');
          empty.className = 'pl-thumb-empty';
          empty.textContent = 'no img';
          thumb.appendChild(empty);
        }
        row.appendChild(thumb);

        const prod = doc.createElement('div');
        prod.className = 'pls-prod';
        const nameLine = doc.createElement('div');
        nameLine.className = 'pls-prod-name';
        const nameSpan = doc.createElement('span');
        nameSpan.textContent = r.title;
        nameLine.appendChild(nameSpan);
        if (r.variant && r.variant !== 'Default Title') {
          const chip = doc.createElement('span');
          chip.className = 'pls-variant';
          chip.textContent = r.variant;
          nameLine.appendChild(chip);
        }
        prod.appendChild(nameLine);
        const skuLine = doc.createElement('div');
        skuLine.className = 'pls-prod-sku';
        skuLine.textContent = r.sku;
        prod.appendChild(skuLine);
        // SKU-notification badge (2026-07-25) — matches the printed order
        // and the picking-portal live-view style. Rendered under the SKU
        // line when any selected order carries an active notification on
        // this base SKU. DOM API only (no innerHTML), textContent is
        // aggregated at merge time via skuToNotif so no injection risk.
        const _notifMsg = skuToNotif.get(r.sku);
        if (_notifMsg) {
          const badge = doc.createElement('div');
          badge.style.cssText = 'display:inline-block;margin-top:4px;padding:3px 8px;'
                              + 'border-radius:10px;background:#fde8e8;color:#c00;'
                              + 'font-weight:700;font-size:10px;border:1px solid #fbb;line-height:1.15';
          badge.textContent = _notifMsg;
          prod.appendChild(badge);
        }
        // Inline list of numbers / players merged from per-personalization line
        // items. Only renders when the base SKU was actually personalized.
        if (r.numbers && r.numbers.length > 0) {
          const numLine = doc.createElement('div');
          numLine.className = 'pls-personalization';
          const lbl = doc.createElement('span');
          lbl.className = 'pls-personalization-label';
          lbl.textContent = 'Numbers: ';
          numLine.appendChild(lbl);
          numLine.appendChild(doc.createTextNode(r.numbers.join(', ')));
          prod.appendChild(numLine);
        }
        if (r.players && r.players.length > 0) {
          const plLine = doc.createElement('div');
          plLine.className = 'pls-personalization';
          const lbl = doc.createElement('span');
          lbl.className = 'pls-personalization-label';
          lbl.textContent = 'Names: ';
          plLine.appendChild(lbl);
          plLine.appendChild(doc.createTextNode(r.players.join(', ')));
          prod.appendChild(plLine);
        }
        row.appendChild(prod);

        const binCell = doc.createElement('div');
        if (r.bins.size > 0) {
          const binSpan = doc.createElement('span');
          binSpan.className = 'pls-bin';
          binSpan.textContent = [...r.bins].join(', ');
          binCell.appendChild(binSpan);
        } else {
          const none = doc.createElement('span');
          none.className = 'pls-bin-none';
          none.textContent = 'No bin';
          binCell.appendChild(none);
        }
        row.appendChild(binCell);

        const qty = doc.createElement('div');
        qty.className = 'pls-qty';
        const x = doc.createElement('span');
        x.className = 'pls-qty-x';
        x.textContent = '×';
        qty.appendChild(x);
        qty.appendChild(doc.createTextNode(String(r.qty)));
        row.appendChild(qty);

        return row;
      };

      // Helper: build aisle section header.
      const buildAisleHeader = (tag, items, warn) => {
        const head = doc.createElement('div');
        head.className = 'pls-aisle';
        const tagEl = doc.createElement('span');
        tagEl.className = 'pls-aisle-tag' + (warn ? ' pls-aisle-tag--warn' : '');
        tagEl.textContent = warn ? 'Not in bin' : `Aisle ${tag}`;
        head.appendChild(tagEl);
        const u = items.reduce((s, r) => s + r.qty, 0);
        const meta = doc.createElement('span');
        meta.className = 'pls-aisle-meta';
        meta.textContent = `${items.length} ${items.length === 1 ? 'SKU' : 'SKUs'} · ${u} ${u === 1 ? 'unit' : 'units'}`;
        head.appendChild(meta);
        return head;
      };

      // Helper: build a product-type subheader.
      const buildTypeSubheader = (typeName, items) => {
        const head = doc.createElement('div');
        head.className = 'pls-type';
        const name = doc.createElement('span');
        name.className = 'pls-type-name';
        name.textContent = typeName;
        head.appendChild(name);
        const u = items.reduce((s, r) => s + r.qty, 0);
        const meta = doc.createElement('span');
        meta.className = 'pls-type-meta';
        meta.textContent = `${items.length} ${items.length === 1 ? 'SKU' : 'SKUs'} · ${u} ${u === 1 ? 'unit' : 'units'}`;
        head.appendChild(meta);
        return head;
      };

      // Helper: emit a section's rows clustered under product-type subheaders.
      // Assumes `items` is already sorted by productType then title.
      const appendTypeGroupedRows = (items) => {
        let currentType = null;
        let currentBucket = [];
        const flush = () => {
          if (currentBucket.length === 0) return;
          root.appendChild(buildTypeSubheader(currentType, currentBucket));
          for (const r of currentBucket) root.appendChild(buildRow(r));
        };
        for (const r of items) {
          const t = r.productType || TYPE_BLANK_LABEL;
          if (t !== currentType) {
            flush();
            currentType = t;
            currentBucket = [r];
          } else {
            currentBucket.push(r);
          }
        }
        flush();
      };

      // ── Assemble the page.
      const root = doc.createElement('div');

      // Top toolbar — date/time + location, print button.
      const toolbar = doc.createElement('div');
      toolbar.className = 'pls-toolbar';
      const tbInfo = doc.createElement('div');
      tbInfo.className = 'pls-toolbar-info';
      tbInfo.textContent = topHeaderText;
      toolbar.appendChild(tbInfo);
      const printBtn = doc.createElement('button');
      printBtn.className = 'pls-print-btn';
      printBtn.textContent = '🖨  Print';
      printBtn.addEventListener('click', () => popup.print());
      toolbar.appendChild(printBtn);
      root.appendChild(toolbar);

      // Title block.
      const header = doc.createElement('div');
      header.className = 'pls-header';
      const h1 = doc.createElement('h1');
      h1.className = 'pls-title';
      h1.textContent = 'Pick List';
      header.appendChild(h1);

      const metaLine = doc.createElement('div');
      metaLine.className = 'pls-meta';
      const itemsB = doc.createElement('b'); itemsB.textContent = String(totalSkus);
      metaLine.appendChild(itemsB);
      metaLine.appendChild(doc.createTextNode(' items'));
      const sep1 = doc.createElement('span'); sep1.className = 'pls-meta-sep'; sep1.textContent = '·';
      metaLine.appendChild(sep1);
      const unitsB = doc.createElement('b'); unitsB.textContent = String(totalQty);
      metaLine.appendChild(unitsB);
      metaLine.appendChild(doc.createTextNode(' units'));
      if (clubs.length > 0) {
        const sep2 = doc.createElement('span'); sep2.className = 'pls-meta-sep'; sep2.textContent = '·';
        metaLine.appendChild(sep2);
        const clubSpan = doc.createElement('span');
        clubSpan.className = 'pls-club';
        clubSpan.textContent = clubs.join(', ');
        metaLine.appendChild(clubSpan);
      }
      header.appendChild(metaLine);

      const orderList = doc.createElement('div');
      orderList.className = 'pls-orderlist';
      const orderLabel = doc.createElement('span');
      orderLabel.className = 'pls-orderlist-label';
      const ordCountB = doc.createElement('b');
      ordCountB.textContent = String(orderNames.length);
      orderLabel.appendChild(ordCountB);
      orderLabel.appendChild(doc.createTextNode(orderNames.length === 1 ? ' order' : ' orders'));
      orderList.appendChild(orderLabel);
      orderNames.forEach(n => {
        const pill = doc.createElement('span');
        pill.className = 'pls-orderpill';
        pill.textContent = n;
        orderList.appendChild(pill);
      });
      header.appendChild(orderList);
      root.appendChild(header);

      // Aisle sections (binned) — each gets product-type subheaders.
      for (const [aisleTag, items] of aisleEntries) {
        root.appendChild(buildAisleHeader(aisleTag, items, false));
        appendTypeGroupedRows(items);
      }
      // Unbinned section at the bottom (warn-styled) — same subgroup treatment.
      if (unbinned.length > 0) {
        root.appendChild(buildAisleHeader('—', unbinned, true));
        appendTypeGroupedRows(unbinned);
      }

      // Footer — subtle generation stamp.
      const footer = doc.createElement('div');
      footer.className = 'pls-footer';
      const fLeft = doc.createElement('span');
      fLeft.textContent = `generated ${fmtNowET()}`;
      footer.appendChild(fLeft);
      const fRight = doc.createElement('span');
      fRight.textContent = locLabel || activeStore || '';
      footer.appendChild(fRight);
      root.appendChild(footer);

      // Swap in.
      while (doc.body.firstChild) doc.body.removeChild(doc.body.firstChild);
      doc.head.appendChild(style);
      doc.body.appendChild(root);
    } catch (e) {
      if (!popup.closed) {
        const err = popup.document.createElement('p');
        err.style.cssText = 'color:#7b0f0f;font-size:15px';
        err.textContent = `Pick list build failed: ${String(e?.message || e)}`;
        while (popup.document.body.firstChild) popup.document.body.removeChild(popup.document.body.firstChild);
        popup.document.body.appendChild(err);
      }
    } finally {
      setPickListFetching(false);
      pickListPopupRef.current = null;
    }
  };

  // ── Row navigation ─────────────────────────────────────────────────────────
  const openOrder = (numericId) => {
    const backTo = `${location.pathname}${location.search || ''}`;
    // Save just the back-target — the full filtered+sorted ID list is
    // maintained by the prefetch useEffect below (covers all matching orders,
    // not just the current 50-row page). If the prefetch hasn't completed
    // yet (rare; only matters on the very first click after page load),
    // fall back to the visible-page's IDs so prev/next isn't blank.
    try {
      sessionStorage.setItem('ordersBackTo', backTo);
      const existing = sessionStorage.getItem('ordersList');
      const haveFullCursor = (() => {
        try {
          const env = JSON.parse(existing || '{}');
          return env?.store === activeStore && Array.isArray(env?.ids) && env.ids.length > 0;
        } catch { return false; }
      })();
      if (!haveFullCursor) {
        const ids = (displayedOrders || [])
          .map(fo => (fo.order?.id || '').split('/').pop())
          .filter(Boolean);
        sessionStorage.setItem('ordersList', JSON.stringify({
          store: activeStore, ids, savedAt: Date.now(),
        }));
      }
    } catch {}
    // 2026-07-06: propagate the current ?loc= into the order-detail URL so
    // split-order views/prints route through the same location perspective
    // as the list.
    const _loc = searchParams.get('loc');
    const _q = _loc ? `?loc=${encodeURIComponent(_loc)}` : '';
    navigate(`/store/${activeStore}/orders/${numericId}${_q}`, { state: { backTo } });
  };

  // Prev/Next cursor prefetch — whenever filter/sort/store changes, fetch the
  // FULL ordered ID list (capped at MAX_CURSOR_IDS=5000) and save to
  // sessionStorage so OrderDetail's footer walks all matching orders instead
  // of just the current 50-row page. Filter the param-build the same way
  // useOrdersQuery does so the cursor always agrees with what the table shows.
  // Skip while searching — search results aren't stable for prev/next walking.
  useEffect(() => {
    if (isSearching) return;
    let cancelled = false;
    const params = new URLSearchParams();
    statuses.forEach(s => params.append('statuses', s));
    clubs.forEach(c => params.append('clubs', c));
    teams.forEach(t => params.append('teams', t));
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo)   params.set('date_to', dateTo);
    if (fulfillByFrom) params.set('fulfill_by_from', fulfillByFrom);
    if (fulfillByTo)   params.set('fulfill_by_to',   fulfillByTo);
    printeds.forEach(v => params.append('printeds', v));
    pickStatuses.forEach(v => params.append('pick_statuses', v));
    pressStatuses.forEach(v => params.append('press_statuses', v));
    packStatuses.forEach(v => params.append('pack_statuses', v));
    deliveryMethods.forEach(v => params.append('delivery_methods', v));
    deliveryNames.forEach(v => params.append('delivery_names', v));
    pickupStores.forEach(s => params.append('pickup_stores', s));
    storeStatuses.forEach(v => params.append('store_statuses', v));
    isCustoms.forEach(v => params.append('is_customs', v));
    crms.forEach(v => params.append('crms', v));
    sources.forEach(v => params.append('sources', v));
    if (retailStore) params.append('retail_store', '1');
    protectStatuses.forEach(v => params.append('protect_statuses', v));
    riskLevels.forEach(v => params.append('risk_levels', v));
    deliveryStatuses.forEach(v => params.append('delivery_statuses', v));
    if (urlLocParam) params.set('loc', urlLocParam);
    locations.forEach(v => params.append('locations', v));
    if (sku.trim()) {
      params.set('sku', sku.trim());
      // 2026-09-24 (Trent): without this the cursor walked every order matching
      // the SKU while the table showed only the not-fully-picked ones — click an
      // order from 30 results and prev/next stepped through 155.
      if (skuUnpicked) params.set('sku_unpicked', '1');
      if (skuExclude) params.set('sku_exclude', '1');
    }
    if (splitTeamFulfillment) params.set('split_team_fulfillment', '1');
    if (includeArchived) params.set('include_archived', '1');
    params.set('sort_key', sortKey);
    params.set('sort_dir', sortDir);
    storeFetch(`/api/orders/cursor-ids?${params}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data?.ids) return;
        try {
          sessionStorage.setItem('ordersList', JSON.stringify({
            store: activeStore,
            ids: data.ids,
            total: data.total,
            truncated: !!data.truncated,
            savedAt: Date.now(),
          }));
        } catch {}
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // skuUnpicked, splitTeamFulfillment and urlLocParam are read in the param
    // build above, so they belong here too — without them the effect keeps the
    // list it built before the toggle and prev/next silently disagrees with the
    // table (2026-09-24).
  }, [statuses, clubs, teams, dateFrom, dateTo, fulfillByFrom, fulfillByTo, printeds, pickStatuses,
      pressStatuses, packStatuses, deliveryMethods, deliveryNames, pickupStores, storeStatuses,
      isCustoms, crms, sources, retailStore, protectStatuses, riskLevels, deliveryStatuses, locations, sku, skuUnpicked, skuExclude,
      splitTeamFulfillment, urlLocParam, includeArchived, sortKey, sortDir, activeStore,
      isSearching]);

  // ── Column picker ──────────────────────────────────────────────────────────
  const [colPickerOpen, setColPickerOpen] = useState(false);
  // Match the current visual column order (drag-reorder reflects in the
  // dropdown) — fall back to COLUMN_DEFS for any newly-added cols that aren't
  // in the saved colOrder yet. Always exclude the sticky checkbox + Order #.
  const allToggleableCols = useMemo(() => {
    const byId = Object.fromEntries(COLUMN_DEFS.map(c => [c.id, c]));
    const seen = new Set();
    const out = [];
    for (const id of colOrder) {
      if (id === 'checkbox' || id === 'name') continue;
      if (byId[id]?.filterOnly) continue;   // SKU and friends — filter only, no table cell
      if (byId[id] && !seen.has(id)) { out.push(byId[id]); seen.add(id); }
    }
    for (const def of COLUMN_DEFS) {
      if (def.id === 'checkbox' || def.id === 'name') continue;
      if (def.filterOnly) continue;
      if (!seen.has(def.id)) out.push(def);
    }
    return out;
  }, [colOrder]);

  // ── Filter helpers ─────────────────────────────────────────────────────────
  const clearAllFilters = () => {
    setStatuses([]); setClubs([]); setTeams([]);
    setDateFrom(''); setDateTo('');
    setFulfillByFrom(''); setFulfillByTo('');
    setPrinteds([]); setPickStatuses([]); setPressStatuses([]); setPackStatuses([]);
    setDeliveryMethods([]); setPickupStores([]); setStoreStatuses([]);
    setIsCustoms([]); setCrms([]); setSources([]); setRetailStore(false); setProtectStatuses([]); setRiskLevels([]); setDeliveryStatuses([]); setLocations([]);
    setSku(''); setSkuUnpicked(false); setSkuExclude(false);
    setSplitTeamFulfillment(false);
    setIncludeArchived(false);
    setSelectedIds(new Set());
  };

  // Summary of active filters for the filter bar — each entry is a chip with
  // its own clear handler. Multi-value filters render as a single chip whose
  // dismiss clears the whole filter.
  const activeFilterSummary = useMemo(() => {
    const chips = [];
    if (statuses.length)       chips.push({ key: 'statuses',        label: `Status: ${statuses.join(', ')}`,             onRemove: () => setStatuses([]) });
    if (clubs.length)          chips.push({ key: 'clubs',           label: `Club: ${clubs.join(', ')}`,                  onRemove: () => setClubs([]) });
    if (teams.length)          chips.push({ key: 'teams',           label: `Team: ${teams.join(', ')}`,                  onRemove: () => setTeams([]) });
    if (dateFrom || dateTo)    chips.push({ key: 'date',            label: `Date: ${dateFrom || '…'} – ${dateTo || '…'}`, onRemove: () => { setDateFrom(''); setDateTo(''); } });
    if (fulfillByFrom || fulfillByTo) chips.push({ key: 'fulfill_by', label: `Fulfill By: ${fulfillByFrom || '…'} – ${fulfillByTo || '…'}`, onRemove: () => { setFulfillByFrom(''); setFulfillByTo(''); } });
    if (printeds.length)       chips.push({ key: 'printeds',        label: `Printed: ${printeds.join(', ')}`,            onRemove: () => setPrinteds([]) });
    if (pickStatuses.length)   chips.push({ key: 'pickStatuses',    label: `Pick: ${pickStatuses.join(', ')}`,           onRemove: () => setPickStatuses([]) });
    if (pressStatuses.length)  chips.push({ key: 'pressStatuses',   label: `Press: ${pressStatuses.join(', ')}`,         onRemove: () => setPressStatuses([]) });
    if (packStatuses.length)   chips.push({ key: 'packStatuses',    label: `Pack: ${packStatuses.join(', ')}`,           onRemove: () => setPackStatuses([]) });
    if (deliveryMethods.length)chips.push({ key: 'deliveryMethods', label: `Delivery: ${deliveryMethods.join(', ')}`,    onRemove: () => setDeliveryMethods([]) });
    if (pickupStores.length)   chips.push({ key: 'pickupStores',    label: `Pickup store: ${pickupStores.join(', ')}`,   onRemove: () => setPickupStores([]) });
    if (storeStatuses.length)  chips.push({ key: 'storeStatuses',   label: `Store status: ${storeStatuses.map(v => STORE_STATUS_LABELS[v] || v).join(', ')}`,  onRemove: () => setStoreStatuses([]) });
    if (isCustoms.length)      chips.push({ key: 'isCustoms',       label: `Custom: ${isCustoms.join(', ')}`,            onRemove: () => setIsCustoms([]) });
    if (crms.length)           chips.push({ key: 'crms',            label: `CRM: ${crms.join(', ')}`,                    onRemove: () => setCrms([]) });
    if (protectStatuses.length) chips.push({ key: 'protect_statuses', label: `Shopify Protect: ${protectStatuses.join(', ')}`, onRemove: () => setProtectStatuses([]) });
    if (riskLevels.length) chips.push({ key: 'risk_levels', label: `Order Risk: ${riskLevels.join(', ')}`, onRemove: () => setRiskLevels([]) });
    if (deliveryStatuses.length) chips.push({ key: 'delivery_statuses', label: `Delivery: ${deliveryStatuses.join(', ')}`, onRemove: () => setDeliveryStatuses([]) });
    if (sources.length)        chips.push({ key: 'sources',         label: `Source: ${sources.join(', ')}`,              onRemove: () => setSources([]) });
    if (retailStore)           chips.push({ key: 'retail_store',    label: 'Retail Unfulfilled (never moved)',           onRemove: () => setRetailStore(false) });
    if (locations.length) {
      const nameById = new Map((availableLocations || []).map(l => [l.shopify_location_id, l.name]));
      chips.push({
        key: 'locations',
        label: `Location: ${locations.map(id => nameById.get(id) || id).join(', ')}`,
        onRemove: () => setLocations([]),
      });
    }
    // 2026-09-24 (Trent): the not-fully-picked toggle narrowed the list with
    // nothing on the results page to say so — 30 rows, two chips, and a footer
    // counting 155. It gets its own chip, and clearing the SKU clears it too,
    // since the backend gates on both and a stale toggle would silently narrow
    // the next SKU someone types.
    if (sku.trim())     chips.push({
      key: 'sku',
      // 2026-09-25 (Trent): the chip states the SENSE of the filter — reading
      // `SKU contains` on a list built by excluding it would be a lie.
      label: (() => {
        // 2026-09-26 (Trent): several SKUs read as any-of / none-of.
        const list = sku.split(',').map(x => x.trim()).filter(Boolean);
        const shown = list.map(x => `"${x}"`).join(', ');
        if (list.length > 1) return skuExclude ? `SKU excludes any of: ${shown}` : `SKU contains any of: ${shown}`;
        return skuExclude ? `SKU excludes: ${shown}` : `SKU contains: ${shown}`;
      })(),
      onRemove: () => { setSku(''); setSkuUnpicked(false); setSkuExclude(false); },
    });
    if (sku.trim() && skuUnpicked && !skuExclude) chips.push({ key: 'skuUnpicked', label: 'SKU not fully picked', onRemove: () => setSkuUnpicked(false) });
    if (splitTeamFulfillment) chips.push({ key: 'splitTeamFulfillment', label: 'Split Team Fulfillment', onRemove: () => setSplitTeamFulfillment(false) });
    if (includeArchived) chips.push({ key: 'includeArchived', label: 'Including Archived', onRemove: () => setIncludeArchived(false) });
    return chips;
  }, [statuses, clubs, teams, dateFrom, dateTo, fulfillByFrom, fulfillByTo, printeds, pickStatuses, pressStatuses,
      packStatuses, deliveryMethods, deliveryNames, pickupStores, storeStatuses, isCustoms, crms, sources, retailStore,
      protectStatuses,
      riskLevels,
      deliveryStatuses,
      locations, sku, skuUnpicked, skuExclude, splitTeamFulfillment, includeArchived, availableLocations]);

  const hasAnyFilter = activeFilterSummary.length > 0;

  // ── Get per-column filter getter/setter ────────────────────────────────────
  const filterGetSet = {
    statuses:        { get: statuses,        set: setStatuses },
    clubs:           { get: clubs,           set: setClubs },
    teams:           { get: teams,           set: setTeams },
    date:            { get: { from: dateFrom, to: dateTo }, set: ({ from, to }) => { setDateFrom(from); setDateTo(to); } },
    fulfill_by:      { get: { from: fulfillByFrom, to: fulfillByTo }, set: ({ from, to }) => { setFulfillByFrom(from); setFulfillByTo(to); } },
    printeds:        { get: printeds,        set: setPrinteds },
    pick_statuses:   { get: pickStatuses,    set: setPickStatuses },
    press_statuses:  { get: pressStatuses,   set: setPressStatuses },
    pack_statuses:   { get: packStatuses,    set: setPackStatuses },
    delivery_methods:{ get: deliveryMethods, set: setDeliveryMethods },
    pickup_stores:   { get: pickupStores,    set: setPickupStores },
    // Virtual key for the Delivery column header filter — its choices are
    // namespaced "ship:SHIPPING" + "store:<name>". The setter splits the
    // selection into the underlying delivery_methods / pickup_stores
    // filters; the getter rebuilds the namespaced array for display.
    delivery_combined: {
      get: [
        ...deliveryMethods.map(m => `ship:${m}`),
        ...deliveryNames.map(n => `ship_name:${n}`),
        ...pickupStores.map(s => `store:${s}`),
      ],
      set: (vals) => {
        const methods = [];
        const names   = [];
        const stores  = [];
        for (const v of (vals || [])) {
          if (typeof v !== 'string') continue;
          if (v.startsWith('ship_name:')) names.push(v.slice('ship_name:'.length));
          else if (v.startsWith('ship:'))  methods.push(v.slice(5));
          else if (v.startsWith('store:')) stores.push(v.slice(6));
        }
        setDeliveryMethods(methods);
        setDeliveryNames(names);
        setPickupStores(stores);
      },
    },
    store_statuses:  { get: storeStatuses,   set: setStoreStatuses },
    is_customs:      { get: isCustoms,       set: setIsCustoms },
    crms:            { get: crms,            set: setCrms },
    sources:         { get: sources,         set: setSources },
    protect_statuses:{ get: protectStatuses, set: setProtectStatuses },
    risk_levels:     { get: riskLevels,      set: setRiskLevels      },
    delivery_statuses:{ get: deliveryStatuses, set: setDeliveryStatuses },
    locations:       { get: locations,       set: setLocations },
    // SKU is the only text filter so far. Stores a raw string; backend
    // does the trimming + ILIKE wildcard wrapping.
    sku:             { get: sku,             set: setSku },
    // Companion boolean to the SKU text filter: only include orders where
    // the matched SKU is under-picked (qty_picked < qty_total on the
    // matching item_state row). Backend ignores it when `sku` is empty.
    sku_unpicked:    { get: skuUnpicked,     set: setSkuUnpicked },
    sku_exclude:     { get: skuExclude,      set: setSkuExclude },
    // Boolean toggle — filter to orders whose fulfillment spans 2+ "Teams *"
    // locations. Backend gates on cache-row location names starting with
    // "Teams " grouped per order. Any user with visibility to either loc
    // already passes scope; this just narrows within that scope.
    split_team_fulfillment: { get: splitTeamFulfillment, set: setSplitTeamFulfillment },
  };

  // ── Plain-text cell value for CSV export ───────────────────────────────────
  // Mirrors renderCellContent without JSX so CSV cells contain readable text.
  const cellTextForExport = useCallback((colId, fo) => {
    const o = fo.order || {};
    let clubteams = [];
    try { clubteams = o.clubteams ? JSON.parse(o.clubteams) : []; } catch {}
    switch (colId) {
      case 'name':         return o.name || '';
      case 'created_at':   return fmtDateTime(o.created_at, activeLocTz);
      case 'customer':     return customerName(fo);
      case 'customer_email': return fo.order?.customer?.email || '';
      case 'status': {
        if (o.cancelled_at) return 'CANCELLED';
        const emLocId = emulating?.shopify_location_id
          ? String(emulating.shopify_location_id) : null;
        if (emLocId && fo.row_fulfillment_status) return fo.row_fulfillment_status;
        return o.fulfillment_status || '';
      }
      case 'pick_pct':     return fo.pick_pct  != null ? `${fo.pick_pct}%`  : '';
      case 'press_pct':    return fo.press_pct != null ? `${fo.press_pct}%` : '';
      case 'pack_pct':     return fo.pack_pct  != null ? `${fo.pack_pct}%`  : '';
      case 'print_status': {
        // Same emulation-scope filter as the chip render below (case
        // 'print_status' in the second switch) — keeps sort key / CSV
        // export consistent with what's on-screen. Strip "Soccer Post "
        // prefix from emulated name so retail-store tags (which backend
        // wrote without the prefix) still match.
        const _allPerLoc = getPerLocationPrintTags(o.tags);
        const _stripSPPrefix = (s) => (s || '').toLowerCase().startsWith('soccer post ')
          ? s.slice('Soccer Post '.length) : s;
        const _emName = _stripSPPrefix(emulating?.name || '').toLowerCase().trim();
        const perLoc = _emName
          ? _allPerLoc.filter(s => _emName.startsWith((s || '').toLowerCase().trim()))
          : _allPerLoc;
        if (perLoc.length) return perLoc.map(s => `Printed ${s}`).join(' | ');
        return hasLegacyPrintTag(o.tags) ? 'Printed' : '';
      }
      case 'items':        return (o.line_items || []).reduce((s, li) => s + (li.quantity || 0), 0);
      case 'players':      return fo.players_count ?? '';
      case 'fulfill_by': {
        const fb = fo.fulfill_by;
        if (!fb) return '';
        // Same local-TZ parse as the visual cell (avoids UTC-midnight off-by-1).
        const ymd = String(fb).slice(0, 10).split('-');
        if (ymd.length === 3) {
          const y = parseInt(ymd[0], 10), m = parseInt(ymd[1], 10), day = parseInt(ymd[2], 10);
          if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(day)) {
            return new Date(y, m - 1, day).toLocaleDateString();
          }
        }
        return fb;
      }
      case 'location': {
        const emLocId = emulating?.shopify_location_id
          ? String(emulating.shopify_location_id) : null;
        const locsAll = Array.isArray(fo.location_names_all)
          ? fo.location_names_all : [];
        if (!emLocId && locsAll.length > 1) {
          return locsAll.map(l => l.name).filter(Boolean).join(' | ');
        }
        return fo.location_name || '';
      }
      case 'delivery': {
        const methods = fo.delivery_methods || [fo.delivery_method];
        const uniq = [...new Set(methods.filter(Boolean))];
        const shipEntries = Array.isArray(fo.delivery_shipping_names)
          ? fo.delivery_shipping_names : [];
        const emLocId = emulating?.shopify_location_id
          ? String(emulating.shopify_location_id) : null;
        const shipLabels = (emLocId
          ? shipEntries.filter(e => e.shopify_location_id === emLocId)
          : shipEntries
        ).map(e => e.name).filter(Boolean);
        const parts = [];
        uniq.forEach(m => {
          const isPickup = PICKUP_TYPES.has((m || '').toUpperCase());
          if (isPickup) {
            parts.push(fo.pickup_store_name || DELIVERY_LABELS[m] || m || 'Pickup in store');
          } else if (shipLabels.length) {
            shipLabels.forEach(l => parts.push(l));
          } else {
            parts.push(DELIVERY_LABELS[m] || m || 'Shipping');
          }
        });
        return parts.join(' | ');
      }
      case 'club':         return o.club || '';
      case 'teams':        return clubteams.join(' | ');
      case 'player_names': {
        // Distinct player names parsed from the order's _soccer_order_info
        // customAttribute. Cached as a JSON string in `players_json` at the
        // row top level (mirrors players_count). Empty when the column
        // hasn't been backfilled yet for this row.
        try {
          const raw = fo.players_json;
          const arr = raw ? JSON.parse(raw) : [];
          return Array.isArray(arr) ? arr.join(' | ') : '';
        } catch { return ''; }
      }
      case 'player_numbers': {
        // Index-aligned with players_json (see recache_stale.py:_extract_meta).
        // Empty per-player when a player has no assigned number.
        try {
          const raw = fo.player_numbers_json;
          const arr = raw ? JSON.parse(raw) : [];
          return Array.isArray(arr) ? arr.join(' | ') : '';
        } catch { return ''; }
      }
      case 'source': {
        if (isAmazonFbm(o))              return 'Amazon FBM';   // any Amazon-sourced order (tags Amazon / FBM-Routed), 2026-10-16
        const src = (o.source || fo.source || '').toLowerCase();
        if (src === 'team_portal')       return 'Team Portal';
        if (src === 'retail')            return 'Retail';
        if (src === 'addon')             return 'Add-On';
        if (src === 'tp_retail')         return 'Team Portal/Retail';
        if (src === 'tp_addon')          return 'Team Portal/Add-On';
        if (src === 'retail_addon')      return 'Retail/Add-On';
        if (src === 'tp_retail_addon')   return 'Team Portal/Retail/Add-On';
        return '';
      }
      case 'is_custom':    return fo.is_custom === true ? 'Yes' : fo.is_custom === false ? 'No' : '';
      case 'store_status': {
        const ss = fo.store_status ?? 0;
        return ss ? formatStoreStatus(ss, fo.store_status_pct) : '';
      }
      case 'bulk_tracking': {
        const distinct = dedupeTrackingEntries(trackingEntries(fo));
        return distinct.map(x => x.number).join('; ');
      }
      case 'delivery_date': {
        const distinct = dedupeTrackingEntries(trackingEntries(fo));
        return distinct.map(trackingLineText).join('; ');
      }
      case 'crm':          return fo.teamportal_crm || '';
      case 'protect_status': {
        const ps = fo.protect_status;
        if (!ps) return '';
        if (ps === 'PROTECTED')     return 'Protected';
        if (ps === 'ACTIVE')        return 'Active';
        if (ps === 'NOT_PROTECTED') return 'Not Protected';
        if (ps === 'PENDING')       return 'Pending';
        if (ps === 'INACTIVE')      return 'Inactive';
        return ps;
      }
      case 'risk_level': {
        const rl = fo.risk_level;
        if (!rl) return '';
        if (rl === 'LOW')     return 'Low';
        if (rl === 'MEDIUM')  return 'Medium';
        if (rl === 'HIGH')    return 'High';
        if (rl === 'PENDING') return 'Pending';
        if (rl === 'NONE')    return 'None';
        return rl;
      }
      case 'total':        return `$${parseFloat(o.total?.amount || 0).toFixed(2)}`;
      case 'net_sales':    return fo.subtotal_price != null ? `$${parseFloat(fo.subtotal_price).toFixed(2)}` : '';
      case 'shipping':     return fo.total_shipping != null ? `$${parseFloat(fo.total_shipping).toFixed(2)}` : '';
      case 'tax':          return fo.total_tax      != null ? `$${parseFloat(fo.total_tax).toFixed(2)}`      : '';
      case 'notes':        return o.note || '';
      case 'tags':         return (o.tags || []).join(', ');
      case 'reworked':     return fo.reworked_count > 0 ? String(fo.reworked_count) : '';
      case 'total_reworks': return fo.total_reworks_count > 0 ? String(fo.total_reworks_count) : '';
      case 'notifications_count': {
        const n = fo.notifications_count || 0;
        return n > 0 ? String(n) : '';
      }
      default:             return '';
    }
  }, [storeColors, activeLocTz]);

  // RFC 4180-ish CSV escape: wrap in quotes if value contains comma, quote,
  // or newline; double internal quotes.
  const csvEscape = (val) => {
    const s = val == null ? '' : String(val);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  // Build the CSV-export URLSearchParams from current filter state. Same as
  // useOrdersQuery.buildParams but local — we use it to walk pages.
  const buildExportParams = useCallback((dbCursor) => {
    const p = new URLSearchParams();
    statuses.forEach(s => p.append('statuses', s));
    clubs.forEach(c => p.append('clubs', c));
    teams.forEach(t => p.append('teams', t));
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo)   p.set('date_to', dateTo);
    if (fulfillByFrom) p.set('fulfill_by_from', fulfillByFrom);
    if (fulfillByTo)   p.set('fulfill_by_to',   fulfillByTo);
    printeds.forEach(v => p.append('printeds', v));
    pickStatuses.forEach(v => p.append('pick_statuses', v));
    pressStatuses.forEach(v => p.append('press_statuses', v));
    packStatuses.forEach(v => p.append('pack_statuses', v));
    deliveryMethods.forEach(v => p.append('delivery_methods', v));
    deliveryNames.forEach(v => p.append('delivery_names', v));
    pickupStores.forEach(s => p.append('pickup_stores', s));
    storeStatuses.forEach(v => p.append('store_statuses', v));
    isCustoms.forEach(v => p.append('is_customs', v));
    crms.forEach(v => p.append('crms', v));
    sources.forEach(v => p.append('sources', v));
    if (retailStore) p.append('retail_store', '1');
    protectStatuses.forEach(v => p.append('protect_statuses', v));
    riskLevels.forEach(v => p.append('risk_levels', v));
    deliveryStatuses.forEach(v => p.append('delivery_statuses', v));
    if (urlLocParam) p.set('loc', urlLocParam);
    locations.forEach(v => p.append('locations', v));
    if (sku.trim()) {
      p.set('sku', sku.trim());
      if (skuUnpicked) p.set('sku_unpicked', '1');
      if (skuExclude) p.set('sku_exclude', '1');
    }
    if (splitTeamFulfillment) p.set('split_team_fulfillment', '1');
    if (includeArchived) p.set('include_archived', '1');
    p.set('sort_key', sortKey);
    p.set('sort_dir', sortDir);
    if (dbCursor) p.set('db_cursor', dbCursor);
    return p;
  }, [statuses, clubs, teams, dateFrom, dateTo, fulfillByFrom, fulfillByTo, printeds, pickStatuses, pressStatuses,
      packStatuses, deliveryMethods, deliveryNames, pickupStores, storeStatuses, isCustoms, crms,
      sources, retailStore, locations, sku, splitTeamFulfillment, includeArchived, sortKey, sortDir]);

  const runExport = useCallback(async () => {
    setExporting(true);
    setExportConfirmOpen(false);
    setExportProgress({ done: 0, total: totalCount || 0 });
    try {
      // Columns to export: visible toggleable columns in their current order,
      // dropping the checkbox column.
      const exportCols = visibleColDefs.filter(c => c.id !== 'checkbox');
      const headerRow = exportCols.map(c => csvEscape(c.label)).join(',');
      const dataRows = [];

      let cursor = null;
      let pageCount = 0;
      const HARD_PAGE_CAP = 1000;  // 50k rows safety net
      while (true) {
        const params = buildExportParams(cursor);
        const res = await storeFetch(`/api/orders?${params}`);
        if (!res.ok) throw new Error(`Export failed at page ${pageCount + 1}: HTTP ${res.status}`);
        const data = await res.json();
        const batch = data.orders || [];
        for (const fo of batch) {
          dataRows.push(exportCols.map(c => csvEscape(cellTextForExport(c.id, fo))).join(','));
        }
        pageCount += 1;
        setExportProgress(prev => ({ done: dataRows.length, total: prev.total || data.total || dataRows.length }));
        cursor = data.next_cursor;
        if (!cursor || batch.length === 0 || pageCount >= HARD_PAGE_CAP) break;
      }

      const csv = [headerRow, ...dataRows].join('\r\n');
      const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `orders-${activeStore}-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  }, [buildExportParams, storeFetch, visibleColDefs, cellTextForExport, totalCount, activeStore]);

  const onExportClick = () => {
    if ((totalCount || 0) > 1000) {
      setExportConfirmOpen(true);
    } else {
      runExport();
    }
  };

  // ── Render cell content ────────────────────────────────────────────────────
  const renderCellContent = (colId, fo) => {
    const o = fo.order || {};
    const numId = (o.id || '').split('/').pop();
    const methods = fo.delivery_methods || [fo.delivery_method];
    const uniqueMethods = [...new Set(methods.filter(Boolean))];
    let clubteams = [];
    try { clubteams = o.clubteams ? JSON.parse(o.clubteams) : []; } catch {}

    switch (colId) {
      case 'name':
        return (
          <a
            href={`/store/${activeStore}/orders/${numId}`}
            className="ots-order-link"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault(); e.stopPropagation();
              openOrder(numId);
            }}
          >{o.name || '—'}</a>
        );
      case 'created_at':
        return fmtDateTime(o.created_at, activeLocTz);
      case 'customer':
        return customerName(fo);
      case 'customer_email':
        return fo.order?.customer?.email || '';
      case 'status': {
        // 2026-07-06 (c): per-loc status column.
        //   Emulated to one loc → that loc's row status only.
        //   All-Locations split (2+ locs) → stacked per-loc badges, one per
        //   loc, index-aligned with the Location / Print / Delivery columns.
        //   Aggregate "Partially Fulfilled" badge is intentionally omitted
        //   in the split case — the per-loc stack already tells that story.
        if (o.cancelled_at) return <StatusBadge status="CANCELLED" />;
        const emLocId = emulating?.shopify_location_id
          ? String(emulating.shopify_location_id) : null;
        const perLoc = Array.isArray(fo.per_loc_status) ? fo.per_loc_status : [];
        if (emLocId) {
          const matched = perLoc.find(e => String(e.shopify_location_id) === emLocId);
          const st = fo.row_fulfillment_status
                  || matched?.status
                  || o.fulfillment_status;
          return <StatusBadge status={st} />;
        }
        if (perLoc.length <= 1) {
          // 2026-07-30 filter-consistency fix: prefer per_loc_status[0].status
          // (which mirrors the SQL WHERE clause's row-level CASE) over
          // o.fulfillment_status (which _shape_order_from_cache_row can
          // override to 'FULFILLED' for orders whose real FOs are all
          // transferred to retail-pickup stores). Without this, filtering
          // "Unfulfilled + Partially Fulfilled" matched rows the shaper then
          // displayed as FULFILLED — user's canonical: SP-323780 (Lancaster
          // row UNFULFILLED per WHERE, but pickup-override made it show
          // FULFILLED). Now the badge matches what the filter matched.
          const first = perLoc[0];
          return <StatusBadge status={first?.status || o.fulfillment_status} />;
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {perLoc.map((e, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: '#475569' }}>{e.location_name || '—'}</span>
                <StatusBadge status={e.status} />
              </div>
            ))}
          </div>
        );
      }
      case 'pick_pct':
      case 'press_pct':
      case 'pack_pct': {
        // 2026-07-06 (c): per-loc percentages when the order spans 2+ locs
        // on All-Locations view. Renders one badge per per_loc_status entry
        // in the same order as the Status / Location columns so the row
        // reads as an aligned band across the table.
        const emLocId = emulating?.shopify_location_id
          ? String(emulating.shopify_location_id) : null;
        const perLoc = Array.isArray(fo.per_loc_status) ? fo.per_loc_status : [];
        const key = colId === 'pick_pct' ? 'pick_pct'
                  : colId === 'press_pct' ? 'press_pct' : 'pack_pct';
        if (!emLocId && perLoc.length > 1) {
          // align-items:flex-start keeps each PPBadge at its content width
          // so 100% / 83% chips stay the same size as single-line rows
          // (default flex align stretched them full-column-width).
          return (
            <div style={{ display: 'flex', flexDirection: 'column',
                          gap: 2, alignItems: 'flex-start' }}>
              {perLoc.map((e, i) => (
                <PPBadge key={i} pct={e[key]} />
              ))}
            </div>
          );
        }
        return <PPBadge pct={fo[key]} />;
      }
      case 'print_status': {
        const _allPerLocSuffixes = getPerLocationPrintTags(o.tags);
        // 2026-07-06 (c): on All-Locations multi-loc orders, print chips
        // stack in the SAME order as the Status / Location / Delivery
        // columns so a reader can scan a single visual row and know the
        // fulfillment + print + shipping state at the loc on that line.
        // Emulated view: unchanged (single-loc filter via startsWith on
        // truncated 40-char tag suffixes).
        const _stripSPPrefix = (s) => (s || '').toLowerCase().startsWith('soccer post ')
          ? s.slice('Soccer Post '.length) : s;
        const _emName = _stripSPPrefix(emulating?.name || '').toLowerCase().trim();
        const _emLocIdPrint = emulating?.shopify_location_id
          ? String(emulating.shopify_location_id) : null;
        const _perLocRows = Array.isArray(fo.per_loc_status) ? fo.per_loc_status : [];
        // Multi-loc: build one entry per per_loc row, looking up which of
        // the (truncated) `_allPerLocSuffixes` starts-with-matches its loc
        // name. Empty spacer row when no match so the column stays
        // vertically aligned with the neighbours.
        // In multi-loc mode entries are shaped as {suffix, locName} — when
        // suffix is null we render a "Not Printed" placeholder chip carrying
        // the loc name so both rows are visible and vertically aligned with
        // the neighbouring Status / Location / Delivery cells.
        const perLocSuffixes = _emLocIdPrint
          ? _allPerLocSuffixes.filter(s => _emName.startsWith((s || '').toLowerCase().trim()))
          : (_perLocRows.length > 1
              ? _perLocRows.map(entry => {
                  const nm = _stripSPPrefix(entry.location_name || '').toLowerCase().trim();
                  const suffix = _allPerLocSuffixes.find(s => nm.startsWith((s || '').toLowerCase().trim())) || null;
                  return { suffix, locName: entry.location_name || '' };
                })
              : _allPerLocSuffixes);
        const legacyPrinted = hasLegacyPrintTag(o.tags);
        // 2026-07-06: multi-loc rows need column-flex so each printed chip
        // sits on its own line. Without this, adjacent inline <span> chips
        // rendered side-by-side and broke visual alignment with the neigh-
        // bour columns (Status / Pick / Press / Pack) that already stack.
        const _printOuter = _perLocRows.length > 1
          ? { display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }
          : null;
        return (
          <div style={_printOuter || undefined}>
            {perLocSuffixes.map((entry, _idx) => {
              // Normalize both shapes: string (single-loc / emulated) or
              // {suffix, locName} (multi-loc alignment mode).
              const suffix = typeof entry === 'string' ? entry : entry.suffix;
              const locName = typeof entry === 'string' ? suffix : entry.locName;
              if (!suffix) {
                return (
                  <div key={`np-${_idx}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: '#475569' }}>{locName}</span>
                    <span style={{
                      display: 'inline-block', padding: '1px 7px', borderRadius: 10,
                      fontSize: 11, fontWeight: 600, background: '#f1f5f9', color: '#475569',
                    }}>Not Printed</span>
                  </div>
                );
              }
              return (
              <span key={suffix} className="ots-print-badge">
                {suffix}
                {!allRolesIn(user, ['crm', 'finance', 'store_manager', 'customer_service', 'external']) && (
                <button
                  type="button"
                  className="ots-print-badge__remove"
                  title="Remove per-location print tag"
                  onClick={e => {
                    e.stopPropagation();
                    if (!numId) return;
                    // Match the Shopify tag case-insensitively on the prefix.
                    // Tags may come back lowercased (`opshubprinted teams las vegas`)
                    // even though we wrote `OpsHubPrinted Teams Las Vegas` — a
                    // strict-equality filter would never strip it and the
                    // optimistic UI removal would no-op until the server
                    // response landed seconds later.
                    // Escape regex metachars in the suffix. The previous escape
                    // pattern was malformed (the char class wasn't terminated
                    // correctly) so `(` in names like "Tinton Falls Warehouse (Vi"
                    // came through unescaped → the RegExp constructor threw
                    // "Unterminated group" and the whole click handler aborted.
                    const escapedSuffix = suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const removeRe = new RegExp(`^OpsHubPrinted\\s+${escapedSuffix}$`, 'i');
                    const removeUpdater = (fo2) =>
                      fo2.order?.id === o.id
                        ? { ...fo2, order: { ...fo2.order, tags: (fo2.order.tags || []).filter(t => !removeRe.test(t)) } }
                        : fo2;
                    setOrders(prev => prev.map(removeUpdater));
                    setSearchResults(prev => prev ? prev.map(removeUpdater) : prev);
                    const removedTag = `OpsHubPrinted ${suffix}`;
                    storeFetch(`/api/orders/${numId}/printed_at_location`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        shopify_location_id: String(
                          (fo.print_locations || []).find(pl => (pl.name||'').toLowerCase() === (suffix||'').toLowerCase())?.shopify_location_id
                          || fo.shopify_location_id || emulating?.shopify_location_id || ''
                        ),
                        // Authoritative — backend strips this exact tag even
                        // when the sloc resolution above falls back to a
                        // non-matching location (admins without an explicit
                        // print_locations map were leaving the chip undeletable).
                        location_suffix: suffix,
                        action: 'remove',
                      }),
                    }).then(r => r.json()).then(data => {
                      if (data.tags) {
                        const okUpdater = (fo2) =>
                          fo2.order?.id === o.id
                            ? { ...fo2, order: { ...fo2.order, tags: data.tags }, printed: false }
                            : fo2;
                        setOrders(prev => prev.map(okUpdater));
                        setSearchResults(prev => prev ? prev.map(okUpdater) : prev);
                      }
                    }).catch(() => {
                      const rollback = (fo2) =>
                        fo2.order?.id === o.id
                          ? { ...fo2, order: { ...fo2.order, tags: [...(fo2.order.tags || []), removedTag] } }
                          : fo2;
                      setOrders(prev => prev.map(rollback));
                      setSearchResults(prev => prev ? prev.map(rollback) : prev);
                    });
                  }}
                >×</button>
                )}
              </span>
            );})}
            {legacyPrinted && perLocSuffixes.length === 0 && (
              <span className="ots-print-badge">
                Printed
                {!allRolesIn(user, ['crm', 'finance', 'store_manager', 'customer_service', 'external']) && (
                <button
                  type="button"
                  className="ots-print-badge__remove"
                  title="Remove print tag"
                  onClick={e => {
                    e.stopPropagation();
                    if (!numId) return;
                    const legacyRemoveUpdater = (fo2) =>
                      fo2.order?.id === o.id
                        ? { ...fo2, order: { ...fo2.order, tags: (fo2.order.tags || []).filter(t => !PRINT_TAGS.has(t.toLowerCase())) }, printed: false }
                        : fo2;
                    setOrders(prev => prev.map(legacyRemoveUpdater));
                    setSearchResults(prev => prev ? prev.map(legacyRemoveUpdater) : prev);
                    storeFetch(`/api/orders/${numId}/remove_print_tag`, { method: 'POST' })
                      .then(r => r.json()).then(data => {
                        if (data.tags) {
                          const ok = (fo2) =>
                            fo2.order?.id === o.id
                              ? { ...fo2, order: { ...fo2.order, tags: data.tags }, printed: false }
                              : fo2;
                          setOrders(prev => prev.map(ok));
                          setSearchResults(prev => prev ? prev.map(ok) : prev);
                        }
                      }).catch(() => {
                        const rb = (fo2) =>
                          fo2.order?.id === o.id
                            ? { ...fo2, order: { ...fo2.order, tags: [...(fo2.order.tags || []), NEW_PRINT_TAG] }, printed: true }
                            : fo2;
                        setOrders(prev => prev.map(rb));
                        setSearchResults(prev => prev ? prev.map(rb) : prev);
                      });
                  }}
                >×</button>
                )}
              </span>
            )}
          </div>
        );
      }
      case 'items':
        return (o.line_items || []).reduce((s, li) => s + (li.quantity || 0), 0);
      case 'players':
        return fo.players_count ?? <span className="ots-muted">—</span>;
      case 'fulfill_by': {
        const fb = fo.fulfill_by;
        if (!fb) return <span className="ots-muted">—</span>;
        // Backend sends a YYYY-MM-DD date string (DATE column). Parse parts
        // manually so JS doesn't treat it as UTC midnight and render the
        // PRIOR day in EDT.
        const ymd = String(fb).slice(0, 10).split('-');
        let txt = fb, isOverdue = false;
        if (ymd.length === 3) {
          const y = parseInt(ymd[0], 10), m = parseInt(ymd[1], 10), day = parseInt(ymd[2], 10);
          if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(day)) {
            // Local-TZ midnight on the target date
            const d = new Date(y, m - 1, day);
            txt = d.toLocaleDateString();
            const isFulfilled = (fo.fulfillment_status || '').toUpperCase() === 'FULFILLED';
            // Use end-of-day for the overdue check so an order due today isn't red mid-day.
            const eod = new Date(y, m - 1, day, 23, 59, 59);
            isOverdue = !isFulfilled && eod.getTime() < Date.now();
          }
        }
        return (
          <span style={{
            color: isOverdue ? '#b71d1d' : '#374151',
            fontWeight: isOverdue ? 600 : 400,
          }}>{txt}</span>
        );
      }
      case 'location': {
        // 2026-07-06 (c): on All-Locations, iterate the SAME per_loc_status
        // order the Status column uses so rows align across columns.
        const emLocId = emulating?.shopify_location_id
          ? String(emulating.shopify_location_id) : null;
        const perLoc = Array.isArray(fo.per_loc_status) ? fo.per_loc_status : [];
        if (!emLocId && perLoc.length > 1) {
          return perLoc.map((e, i) => (
            <div key={i}>{e.location_name || '—'}</div>
          ));
        }
        return fo.location_name || <span className="ots-muted">—</span>;
      }
      case 'delivery': {
        // 2026-07-06 (c): align with per_loc_status. On All-Locations with
        // 2+ locs, render exactly one row per loc — index-aligned with the
        // Location + Status columns — carrying that loc's presented
        // shipping name (or pickup store name for PICK_UP FOs). On
        // emulated view, render only that loc's entries.
        const shipEntries = Array.isArray(fo.delivery_shipping_names)
          ? fo.delivery_shipping_names : [];
        const emLocId = emulating?.shopify_location_id
          ? String(emulating.shopify_location_id) : null;
        const perLoc = Array.isArray(fo.per_loc_status) ? fo.per_loc_status : [];
        const isPickup = uniqueMethods.some(m => PICKUP_TYPES.has((m || '').toUpperCase()));
        const shipByLoc = new Map();
        for (const e of shipEntries) {
          if (!shipByLoc.has(e.shopify_location_id)) shipByLoc.set(e.shopify_location_id, e.name);
        }
        const renderRow = (name, key, pickup) => {
          if (pickup) {
            const label = name || 'Pickup in store';
            const color = storeColors[label];
            return (
              <div key={key} className="ots-delivery-row">
                🏪
                {color ? (
                  <span style={{
                    display: 'inline-block', padding: '1px 7px', borderRadius: 10,
                    fontSize: 11, fontWeight: 600, backgroundColor: color.background, color: color.text,
                  }}>{label}</span>
                ) : <span style={{ fontSize: 12 }}>{label}</span>}
              </div>
            );
          }
          return (
            <div key={key} className="ots-delivery-row">
              🚚 <span style={{ fontSize: 12 }}>{name}</span>
            </div>
          );
        };
        if (!emLocId && perLoc.length > 1) {
          return perLoc.map((e, i) => {
            const label = shipByLoc.get(e.shopify_location_id)
              || (isPickup ? (fo.pickup_store_name || 'Pickup in store') : 'Shipping');
            return renderRow(label, i, isPickup);
          });
        }
        // Single-loc / emulated: original behavior — one row per delivery method.
        return (
          <>
            {uniqueMethods.map(m => {
              const pickup = PICKUP_TYPES.has((m || '').toUpperCase());
              if (pickup) {
                const label = fo.pickup_store_name || DELIVERY_LABELS[m] || m || 'Pickup in store';
                return renderRow(label, m, true);
              }
              const filteredShip = emLocId
                ? shipEntries.filter(e => e.shopify_location_id === emLocId).map(e => e.name)
                : shipEntries.map(e => e.name);
              const labels = filteredShip.filter(Boolean).length
                ? filteredShip.filter(Boolean)
                : [DELIVERY_LABELS[m] || m || 'Shipping'];
              return labels.map((lbl, idx) => renderRow(lbl, `${m}-${idx}`, false));
            })}
          </>
        );
      }
      case 'club':
        return o.club || <span className="ots-muted">—</span>;
      case 'teams': {
        // TP team overrides (Settings → Team Portal): titles the cache writer
        // substituted with the player's CURRENT TP team render red.
        let ovTeams = [];
        try {
          const f = fo.tp_override_flags_json ? JSON.parse(fo.tp_override_flags_json) : null;
          ovTeams = (f && Array.isArray(f.teams)) ? f.teams : [];
        } catch { ovTeams = []; }
        return clubteams.length === 0
          ? <span className="ots-muted">—</span>
          : clubteams.map((t, i) => (
              <div key={i} style={ovTeams.includes(t)
                  ? { color: '#c1281c', fontWeight: 600 } : undefined}>
                {t}
              </div>
            ));
      }
      case 'player_names': {
        let players = [];
        try {
          const raw = fo.players_json;
          players = raw ? JSON.parse(raw) : [];
        } catch { players = []; }
        if (!Array.isArray(players) || players.length === 0) {
          return <span className="ots-muted">—</span>;
        }
        return players.map((p, i) => <div key={i}>{p}</div>);
      }
      case 'player_numbers': {
        // Index-aligned with player_names — same row height so the number
        // stacks under its player. NBSP for players with no number so the
        // row still occupies visual space. Whole column shows "—" when no
        // player on this order has any assigned number.
        let numbers = [];
        try {
          const raw = fo.player_numbers_json;
          numbers = raw ? JSON.parse(raw) : [];
        } catch { numbers = []; }
        if (!Array.isArray(numbers) || numbers.length === 0
            || numbers.every(n => !n)) {
          return <span className="ots-muted">—</span>;
        }
        // TP number overrides: numbers the cache writer substituted with the
        // player's CURRENT TP number render red. Flags carry player NAMES;
        // numbers are index-aligned with players_json.
        let ovNames = [], ovPlayerNames = [];
        try {
          const f = fo.tp_override_flags_json ? JSON.parse(fo.tp_override_flags_json) : null;
          ovNames = (f && Array.isArray(f.numbers)) ? f.numbers : [];
        } catch { ovNames = []; }
        if (ovNames.length) {
          try { ovPlayerNames = fo.players_json ? JSON.parse(fo.players_json) : []; } catch { ovPlayerNames = []; }
        }
        return numbers.map((n, i) => (
          <div key={i} style={ovNames.length && ovPlayerNames[i] && ovNames.includes(ovPlayerNames[i])
              ? { color: '#c1281c', fontWeight: 600 } : undefined}>
            {n || ' '}
          </div>
        ));
      }
      case 'source':
        // Amazon FBM order routed to a store by the FBM Report (tag FBM-Routed), 2026-10-14.
        if (isAmazonFbm(o)) return <Badge tone={isFbmRouted(o) ? 'success' : 'info'}>Amazon FBM</Badge>;
        return <SourceBadge source={o.source || fo.source} />;
      case 'is_custom':
        return fo.is_custom === true ? <Badge tone="success">Yes</Badge>
          : fo.is_custom === false ? <Badge>No</Badge>
          : <span className="ots-muted">—</span>;
      case 'store_status': {
        const ss = fo.store_status ?? 0;
        if (!ss) return <span className="ots-muted">—</span>;
        const pct = parseInt(fo.store_status_pct, 10) || 0;
        const lbl = formatStoreStatus(ss, pct);
        // Tone by STAGE, not by pct completion:
        //   Picked Up (3) at 100 pct → success (green) — done
        //   Picked Up (3) at <100    → info (light blue) — SP-300486 case:
        //                              1 item collected, more still RFP; visually
        //                              belongs with the RFP bucket (still active)
        //   Ready For Pickup (2)     → info (light blue)
        //   In Transit (1)           → attention (yellow)
        let tone = 'attention';
        if (ss === 3 && pct >= 100) tone = 'success';
        else if (ss === 3 || ss === 2) tone = 'info';
        return <Badge tone={tone}>{lbl}</Badge>;
      }
      case 'bulk_tracking': {
        const list = trackingEntries(fo);
        const distinct = dedupeTrackingEntries(list);
        // 2026-09-10 (SP-355456): on All-Locations with 2+ locs, one line
        // per per_loc_status entry — SAME order/alignment as the Status /
        // Print / Pick / Press / Pack columns — so the tracking number that
        // shipped from a location renders on that location's line, not
        // whichever line happens to render first. Single-loc/emulated:
        // unchanged per-number lines.
        const emLocId = emulating?.shopify_location_id
          ? String(emulating.shopify_location_id) : null;
        const perLoc = Array.isArray(fo.per_loc_status) ? fo.per_loc_status : [];
        if (!emLocId && perLoc.length > 1) {
          const { lines, unassigned } = trackingByLocation(distinct, perLoc);
          const renderNumbers = (entries) => entries.length
            ? entries.map((e, j) => (
                <React.Fragment key={e.number || j}>
                  {j > 0 && ', '}
                  {e.url
                    ? <a href={e.url} target="_blank" rel="noreferrer">{e.number}</a>
                    : e.number}
                </React.Fragment>
              ))
            : <span className="ots-muted">—</span>;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {lines.map((entries, i) => (
                <div className="ots-mono ots-track-line" key={i}>{renderNumbers(entries)}</div>
              ))}
              {unassigned.length > 0 && (
                <div className="ots-mono ots-track-line" key="unassigned">{renderNumbers(unassigned)}</div>
              )}
            </div>
          );
        }
        if (!list.length) return <span className="ots-muted">—</span>;
        return (
          <>
            {distinct.map((e, i) => (
              <div className="ots-mono ots-track-line" key={e.number || i}>
                {e.url
                  ? <a href={e.url} target="_blank" rel="noreferrer">{e.number}</a>
                  : e.number}
              </div>
            ))}
          </>
        );
      }
      case 'delivery_date': {
        const list = trackingEntries(fo);
        const distinct = dedupeTrackingEntries(list);
        const lineCls = (entries) => {
          // A location line's cell class is driven by its own entries: bad
          // (exception/returned) if any entry there is; muted if every
          // entry there is muted-worthy (or the line is empty, which
          // renders the same '—' as the muted single-loc case).
          if (!entries.length) return ['ots-track-line', 'ots-muted'];
          const bad = entries.some(e => e.status === 'exception' || e.status === 'returned');
          const texts = entries.map(trackingLineText);
          const muted = texts.every(t => t === '—' || t === 'Awaiting UPS scan');
          return ['ots-track-line', bad ? 'ots-track-bad' : '', muted ? 'ots-muted' : ''].filter(Boolean);
        };
        const emLocId = emulating?.shopify_location_id
          ? String(emulating.shopify_location_id) : null;
        const perLoc = Array.isArray(fo.per_loc_status) ? fo.per_loc_status : [];
        if (!emLocId && perLoc.length > 1) {
          const { lines, unassigned } = trackingByLocation(distinct, perLoc);
          const renderLine = (entries, key) => (
            <div className={lineCls(entries).join(' ')} key={key}>
              {entries.length ? entries.map(trackingLineText).join(', ') : '—'}
            </div>
          );
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {lines.map((entries, i) => renderLine(entries, i))}
              {unassigned.length > 0 && renderLine(unassigned, 'unassigned')}
            </div>
          );
        }
        if (!list.length) return <span className="ots-muted">—</span>;
        return (
          <>
            {distinct.map((e, i) => {
              const text = trackingLineText(e);
              const bad = e.status === 'exception' || e.status === 'returned';
              const muted = text === '—' || text === 'Awaiting UPS scan';
              const cls = ['ots-track-line', bad ? 'ots-track-bad' : '', muted ? 'ots-muted' : ''].filter(Boolean).join(' ');
              return <div className={cls} key={e.number || i}>{text}</div>;
            })}
          </>
        );
      }
      case 'crm':
        return fo.teamportal_crm || <span className="ots-muted">—</span>;
      case 'protect_status': {
        // Shopify Protect coverage chip. Server exposes fo.protect_status
        // as the raw Shopify enum. Missing = shop has no Protect app or
        // the row pre-dates the column — render em-dash.
        const ps = fo.protect_status;
        if (!ps) return <span className="ots-muted">—</span>;
        // Polaris v12 tones: success (green), info (light-green vibe),
        // warning (orange), attention (yellow), critical (red).
        // ACTIVE      → light green  → 'success-subdued' via progress='partiallyComplete'
        // PROTECTED   → green
        // NOT_PROTECTED → orange
        // PENDING     → yellow
        // INACTIVE    → red
        let tone = 'info', label = ps;
        if      (ps === 'PROTECTED')     { tone = 'success';      label = 'Protected'; }
        else if (ps === 'ACTIVE')        { tone = 'success';      label = 'Active'; }
        else if (ps === 'NOT_PROTECTED') { tone = 'warning';      label = 'Not Protected'; }
        else if (ps === 'PENDING')       { tone = 'attention';    label = 'Pending'; }
        else if (ps === 'INACTIVE')      { tone = 'critical';     label = 'Inactive'; }
        // Distinguish ACTIVE (light green) from PROTECTED (solid green) —
        // Polaris Badge exposes `progress` for this. `partiallyComplete`
        // renders a paler shade of the same tone, giving us the two
        // greens the spec asks for without a custom component.
        const progress = ps === 'ACTIVE' ? 'partiallyComplete' : 'complete';
        return <Badge tone={tone} progress={progress}>{label}</Badge>;
      }
      case 'risk_level': {
        // Shopify order-risk chip. Server exposes fo.risk_level as the raw
        // enum (LOW/MEDIUM/HIGH/PENDING/NONE). NULL = pre-backfill row —
        // render em-dash. NONE = Shopify has no provider or declined to
        // score — render a plain grey "None" chip so the operator sees
        // it's been evaluated with no signal.
        const rl = fo.risk_level;
        if (!rl) return <span className="ots-muted">—</span>;
        // Polaris Badge with no `tone` renders the neutral grey chip we
        // want for NONE.
        if (rl === 'NONE') return <Badge>None</Badge>;
        let tone = 'info', label = rl;
        if      (rl === 'LOW')     { tone = 'success';   label = 'Low'; }
        else if (rl === 'MEDIUM')  { tone = 'warning';   label = 'Medium'; }
        else if (rl === 'HIGH')    { tone = 'critical';  label = 'High'; }
        else if (rl === 'PENDING') { tone = 'info';      label = 'Pending'; }
        return <Badge tone={tone}>{label}</Badge>;
      }
      case 'total':
        return `$${parseFloat(o.total?.amount || 0).toFixed(2)}`;
      case 'net_sales':
        return fo.subtotal_price != null
          ? `$${parseFloat(fo.subtotal_price).toFixed(2)}`
          : <span className="ots-muted">—</span>;
      case 'shipping':
        return fo.total_shipping != null
          ? `$${parseFloat(fo.total_shipping).toFixed(2)}`
          : <span className="ots-muted">—</span>;
      case 'tax':
        return fo.total_tax != null
          ? `$${parseFloat(fo.total_tax).toFixed(2)}`
          : <span className="ots-muted">—</span>;
      case 'notes':
        return o.note || <span className="ots-muted">—</span>;
      case 'tags':
        return (o.tags || []).slice(0, 3).join(', ') || <span className="ots-muted">—</span>;
      case 'reworked':
        return fo.reworked_count > 0
          ? <Badge tone="critical">{fo.reworked_count}</Badge>
          : <span className="ots-muted">—</span>;
      case 'total_reworks':
        return fo.total_reworks_count > 0
          ? <Badge tone="warning">{fo.total_reworks_count}</Badge>
          : <span className="ots-muted">—</span>;
      case 'notifications_count':
        return fo.notifications_count > 0
          ? <Badge tone="critical">{fo.notifications_count}</Badge>
          : <span className="ots-muted">—</span>;
      default:
        return <span className="ots-muted">—</span>;
    }
  };

  // ── Table card height ──────────────────────────────────────────────────────
  // Reserve viewport space for the pagination footer + a margin so the entire
  // page (header + toolbar + wrap + footer) fits without page-content needing
  // to scroll. With page-content not scrolling, the page header literally
  // cannot scroll either.
  const PAGINATION_RESERVE_PX = 90;  // pagination ~40 + footer margin + safety
  const tableCardRef = useRef(null);
  // Paging (Next / Prev / a typed page): start the new page at its first row, not wherever the last one was
  // scrolled to (Eric, 2026-10-18). Skipped on first render so a deep link / refresh keeps its own scroll.
  const _prevPageNumRef = useRef(pageNum);
  useEffect(() => {
    if (_prevPageNumRef.current === pageNum) return;
    _prevPageNumRef.current = pageNum;
    const el = tableCardRef.current;
    if (el) el.scrollTop = 0;
    try { window.scrollTo(0, 0); } catch (_e) { /* non-browser */ }
  }, [pageNum]);
  const [tableHeight, setTableHeight] = useState(null);
  useEffect(() => {
    const compute = () => {
      const el = tableCardRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setTableHeight(Math.max(280, window.innerHeight - top - PAGINATION_RESERVE_PX - 16));
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // ── Drag state for visual indicator ───────────────────────────────────────
  const [draggingColId, setDraggingColId] = useState(null);
  // Only one filter popover open at a time. Clicking another funnel auto-closes
  // any currently-open one (the headers swap which one matches openFunnelId).
  const [openFunnelId, setOpenFunnelId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="ots-page">

      {/* Toast for print restriction */}
      {printToast && (
        <div key={printToast.key} className="opshub-toast" role="alert" aria-live="assertive">
          <Banner tone="warning" onDismiss={() => setPrintToast(null)}>
            <Text as="p">{printToast.msg}</Text>
          </Banner>
        </div>
      )}

      {/* Bulk notification toast — success / "wrong scope" warning. Uses the
          same Banner-in-a-div pattern as printToast above because this app
          mounts AppProvider without a <Frame>, and Polaris's <Toast> throws
          "No Frame provided" → blank screen. */}
      {bulkNotifToast && (
        <div key={bulkNotifToast.key} className="opshub-toast" role="alert" aria-live="polite">
          <Banner tone="success" onDismiss={() => setBulkNotifToast(null)}>
            <Text as="p">{bulkNotifToast.msg}</Text>
          </Banner>
        </div>
      )}

      {/* Bulk notification composer modal — simple textarea, no scope/store
          fields (those are derived from the selected orders by the backend). */}
      <Modal
        open={bulkNotifOpen}
        onClose={() => { if (!bulkNotifSaving) setBulkNotifOpen(false); }}
        title={`Notify ${selectedIds.size} selected orders`}
        primaryAction={{
          content: 'Set Notification',
          onAction: handleBulkNotificationSubmit,
          loading: bulkNotifSaving,
          disabled:
            bulkNotifSaving
            || !bulkNotifMessage.trim()
            || bulkNotifMessage.length > 300,
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setBulkNotifOpen(false),
          disabled: bulkNotifSaving,
        }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {bulkNotifError && (
              <Banner tone="critical" onDismiss={() => setBulkNotifError(null)}>
                {bulkNotifError}
              </Banner>
            )}
            <TextField
              label={`Notification message (${300 - bulkNotifMessage.length} chars left)`}
              value={bulkNotifMessage}
              onChange={v => setBulkNotifMessage(v.slice(0, 300))}
              multiline={4}
              maxLength={300}
              autoComplete="off"
              autoFocus
              error={bulkNotifMessage.length > 300 ? 'Too long' : undefined}
              helpText={`This message will appear on all ${selectedIds.size} selected orders.`}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Bulk Print Labels modal */}
      {BULK_LABELS_ENABLED && bulkLabelsOpen && (
        <BulkLabelsModal
          open={bulkLabelsOpen}
          onClose={() => setBulkLabelsOpen(false)}
          orderIds={Array.from(selectedIds).map(gid => String(gid).split('/').pop())}
          storeFetch={storeFetch}
          activeStore={activeStore}
          urlShopifyLocationId={searchParams.get('loc') || null}
        />
      )}

      {/* Hidden print clone */}
      {printReadyOrders && (
        <div ref={printSourceRef} className="print-clone" aria-hidden="true">
          {printReadyOrders.map(({ fo, fullOrder, bins, overstockBins, numberPreviews, skuNotifications, merged }, idx) => (
            <React.Fragment key={fo.order?.id || idx}>
              {merged ? (
                <MergedNjPrint fo={fo} fullOrder={fullOrder} locs={merged.locs} numberPreviews={numberPreviews || {}} skuNotifications={skuNotifications || {}}
                  storeKey={activeStore || 'soccerpost'} storeDisplayName={STORE_DISPLAY_NAMES?.[activeStore] || 'Soccer Post'} printoutFooterHtml={printoutFooterHtml} />
              ) : (
              <OrderPrintCard fo={fo} fullOrder={fullOrder} bins={bins || {}} overstockBins={overstockBins || {}} numberPreviews={numberPreviews || {}} skuNotifications={skuNotifications || {}}
                locationId={(() => {
                  // Print scope priority:
                  //   1. emulating.shopify_location_id (admin or non-admin emulation)
                  //   2. exactly one location selected in the LIST location filter
                  //   3. for non-admin/non-customer_service: their single allowed
                  //      shopify_location_id on the active store (auto-scope)
                  //   4. null = unscoped (admin/customer_service viewing all)
                  if (emulating?.shopify_location_id) return String(emulating.shopify_location_id);
                  if (locations?.length === 1) return String(locations[0]);
                  if (!isAdmin) {
                    const myLocs = (user?.locations || [])
                      .filter(l => (l.store_key || 'soccerpost') === (activeStore || 'soccerpost')
                                && l.shopify_location_id && String(l.shopify_location_id) !== '0');
                    if (myLocs.length === 1) return String(myLocs[0].shopify_location_id);
                  }
                  return null;
                })()}
                storeKey={activeStore || 'soccerpost'}
                storeDisplayName={STORE_DISPLAY_NAMES?.[activeStore] || 'Soccer Post'}
                printoutFooterHtml={printoutFooterHtml}
              />
              )}
              {idx < printReadyOrders.length - 1 && <div className="print-page-break" />}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Sort-mode picker — shown right after "Print selected". "Sort by
          Order" prints in order-name order (default); "Collate by Club"
          groups every selected order by its club metafield so a bulk print
          across 9 clubs prints all of A, then all of B, etc. */}
      {printSortChoice && (() => {
        const count = printSortChoice.orders?.length || 0;
        const clubCount = new Set(
          (printSortChoice.orders || []).map(fo => (fo.order?.club || '').trim())
                                        .filter(Boolean)
        ).size;
        const proceed = (mode) => {
          const sorted = _sortPrintOrders(printSortChoice.orders, mode);
          setPrintSortChoice(null);
          triggerPrint(sorted);
        };
        return (
          <Modal open
            title={`Print ${count} order${count === 1 ? '' : 's'}`}
            onClose={() => setPrintSortChoice(null)}
            primaryAction={{ content: 'Sort by Order',    onAction: () => proceed('order') }}
            secondaryActions={[
              { content: 'Collate by Club', onAction: () => proceed('club') },
              { content: 'Cancel',          onAction: () => setPrintSortChoice(null) },
            ]}
          >
            <Modal.Section>
              <BlockStack gap="200">
                <Text variant="bodyMd">
                  How should the {count} selected order{count === 1 ? '' : 's'} be arranged in the print output?
                </Text>
                <Text tone="subdued" variant="bodyMd">
                  <strong>Sort by Order</strong> — prints in order-name order (SP-…, SUR-…).
                </Text>
                <Text tone="subdued" variant="bodyMd">
                  <strong>Collate by Club</strong> — groups by club metafield so all
                  {clubCount ? ` ${clubCount} clubs` : ' clubs'} print in blocks
                  (Club A, then Club B, then Club C …). Orders with no
                  club fall to the end.
                </Text>
                              {njPrintOffer && (
                  <div style={{ marginTop: 4, paddingTop: 10, borderTop: '1px solid #e1e3e5' }}>
                    <Checkbox
                      label="Merge NJ Warehouses"
                      helpText="One ticket per order: the Tinton Falls Warehouse items first, a dividing line, then the Eatontown Warehouse items. The order is tagged as printed at each warehouse that had items on the ticket. Orders with items at only one of the two print as usual."
                      checked={printMergeNj}
                      onChange={setMergeNj}
                    />
                  </div>
                )}
</BlockStack>
            </Modal.Section>
          </Modal>
        );
      })()}

      {/* On-hold confirm modal — listed orders cannot be printed; user must
          either cancel or drop them and proceed with the rest. */}
      {printOnHoldOrders && (() => {
        const heldIds   = new Set(printOnHoldOrders.onHold.map(fo => fo.order?.id));
        const remaining = printOnHoldOrders.all.filter(fo => !heldIds.has(fo.order?.id));
        const heldCount = printOnHoldOrders.onHold.length;
        const isMulti = heldCount > 1;
        // HARD BLOCK on held orders — no "Print anyway" escape. The user can
        // either skip the held ones (Print remaining N) or cancel.
        const printRemaining = () => {
          setPrintOnHoldOrders(null);
          if (remaining.length === 0) return;
          triggerPrint(remaining);
        };
        const primaryAction = remaining.length > 0
          ? { content: `Print remaining ${remaining.length}`, onAction: printRemaining }
          : { content: 'OK', onAction: () => setPrintOnHoldOrders(null) };
        const secondaryActions = remaining.length > 0
          ? [{ content: 'Cancel', onAction: () => setPrintOnHoldOrders(null) }]
          : undefined;
        return (
          <Modal open title={isMulti ? `${heldCount} orders on hold` : 'Order is on hold'}
            onClose={() => setPrintOnHoldOrders(null)}
            primaryAction={primaryAction}
            secondaryActions={secondaryActions}
          >
            <Modal.Section>
              <BlockStack gap="300">
                <Text variant="bodyMd" tone="critical">
                  {isMulti
                    ? <>These orders are <strong>on hold in Shopify</strong> and cannot be printed. Release the hold in Shopify, then come back to print.</>
                    : <>This order is <strong>on hold in Shopify</strong> and cannot be printed. Release the hold in Shopify, then come back to print.</>}
                </Text>
                <BlockStack gap="100">
                  {printOnHoldOrders.onHold.map((fo, idx) => (
                    <Text key={idx} variant="bodyMd" fontWeight="semibold">{fo.order?.name || '—'}</Text>
                  ))}
                </BlockStack>
              </BlockStack>
            </Modal.Section>
          </Modal>
        );
      })()}

      {/* Throttle-abort modal — surfaces the fatal "one or more orders
          couldn't load" case for bulk print. See executePrint(): if ANY
          per-order fetch fails after retries, we hard-abort the whole
          print — no window.print(), no addPrintTags, no event-log rows.
          Message text is fixed per operator request 2026-08-17. */}
      {printFailureModal && (
        <Modal open title="Shopify is throttling. Please retry again."
          onClose={() => setPrintFailureModal(null)}
          primaryAction={{ content: 'OK', onAction: () => setPrintFailureModal(null) }}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text variant="bodyMd" tone="critical">
                <strong>{printFailureModal.count}</strong> of {printFailureModal.total} orders
                failed to load. <strong>No orders were printed</strong> and
                <strong> no orders were marked as printed</strong>. Please retry the
                bulk print in a few seconds — Shopify's rate limit will clear.
              </Text>
              {printFailureModal.failures && printFailureModal.failures.length > 0 && (
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">
                    Affected orders (first {Math.min(printFailureModal.failures.length, 25)}):
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    {printFailureModal.failures.map(f => f.name || (f.id || '').split('/').pop()).filter(Boolean).join(', ')}
                    {printFailureModal.count > printFailureModal.failures.length ? ' …' : ''}
                  </Text>
                </BlockStack>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}

      {/* Location-emulation guardrail modal. Fires when the user tries to
          bulk-print with access to multiple locations but no emulation set.
          Root fix for the silent-tag-drop bug (Matt Coleman, 2026-08-13,
          79 of 177 orders paper-printed without OpsHubPrinted tag). */}
      {printNeedsLocation != null && (
        <Modal open
          title="Pick a location before printing"
          onClose={() => setPrintNeedsLocation(null)}
          primaryAction={{ content: 'OK', onAction: () => setPrintNeedsLocation(null) }}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text variant="bodyMd">
                You have <strong>{printNeedsLocation}</strong> order{printNeedsLocation !== 1 ? 's' : ''} selected,
                but you're viewing <strong>All Locations</strong>. Bulk-printing
                without a specific location leads to orders being paper-printed
                but not tagged as <em>OpsHubPrinted</em>.
              </Text>
              <Text variant="bodyMd">
                Use the location picker at the top of the page to emulate a
                specific location (e.g. Teams Lancaster), then try Print again.
                Your selection is preserved.
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}

      {/* Already-printed confirm modal */}
      {printConfirmOrders && (() => {
        const total   = printConfirmOrders?.length || 0;
        const alreadyN = printAlreadyPrinted.length;
        const allPrinted = alreadyN >= total && total > 0;
        return (
        <Modal open title={allPrinted ? 'All orders already printed' : 'Some orders already printed'}
          onClose={() => setPrintConfirmOrders(null)}
          primaryAction={{ content: 'Print anyway', onAction: () => executePrint(printConfirmOrders) }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setPrintConfirmOrders(null) }]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text variant="bodyMd">
                Printing <strong>{total}</strong> order{total !== 1 ? 's' : ''} total.{' '}
                {allPrinted
                  ? <>All <strong>{total}</strong> have already been printed — print again?</>
                  : <>The following <strong>{alreadyN}</strong> have already been printed — print again?</>}
              </Text>
              <BlockStack gap="100">
                {printAlreadyPrinted.map((fo, idx) => {
                  // Prefer local fo.order.name when the row was hydrated
                  // on this page; fall back to the backend `names` map for
                  // selections that came from OTHER pages (where the local
                  // shell has no name); fall back to the numeric ID last.
                  const nm = fo.order?.name
                          || printedNamesByGid[fo.order?.id]
                          || (fo.order?.id || '').split('/').pop()
                          || '—';
                  return (
                    <Text key={idx} variant="bodyMd" fontWeight="semibold">{nm}</Text>
                  );
                })}
              </BlockStack>
            </BlockStack>
          </Modal.Section>
        </Modal>
        );
      })()}

      {/* Page header */}
      <div className="ots-header sticky-page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
        <h1 className="ots-title page-title">
          Orders
          {isVirtual && <span className="ots-virtual-badge">Virtual</span>}
        </h1>
        <p className="ots-subtitle page-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>
          {(() => {
            if (emulating) return emulating.name;
            // 2026-07-07: header should always match the sidebar's
            // "All Locations" chip when no emulation is active — both
            // admin and non-admin. Previously admin fell to the store
            // display name (e.g. "B2B"), which mismatched the sidebar.
            if (isAdmin) return 'All Locations';
            // 2026-07-07: non-admin with no emulation. If they have >1 loc
            // on the current store they're viewing "All Locations" — say
            // so instead of dropping to a single loc's name. If they have
            // exactly one loc, prefer THAT (single-loc auto-emulation
            // usually beats us here, but a first-render race can still
            // land here before emulating settles). Never fall back to the
            // JWT-primary user.location_name — it can be for a different
            // store entirely (eric+warehouse on b2b used to show
            // "Eatontown Warehouse (Virtual)" here, a soccerpost loc).
            const currentStoreLocs = (user?.locations || []).filter(l => l.store_key === activeStore);
            if (currentStoreLocs.length > 1) return 'All Locations';
            return currentStoreLocs[0]?.name || '';
          })()}
          </span>
          <LocStatusChips
            storeFetch={storeFetch}
            activeStore={activeStore}
            shopifyLocationId={emulating?.shopify_location_id || 'all'}
            retailActive={retailStore}
            onRetailToggle={() => setRetailStore(v => !v)}
          />
        </p>
        </div>

        {/* Row-highlight legend — 2026-07-25. Explains what each background
            color on an order row means. Colors mirror the CSS rules in
            orders-tanstack.css (has-notifications / has-sku-notifications).
            Combos render as diagonal stripes between the two
            colors. Order-notification is intentionally listed
            first — matches the CSS load order + operator escalation
            priority (an order-scoped notification is CS-critical). */}
        <div className="ots-row-legend" role="note" aria-label="Row highlight legend"
             style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
                      padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8,
                      background: '#fff', fontSize: 12, color: '#374151' }}>
          <span style={{ fontWeight: 600, color: '#6d7175' }}>Row highlights:</span>
          {[
            { bg: '#fde8e8', border: '#fbb',    label: 'Order notification' },
            { bg: '#fef7d1', border: '#e6d180', label: 'SKU notification' },
            { bg: '#c9def7', border: '#6ea8f0', label: 'Retail order unfulfilled at a store', pulse: true },
            { bg: '#cdeedb', border: '#7fcf9c', label: 'Amazon FBM order to ship from this store', shimmer: true },
          ].map(x => (
            <span key={x.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span aria-hidden="true" className={x.pulse ? 'ots-retail-pulse' : x.shimmer ? 'ots-fbm-shimmer' : undefined} style={{
                display: 'inline-block', width: 14, height: 14, borderRadius: 3,
                background: x.bg, border: `1px solid ${x.border}`,
              }} />
              {x.label}
            </span>
          ))}
          <span style={{ color: '#6d7175', fontStyle: 'italic' }}>
            Combined → diagonal stripes
          </span>
        </div>
      </div>

      <div className="ots-card">

        {/* ── Toolbar: search + location + actions ─────────────────────── */}
        <div className="ots-toolbar">
          {/* Search input — debounced /api/orders/search, overrides filter list */}
          <div className="ots-search-wrap">
            <input
              type="text"
              className="ots-search-input"
              placeholder="Search by customer, order, email…"
              value={searchQ}
              onChange={handleSearchChange}
              autoComplete="off"
            />
            {searchQ && (
              <button
                type="button"
                className="ots-search-clear"
                onClick={clearSearch}
                aria-label="Clear search"
                title="Clear"
              >
                ×
              </button>
            )}
          </div>

          {/* Location selector (admin only) */}
          {isAdmin && !emulating && activeStore !== 'all' && availableLocations.length > 0 && (
            <select
              className="ots-location-select"
              value={locations[0] || ''}
              onChange={e => setLocations(e.target.value ? [e.target.value] : [])}
            >
              <option value="">All locations</option>
              {availableLocations.map(l => (
                <option key={l.shopify_location_id} value={l.shopify_location_id}>{l.name}</option>
              ))}
            </select>
          )}

          {/* Advanced filter popover — sits right of the All-Locations
              dropdown when admin sees one, else immediately right of
              the search input. Drives the same filterGetSet state that
              the per-column header pills drive (apply-as-you-go), so
              the URL, header pills, and panel always agree. */}
          <AdvancedFilters
            filterGetSet={filterGetSet}
            choicesById={{
              club:     dynamicChoices.club,
              teams:    dynamicChoices.teams,
              crm:      dynamicChoices.crm,
              pickup_store: dynamicChoices.pickup_store,
              delivery: dynamicChoices.delivery,
            }}
            // Drives the rail's column order so it always matches the table's
            // visible left→right column order. Hidden columns still appear in
            // the rail (operators may want to filter on a hidden column).
            colOrder={colOrder}
            onResetAll={clearAllFilters}
            activeCount={[
              statuses, clubs, teams, printeds,
              pickStatuses, pressStatuses, packStatuses,
              deliveryMethods, deliveryNames, pickupStores, storeStatuses,
              isCustoms, crms, sources, retailStore, protectStatuses, riskLevels, deliveryStatuses,
            ].filter(arr => Array.isArray(arr) && arr.length > 0).length
             + (retailStore ? 1 : 0)
             + ((dateFrom || dateTo) ? 1 : 0)
             + ((fulfillByFrom || fulfillByTo) ? 1 : 0)
             + (sku.trim() ? 1 : 0)}
          />

          {/* Include Archived toggle — pressed = include closed orders. */}
          <Button
            size="slim"
            pressed={includeArchived}
            onClick={() => setIncludeArchived(v => !v)}
          >
            Include Archived
          </Button>

          {/* Save current view → opens name modal */}
          <Button size="slim" onClick={() => setSaveModalOpen(true)}>
            Save
          </Button>

          {/* Saved Views dropdown — hidden when empty */}
          <SavedViewsDropdown
            views={savedViews}
            onLoad={handleLoadView}
            onSetDefault={handleSetDefaultView}
            onDelete={handleDeleteView}
          />

          <div className="ots-toolbar-spacer" />

          <div className="ots-toolbar-actions">
            {/* Refresh */}
            <Button size="slim" onClick={refresh} disabled={fetching}>
              ↻ Refresh
            </Button>

            {/* Columns picker */}
            <Popover
              active={colPickerOpen}
              activator={
                <Button size="slim" disclosure onClick={() => setColPickerOpen(v => !v)}>
                  Columns
                </Button>
              }
              onClose={() => setColPickerOpen(false)}
              preferredAlignment="right"
            >
              <div className="ots-col-picker">
                <div className="ots-col-picker-item ots-col-picker-header">
                  <label style={{ flex: 1 }}>
                    <input
                      type="checkbox"
                      checked={allToggleableCols.every(c => !hiddenCols.has(c.id))}
                      onChange={() => {
                        const allVis = allToggleableCols.every(c => !hiddenCols.has(c.id));
                        const next = allVis
                          ? new Set(allToggleableCols.map(c => c.id))
                          : new Set();
                        setHiddenCols(next);
                        saveHiddenCols(next);
                      }}
                    />
                    <strong>{allToggleableCols.every(c => !hiddenCols.has(c.id)) ? 'Hide all' : 'Show all'}</strong>
                  </label>
                  <button
                    type="button"
                    className="ots-col-picker-reset"
                    onClick={() => {
                      // Reset visibility: only the FORCE_HIDE defaults remain hidden.
                      const FORCE_HIDE = new Set(['notes', 'tags', 'pickup_store']);
                      setHiddenCols(FORCE_HIDE);
                      saveHiddenCols(FORCE_HIDE);
                      // Reset order to canonical default
                      setColOrder(DEFAULT_COLUMN_ORDER);
                      saveColOrder(DEFAULT_COLUMN_ORDER);
                    }}
                    title="Reset columns to defaults"
                  >
                    Reset
                  </button>
                </div>
                <div className="ots-col-picker-divider" />
                {allToggleableCols.map(def => (
                  <div
                    key={def.id}
                    className="ots-col-picker-item"
                    draggable
                    onDragStart={e => handleColDragStart(e, def.id)}
                    onDragOver={handleColDragOver}
                    onDrop={e => handleColDrop(e, def.id)}
                    onDragEnd={handleColDragEnd}
                  >
                    <span className="ots-col-picker-drag-handle" title="Drag to reorder">⠿</span>
                    <label>
                      <input
                        type="checkbox"
                        checked={!hiddenCols.has(def.id)}
                        onChange={() => {
                          setHiddenCols(prev => {
                            const next = new Set(prev);
                            if (next.has(def.id)) next.delete(def.id); else next.add(def.id);
                            saveHiddenCols(next);
                            return next;
                          });
                        }}
                      />
                      {def.label}
                    </label>
                  </div>
                ))}
              </div>
            </Popover>

            {/* Export to CSV — sits to the right of the Columns picker */}
            <Button size="slim" onClick={onExportClick} disabled={exporting}>
              {exporting
                ? `Exporting… ${exportProgress.done.toLocaleString()}${exportProgress.total ? `/${exportProgress.total.toLocaleString()}` : ''}`
                : 'Export'}
            </Button>
          </div>
        </div>

        {/* Save-current-view name modal */}
        <SaveViewModal
          open={saveModalOpen}
          onClose={() => setSaveModalOpen(false)}
          onSave={saveCurrentView}
          existingNames={savedViews.map(v => v.name)}
        />

        {/* Export confirmation modal — only when > 1000 rows */}
        {exportConfirmOpen && (
          <Modal
            open
            title="Export a lot of rows?"
            onClose={() => setExportConfirmOpen(false)}
            primaryAction={{ content: `Export ${(totalCount || 0).toLocaleString()} rows`, onAction: runExport }}
            secondaryActions={[{ content: 'Cancel', onAction: () => setExportConfirmOpen(false) }]}
          >
            <Modal.Section>
              <Text as="p">
                You're about to export <strong>{(totalCount || 0).toLocaleString()}</strong> orders matching the current filter to CSV. This may take a minute or two and will use the visible columns in their current order.
              </Text>
            </Modal.Section>
          </Modal>
        )}

        {/* ── Filter summary ───────────────────────────────────────────── */}
        {hasAnyFilter && (
          <div className="ots-filter-summary">
            <Button size="micro" onClick={clearAllFilters}>Reset filters</Button>
            {activeFilterSummary.map(chip => (
              <Tag key={chip.key} onRemove={chip.onRemove}>{chip.label}</Tag>
            ))}
          </div>
        )}

        {/* ── Bulk action bar ──────────────────────────────────────────── */}
        {selectedIds.size > 0 && (
          <div className="ots-bulk-bar">
            {/* Count + select-all dropdown */}
            <Popover
              active={selectMenuOpen}
              activator={
                <button
                  type="button"
                  className="ots-bulk-count-btn"
                  onClick={() => setSelectMenuOpen(v => !v)}
                  disabled={selectingAll}
                >
                  {selectingAll ? 'Selecting…' : `${selectedIds.size} selected`}
                  <span>▾</span>
                </button>
              }
              onClose={() => setSelectMenuOpen(false)}
              preferredAlignment="left"
            >
              <div className="ots-select-menu-popover">
                {totalCount !== null && totalCount > 500 ? (
                  <div className="ots-select-menu-item ots-select-menu-item--disabled">
                    Cannot select more than 500 — adjust filter
                  </div>
                ) : (
                  <button className="ots-select-menu-item" onClick={handleSelectAllFiltered} disabled={selectingAll}>
                    Select all {totalCount !== null ? totalCount.toLocaleString() : ''} orders with current filter
                  </button>
                )}
                <div className="ots-select-menu-divider" />
                <button className="ots-select-menu-item" onClick={() => { setSelectedIds(new Set()); setSelectMenuOpen(false); }}>
                  Unselect all
                </button>
              </div>
            </Popover>

            {/* Print selected */}
            {canPrint && (<div
              title={!printLocationInScope ? "You can't print orders when you are viewing \"All Locations\". Choose a single location first." : undefined}
              style={!printLocationInScope ? { opacity: 0.55, cursor: 'not-allowed', display: 'inline-block' } : { display: 'inline-block' }}
            >
              <Button
                size="slim"
                disabled={selectingAll || printFetching}
                onClick={() => {
                  if (!printLocationInScope) {
                    setPrintToast({
                      msg: "You can't print orders when you are viewing \"All Locations\". Choose a single location first.",
                      key: Date.now(),
                    });
                    return;
                  }
                  handlePrintSelected();
                }}
              >
                Print selected ({selectedIds.size})
              </Button>
            </div>)}

            {/* Bulk Print Labels — buy UPS/USPS shipping labels for every
                selected order with a shippable FO at this location. Same
                location gate as Print Selected: labels are purchased FROM a
                specific location. */}
            {BULK_LABELS_ENABLED && canPrint && <div
              title={!printLocationInScope ? "You can't buy labels when you are viewing \"All Locations\". Choose a single location first." : undefined}
              style={!printLocationInScope ? { opacity: 0.55, cursor: 'not-allowed', display: 'inline-block' } : { display: 'inline-block' }}
            >
              <Button
                size="slim"
                disabled={selectingAll}
                onClick={() => {
                  if (!printLocationInScope) {
                    setPrintToast({
                      msg: "You can't buy labels when you are viewing \"All Locations\". Choose a single location first.",
                      key: Date.now(),
                    });
                    return;
                  }
                  setBulkLabelsOpen(true);
                }}
              >
                Bulk Print Labels ({selectedIds.size})
              </Button>
            </div>}

            {/* Pick list selected — aggregates every line item across the
                selected orders by SKU into a single printable pick list.
                Same location-scope gate as Print Selected — bin codes are
                per-location so an unscoped pick list has no aisles. */}
            {canPrint && (<div
              title={!printLocationInScope ? "You can't generate a pick list when you are viewing \"All Locations\". Choose a single location first." : undefined}
              style={!printLocationInScope ? { opacity: 0.55, cursor: 'not-allowed', display: 'inline-block' } : { display: 'inline-block' }}
            >
              <Button
                size="slim"
                disabled={selectingAll || pickListFetching}
                loading={pickListFetching}
                onClick={() => {
                  if (!printLocationInScope) {
                    setPrintToast({
                      msg: "You can't generate a pick list when you are viewing \"All Locations\". Choose a single location first.",
                      key: Date.now(),
                    });
                    return;
                  }
                  handlePickListSelected();
                }}
              >
                Pick list selected ({selectedIds.size})
              </Button>
            </div>)}

            {/* Notification selected — broadcasts one message to N orders.
                Gated to roles that can manage notifications (same as Settings
                → Notifications tab). Disabled at All-Locations to keep
                selections single-store; the backend rejects spans anyway. */}
            {canBulkNotify && (
              <div
                title={activeStore === 'all'
                  ? 'Bulk notifications must target one store. Choose a store first.'
                  : undefined}
                style={activeStore === 'all'
                  ? { opacity: 0.55, cursor: 'not-allowed', display: 'inline-block' }
                  : { display: 'inline-block' }}
              >
                <Button
                  size="slim"
                  disabled={selectingAll || bulkNotifSaving}
                  onClick={() => {
                    if (activeStore === 'all') {
                      setBulkNotifToast({
                        msg: 'Bulk notifications must target one store. Choose a store first.',
                        key: Date.now(),
                      });
                      return;
                    }
                    setBulkNotifError(null);
                    setBulkNotifMessage('');
                    setBulkNotifOpen(true);
                  }}
                >
                  Notification selected ({selectedIds.size})
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Table ───────────────────────────────────────────────────── */}
        <div
          className={`ots-table-wrap${isVirtual ? ' ots-table-wrap--virtual' : ''}`}
          ref={tableCardRef}
          style={tableHeight ? { height: tableHeight } : undefined}
        >
          {fetching && (
            <div className="ots-progress-bar"><div className="ots-progress-bar__fill" /></div>
          )}

          {/* Always render the table shell so the column headers stay visible
              even when the current filter/sort combo returns zero rows or the
              request errored. Prior behavior swapped the whole table for a
              plain <div>, which hid the headers and broke the "share a URL
              with filters" flow — users pasting a filtered URL that happens
              to match no orders saw a blank page. Empty + error states are
              now emitted as a full-width row inside <tbody> below. */}
          {(
            <>
              {/* ── Shared table: contains thead (always) + tbody (Phase 1 only) ── */}
              <table className={`ots-table${isVirtual ? ' ots-table--virtual-head' : ''}`}>
                <thead ref={theadRef}>
                  <tr>
                    {/* Sticky checkbox header */}
                    <th className="ots-th ots-th--sticky-cb" data-colid="checkbox">
                      <div className="ots-cb-th">
                        <input
                          type="checkbox"
                          className="ots-cb"
                          checked={allPageSelected}
                          ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                          onChange={handleSelectAll}
                          onClick={e => e.stopPropagation()}
                          title="Select all on page"
                        />
                      </div>
                    </th>

                    {/* Data columns */}
                    {visibleColDefs.filter(d => d.id !== 'checkbox').map(def => {
                      const tsCol = table.getColumn(def.id);
                      const isFilterActive = (() => {
                        const fk = def.apiFilterKey;
                        if (!fk) return false;
                        if (fk === 'date') return !!(dateFrom || dateTo);
                        if (fk === 'fulfill_by') return !!(fulfillByFrom || fulfillByTo);
                        const val = filterGetSet[fk]?.get;
                        return Array.isArray(val) ? val.length > 0 : !!val;
                      })();

                      // Build filter props
                      const baseFilterProps = { isActive: isFilterActive };
                      let filterProps = baseFilterProps;

                      if (def.filterType === 'choice') {
                        const fk = def.apiFilterKey;
                        const currentValue = filterGetSet[fk]?.get || [];
                        const setter = filterGetSet[fk]?.set || (() => {});
                        // Dynamic choices for club/teams/pickup_store/crm
                        const choices = ['club', 'teams', 'pickup_store', 'crm', 'delivery'].includes(def.id)
                          ? (dynamicChoices[def.id] || [])
                          : (def.choices || []);
                        filterProps = {
                          ...baseFilterProps,
                          choices,
                          selected: currentValue,
                          onChange: setter,
                          onClear: () => setter([]),
                          searchable: def.searchable || false,
                          searchLabel: `Search ${def.label}…`,
                        };
                      } else if (def.filterType === 'date') {
                        // Each date-typed column drives its own state pair;
                        // route based on apiFilterKey so Created and Fulfill By
                        // don't stomp on each other.
                        if (def.apiFilterKey === 'fulfill_by') {
                          filterProps = {
                            ...baseFilterProps,
                            dateFrom: fulfillByFrom,
                            dateTo:   fulfillByTo,
                            onChange: ({ from, to }) => { setFulfillByFrom(from); setFulfillByTo(to); },
                            onClear:  () => { setFulfillByFrom(''); setFulfillByTo(''); },
                          };
                        } else {
                          filterProps = {
                            ...baseFilterProps,
                            dateFrom,
                            dateTo,
                            onChange: ({ from, to }) => { setDateFrom(from); setDateTo(to); },
                            onClear: () => { setDateFrom(''); setDateTo(''); },
                          };
                        }
                      }

                      const pinInfo = pinLayout[def.id];
                      const pinStyle = pinInfo ? {
                        position: 'sticky',
                        [pinInfo.side === 'left' ? 'left' : 'right']: `${pinInfo.stickyOffset}px`,
                        width: `${PIN_COL_WIDTH}px`,
                        minWidth: `${PIN_COL_WIDTH}px`,
                        maxWidth: `${PIN_COL_WIDTH}px`,
                        zIndex: pinInfo.zIndex,
                        background: '#f0f4fa',
                        // Prevent content overflow from shifting visually
                        // during horizontal scroll under adjacent cells.
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      } : undefined;
                      const pinClass = [
                        pinInfo?.isLastLeft ? 'ots-th--pin-left-last' : '',
                        pinInfo?.isFirstRight ? 'ots-th--pin-right-first' : '',
                      ].filter(Boolean).join(' ');

                      return (
                        <HeaderCell
                          key={def.id}
                          column={tsCol}
                          label={def.label}
                          tooltip={def.tooltip || null}
                          sortable={def.sortable}
                          filterType={def.filterType}
                          filterProps={filterProps}
                          isStickyOrder={def.sticky === 'order'}
                          colId={def.id}
                          dragHandlers={{
                            onDragStart: e => { setDraggingColId(def.id); handleColDragStart(e, def.id); },
                            onDragOver:  e => { setDragOverColId(def.id); handleColDragOver(e); },
                            onDrop:      e => { handleColDrop(e, def.id); setDragOverColId(null); setDraggingColId(null); },
                            onDragEnd:   ()  => { handleColDragEnd(); setDraggingColId(null); setDragOverColId(null); },
                          }}
                          isDragging={draggingColId === def.id}
                          isDragOver={dragOverColId === def.id}
                          openFunnelId={openFunnelId}
                          // Cross-close pin menu when opening filter
                          setOpenFunnelId={(id) => { setOpenFunnelId(id); if (id) setOpenPinMenuId(null); }}
                          pinnedCols={pinnedCols}
                          setPin={setPin}
                          openPinMenuId={openPinMenuId}
                          // Cross-close filter when opening pin menu
                          setOpenPinMenuId={(id) => { setOpenPinMenuId(id); if (id) setOpenFunnelId(null); }}
                          pinStyle={pinStyle}
                          pinClass={pinClass}
                        />
                      );
                    })}
                  </tr>
                </thead>

                {/* Phase 1 tbody — hidden in virtual mode via CSS */}
                {!isVirtual && (
                  <tbody className={fetching ? 'ots-tbody--dim' : ''}>
                    {/* Empty / error states preserve the thead so column
                        headers remain visible when a filter (esp. a URL-
                        pasted one) returns no rows. colSpan covers every
                        rendered column including the sticky checkbox. */}
                    {error ? (
                      <tr><td colSpan={visibleColDefs.length}
                              className="ots-empty"
                              style={{ color: '#7b0f0f', padding: '32px 12px', textAlign: 'left' }}>
                          <span style={{ position: 'sticky', left: 16, display: 'inline-block' }}>{error}</span>
                      </td></tr>
                    ) : (displayedOrders.length === 0 && !fetching && !searchLoading) ? (
                      <tr><td colSpan={visibleColDefs.length}
                              className="ots-empty"
                              style={{ padding: '32px 12px', textAlign: 'left' }}>
                          <span style={{ position: 'sticky', left: 16, display: 'inline-block' }}>
                            {isSearching ? `No orders match "${searchQ.trim()}".` : 'No orders found.'}
                          </span>
                      </td></tr>
                    ) : null}
                    {isSearching && !searchLoading && searchHidden > 0 && (
                      <tr><td colSpan={visibleColDefs.length}
                              style={{ background: '#fff8e1', borderBottom: '1px solid #f0e0a0',
                                       padding: '10px 12px', textAlign: 'left', fontSize: 13 }}>
                          <span style={{ position: 'sticky', left: 16, display: 'inline-flex', alignItems: 'center' }}>
                          {searchHidden} match{searchHidden === 1 ? '' : 'es'} hidden by active filters{' '}
                          <button type="button"
                                  onClick={() => {
                                    setStatuses([]); setPackStatuses([]);
                                    setPickStatuses([]); setPressStatuses([]);
                                    setClubs([]); setTeams([]); setPrinteds([]);
                                    setDeliveryMethods([]); setDeliveryNames([]);
                                    setPickupStores([]); setStoreStatuses([]);
                                    setIsCustoms([]); setCrms([]); setSources([]); setRetailStore(false);
                                    setProtectStatuses([]); setRiskLevels([]); setDeliveryStatuses([]);
                                    setDateFrom(''); setDateTo('');
                                    setFulfillByFrom(''); setFulfillByTo('');
                                    setSearchHidden(0); setSearchResults(null);
                                  }}
                                  style={{ marginLeft: 8, border: '1px solid #b8860b', background: '#fff',
                                           color: '#8a6d00', borderRadius: 6, padding: '3px 10px',
                                           fontWeight: 600, cursor: 'pointer' }}>
                            Clear filters
                          </button>
                          </span>
                      </td></tr>
                    )}
                    {displayedOrders.map((fo, i) => {
                      const o = fo.order || {};
                      const isSelected = selectedIds.has(o.id);
                      const _hasNotif = (fo.notifications_count || 0) > 0;
                      const _hasSku   = (fo.sku_notifications_count || 0) > 0;
                      const rowClass = [
                        'ots-tr',
                        o.cancelled_at ? 'ots-tr--cancelled' : '',
                        (!o.cancelled_at && o.closed_at) ? 'ots-tr--archived' : '',
                        isSelected ? 'ots-tr--selected' : '',
                        // Row highlight signals: red (order notification), yellow
                        // (SKU notification). The CSS handles multi-signal combos
                        // with diagonal-stripe backgrounds.
                        _hasNotif ? 'ots-tr--has-notifications' : '',
                        _hasSku   ? 'ots-tr--has-sku-notifications' : '',
                        // Pulsing green: 100% retail order still unfulfilled at a
                        // corporate retail store (server-stamped, 2026-10-07).
                        fo.retail_store_unfulfilled ? 'ots-tr--retail-store' : '',
                        isFbmOpenRow(fo) ? 'ots-tr--fbm' : '',
                      ].filter(Boolean).join(' ');

                      return (
                        <tr
                          key={fo.fulfillment_order_id || o.id || i}
                          className={rowClass}
                          onClick={(e) => {
                            if (e.metaKey || e.ctrlKey) {
                              window.open(`/store/${activeStore}/orders/${orderNumericId(o.id)}`, '_blank');
                              return;
                            }
                            openOrder(orderNumericId(o.id));
                          }}
                          onAuxClick={(e) => {
                            if (e.button === 1) { e.preventDefault(); window.open(`/store/${activeStore}/orders/${orderNumericId(o.id)}`, '_blank'); }
                          }}
                        >
                          {/* Sticky checkbox cell */}
                          <td className="ots-td ots-td--sticky-cb" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="ots-cb"
                              checked={isSelected}
                              onChange={e => handleRowCheckbox(e, o.id, i)}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>

                          {/* Data cells */}
                          {visibleColDefs.filter(d => d.id !== 'checkbox').map(def => {
                            const isStickyOrder = def.sticky === 'order';
                            const pinInfo = pinLayout[def.id];
                            const tdPinStyle = pinInfo ? {
                              position: 'sticky',
                              [pinInfo.side === 'left' ? 'left' : 'right']: `${pinInfo.stickyOffset}px`,
                              width: `${PIN_COL_WIDTH}px`,
                              minWidth: `${PIN_COL_WIDTH}px`,
                              maxWidth: `${PIN_COL_WIDTH}px`,
                              zIndex: pinInfo.zIndex,
                              background: isSelected ? '#dbe6f5' : '#f6f8fc',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            } : undefined;
                            const tdClass = [
                              'ots-td',
                              isStickyOrder ? 'ots-td--sticky-order' : '',
                              pinInfo?.isLastLeft ? 'ots-td--pin-left-last' : '',
                              pinInfo?.isFirstRight ? 'ots-td--pin-right-first' : '',
                            ].filter(Boolean).join(' ');
                            return (
                              <td
                                key={def.id}
                                className={tdClass}
                                style={tdPinStyle}
                                data-pinned={pinInfo?.side || undefined}
                                onClick={isStickyOrder || def.id === 'print_status' ? e => e.stopPropagation() : undefined}
                              >
                                {renderCellContent(def.id, fo)}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                )}
              </table>

              {/* Phase 2 virtual tbody — rendered outside the <table> as a div */}
              {isVirtual && (
                <VirtualTbody
                  orders={orders}
                  visibleColDefs={visibleColDefs}
                  selectedIds={selectedIds}
                  fetching={fetching}
                  hasMore={hasMore}
                  loadMore={loadMore}
                  renderCellContent={renderCellContent}
                  handleRowCheckbox={handleRowCheckbox}
                  openOrder={openOrder}
                  activeStore={activeStore}
                  orderNumericId={orderNumericId}
                  colWidths={colWidths}
                  PREFETCH_THRESHOLD={PREFETCH_THRESHOLD}
                />
              )}
            </>
          )}
        </div>

        {/* ── Pagination footer (Phase 1 only) / Virtual status (Phase 2) ── */}
        {(orders.length > 0 || (isSearching && _searchTotal > 0)) && (
          <div className="ots-pagination">
            <div className="ots-pagination-info">
              {isVirtual ? (
                <>
                  <strong>{orders.length.toLocaleString()} rows loaded</strong>
                  {totalCount != null && <span> of {totalCount.toLocaleString()} total</span>}
                  {hasMore && <span style={{ color: '#6d7175', marginLeft: 4 }}>· scroll to load more</span>}
                </>
              ) : isSearching ? (
                <>
                  <strong>Page {pageNum} of {_searchPagesTotal}</strong>
                  <span> · {((pageNum - 1) * pageSize + 1).toLocaleString()}–{Math.min(pageNum * pageSize, _searchTotal).toLocaleString()} of {_searchTotal.toLocaleString()} search results</span>
                </>
              ) : (
                <>
                  <strong>Page {pageNum}</strong>
                  {totalCount != null && (
                    <span> · {((pageNum - 1) * pageSize + 1).toLocaleString()}–{Math.min(pageNum * pageSize, totalCount).toLocaleString()} of {totalCount.toLocaleString()} orders</span>
                  )}
                </>
              )}
            </div>
            {!isVirtual && (
              <div className="ots-pagination-btns">
                <button className="ots-pagination-btn" onClick={goPrev} disabled={!hasPrev || fetching}>
                  ← Previous
                </button>
                <button className="ots-pagination-btn" onClick={goNext} disabled={!hasNext || fetching}>
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
